import { Button, Form, Input, Modal, Popover, Switch } from "antd";
import cls from "classnames";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import { cloneDeep, get, isArray, isEmpty, isString, last, map, set, some, uniqBy } from "lodash";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector, useStore as useReduxStore } from "react-redux";
import { Outlet, useParams, useSearchParams } from "react-router-dom";
import useState from "react-usestateref";

import { editorActions } from "@store/editor";
import { feedActions } from "@store/feed";
import { fotActions } from "@store/fot";
import { studioActions } from "@store/studio";
import { useModal } from "@uiview/views/AModal/useModal";
import { BrainClient } from "imagica-corekit/dist/base/api/BrainClient";
import { HomeUtil } from "imagica-corekit/dist/cases/home/HomeUtil";
import style from "./css/HomeUsePlugin.module.css";
import { getInputNodesForNodeLabel, isOnlyChatInterface, sleep } from "./util";

import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Api } from "@uikit/service/api";
import { settings } from "imagica-corekit/dist/base/kernel/Settings";
import { CotFlow } from "imagica-corekit/dist/cases/cot/CotFlow";
import ReactFlow, {
  Background,
  BackgroundVariant,
  MarkerType,
  SelectionMode,
  applyEdgeChanges,
  applyNodeChanges,
  useKeyPress,
  useOnSelectionChange,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import CustomNewEdge from "../../uiview/views/CustomNewEdge/CustomNewEdge";
import CustomNewLine from "../../uiview/views/CustomNewLine/CustomNewLine";
import func from "@uikit/func";
import CustomEdge from "./CustomEdge";
import CustomGroup from "./CustomGroup";
import CustomNode from "./CustomNode";
import {
  arrayKeyToObject,
  easeInOutExpo,
  getAutomaticDisplayType,
  getCenterPositionByElement,
  splitText,
} from "./util";

import useAlertBeforeunload from "@custom-hooks/useAlertBeforeunload";
import useCompositionKeypress from "@custom-hooks/useCompositionKeypress";
import useCreateFunction from "@custom-hooks/useCreateFunction";
import useFocusNode from "@custom-hooks/useFocusNode";
import useHandleChatBox from "@custom-hooks/useHandleChatBox";
import useNodeEdgeMaxIndex from "@custom-hooks/useNodeEdgeMaxIndex";
import useObjectRelationGqlRequest from "@custom-hooks/useObjectRelationGqlRequest";
import useOpenAiParam from "@custom-hooks/useOpenAiParam";
import usePostUrlCanAbort from "@custom-hooks/usePostUrlCanAbort";
import useSaveProject from "@custom-hooks/useSaveProject";
import useSharedLinkToGetProject from "@custom-hooks/useSharedLinkToGetProject";
import useTakeProjectSnapshot from "@custom-hooks/useTakeProjectSnapshot";
import { useUpdateNodeEdgeInfo } from "@uikit/hooks/useUpdateNodeEdgeInfo";
import { CustomNewConnectionLine } from "../../uiview/views/CustomNewConnectionLine/CustomNewConnectionLine";
import UI from "../UI";
import LoadingOutline from "../components/LoadingOutline";
import AISaas from "./AISaas";
import AISaasV2 from "./AISaasV2";
import CodeModal from "./CodeModal";
import ConnectionLine from "./ConnectionLine";
import { CotInputView } from "./CotInput/CotInputView";
import CustomAPIFunction from "./CustomAPIFunction";
import CustomJSFunction from "./CustomJSFunction";
import HomeNavBar from "./HomeNavBar";
import HomeToolBar from "./HomeToolBar";
import IdeaList from "./IdeaList";
import Publish from "./Publish";
import SettingPreview from "./SettingPreview";
import {
  CUSTOM_FUNCTION_VARS_JS_REG,
  CUSTOM_FUNCTION_VARS_REG,
  DIRECTIONHANDLE,
  EDITORABLE_INPUT_TYPE,
  GROUPID_REG,
  IMAGE_TYPES,
  NEW_EDGE_REG,
  NEW_LINE_REG,
  PROJECT_LOAD_SIZE,
  PROJECT_NAME_LENGTH,
  STOCK_TYPES,
  WEB_APP_DEFAULT_SUBTITLE,
  WEB_APP_DEFAULT_TITLE,
} from "./constants";
import {
  allPreDefinedFunctions,
  defaultApis,
  getFunctionNameByAlias,
  getMidjourneyV2,
} from "./nodeTypeComponents/nodeTypeDispose";
import utilIndex from "./utils/index";
import useRunEdgeAddNodeToPreview from "@custom-hooks/useRunEdgeAddNodeToPreview";
import { logEvent } from "@uikit/service/amplitude";
import { CustomEdgeUtil } from "@uikit/util/CustomEdgeUtil";
import { UserIdentity } from "@uikit/util/userIdentity";
import skipEmptyVerification from "@uiview/views/Nodes/Agent/skipEmptyVerification";
import useConnectAgentNode from "@uiview/views/Nodes/Agent/useConnectAgentNode";
import classNames from "classnames";
import { FotClient } from "imagica-corekit/dist/base/api/FotClient";
import { MessageCenter } from "imagica-corekit/dist/base/kernel/MessageCenter";
import { useNavigate } from "react-router-dom";
import { HomeChildProvider } from "../../custom-hooks/HomeUsePluginRoot/childContainer";
import { HomeUsePluginProvider, useHomeUsePluginContainer } from "../../custom-hooks/HomeUsePluginRoot/container";
import useCotFlow from "../../custom-hooks/useCotFlow";
import useCreateNewProject from "../../custom-hooks/useCreateNewProject";
import useCreateProject from "../../custom-hooks/useCreateProject";
import useFeatureTags from "../../custom-hooks/useFeatureTags";
import useFunctionsPagination from "../../custom-hooks/useFunctionsPagination";
import useGeneratorDomTestID from "../../custom-hooks/useGeneratorDomTestID";
import useGuideStep from "../../custom-hooks/useGuideStep";
import useHandleAskBrainData from "../../custom-hooks/useHandleAskBrainData";
import useHandleOtherTypeRun from "../../custom-hooks/useHandleOtherTypeRun";
import useHandleReactFlowDrag from "../../custom-hooks/useHandleReactFlowDrag";
import useHandleSave from "../../custom-hooks/useHandleSave";
import { useSaasTheme } from "../../custom-hooks/useSaasTheme";
import useSetNodeErrorTips from "../../custom-hooks/useSetNodeErrorTips";
import useShowStockInterfaceFunc from "../../custom-hooks/useShowStockInterfaceFunc";
import useUserAuth from "../../custom-hooks/useUserAuth";
import { getIt } from "../../uikit/getIt";
import useHomeUsePluginUtil from "../../uikit/hooks/useHomeUsePluginUtil";
import { initSDK } from "../../uikit/service/birdEatsBugSDKInit";
import { ThemeModal, ThemeSelect } from "../components/SaasTheme";
import { CotInputBloc } from "./CotInput/CotInputBloc";
import GuideVideoView from "./nodeTypeComponents/GuideVideoView";
import GuideNodePage from "./nodeTypeComponents/guideNodePage";
import { getQueryParams, getStartNodes } from "./util";

import { useAddToPreview } from "@custom-hooks/HomeUsePluginRoot/useAddToPreview";
import { useContainerMounted } from "@custom-hooks/HomeUsePluginRoot/useContainerMounted";
import { useHomeGeneralMethod } from "@custom-hooks/HomeUsePluginRoot/useHomeGeneralMethod";
import { useHomeReactFlow } from "@custom-hooks/HomeUsePluginRoot/useHomeReactFlow";
import { useNodesRenderComplated } from "@custom-hooks/HomeUsePluginRoot/useNodesRenderComplated";
import { PreviewAppProvider, usePreviewAppStore } from "@custom-hooks/HomeUsePluginRoot/usePreviewAppStore";
import { useIsStaticApp } from "@custom-hooks/useNodePreview";
import { useSignal } from "@preact/signals-react";
import { useCotFlowV3 } from "@uikit/hooks/useCotFlowV3";
import { useManualLine } from "@uikit/hooks/useManualLine";
import { UiFunctionService } from "@uikit/service/UiFunctionService";
import { CopyUtil } from "@uikit/util/CopyUtil";
import { HomeUsePluginUtil } from "@uikit/util/HomeUsePluginUtil";
import { isShareWebApp } from "@uikit/util/UrlUtil";
import { AISaasApp } from "@uiview/views/AISaas";
import { useAiSaasStore } from "@uiview/views/AISaas/useAISaasStore";
import AutoResizablePanel from "@uiview/views/AutoResizablePanel/AutoResizablePanel";
import InvalidShareAppTip from "@uiview/views/InvalidShareAppTip/InvalidShareAppTip";
import { TemplateModal } from "@uiview/views/NewOnboardingFlow/TemplateModal/TemplateModal";
import { getExpandedBluePrintNodes } from "@uiview/views/Nodes/BluePrintNode/BluePrintUtil";
import { disablePublishDisplayType } from "@uiview/views/Nodes/nodeConfig.ts";
import { PreviewAppUtil } from "@uiview/views/PreviewApp/PreviewAppUtil";
import PreviewPane from "@uiview/views/PreviewPane";
import { FotAuthStore } from "imagica-corekit/dist/base/store/FotAuthStore";
import { FunctionV2Store } from "imagica-corekit/dist/base/store/FunctionV2Store";
import { ProjectStore } from "imagica-corekit/dist/base/store/ProjectStore";
import { ToolPieceStore } from "imagica-corekit/dist/base/store/ToolPieceStore";
import { ProjectUtil } from "imagica-corekit/dist/cases/util/ProjectUtil";
import useNewEdgeRun from "../../uikit/hooks/useNewEdgeRun";
import { GuideContainer } from "../../uiview/views/Guide";
import { UnSupportedGraph } from "../../uiview/views/UnSupportedGraph";
import Home from "./Home";
import { WebOnlyPromptView } from "./WebOnlyPromptView/WebOnlyPromptView";

import { ChatAIPage } from "@uiview/views/ChatAI/ChatAISharedPage";
import { getDefaultPreviewAppTypeList } from "@uiview/views/PreviewApp/const";
import { JsonUtil } from "imagica-corekit/dist/base/cutil/JsonUtil";
import { BlueprintsStore } from "imagica-corekit/dist/base/store/BlueprintsStore";
import { HomePluginStore } from "imagica-corekit/dist/base/store/HomePluginStore";
import { useStore as useImagincStore } from "imagica-uikit/dist/hooks/useStore";

import { HomeUsePluginContext, HomeContextParams } from "@uiview/pages/homeUsePlugin/HomeUsePluginContext";
import { HomeStore } from "imagica-corekit/dist/cases/store/HomeStore";
import { SubscriptionStore } from "imagica-corekit/dist/cases/store/SubscriptionStore";
import { PreferencesStore } from "imagica-corekit/dist/base/store/PreferencesStore";
import { useFitCanvas } from "@uikit/hooks/useFitCanvas";
import useSaveSubscriber from "@custom-hooks/useSaveSubscriber";
import { tryPromise } from "imagica-corekit/dist/base/cutil/LangUtil";
import { PreviewService } from "imagica-corekit/dist/cases/preview/PreviewService";
import { WebConfigStore } from "imagica-corekit/dist/cases/webconfig/WebConfigStore";
import { FlowManager } from "@uikit/flow/FlowManager";
import { FlowPositionUtil } from "@uikit/flow/FlowPositionUtil";
import isBlank from "@sedan-utils/is-blank";
import { signal } from "@preact/signals-react";
import { useCreation } from "ahooks";
import { TermsPopup } from "./TermsPopup/TermsPopup";
import { TermsPopupBloc } from "./TermsPopup/TermsPopupBloc";

const previewService = getIt(PreviewService);
const messageCenter = getIt(MessageCenter);
const authStore = getIt(FotAuthStore);
const projectStore = getIt(ProjectStore);
const homePluginStore = getIt(HomePluginStore);
const homeStore = getIt(HomeStore);
const preferenceStore = getIt(PreferencesStore);
// 间距
const distance = 10;
const selectApiReg = /^\//;
const needAInputField = "Need an input field";
const needAOutputField = "Click Add Content below and select content from your canvas";
const forbidAddToPreviewType = ["uploadFile", "Agent"];
const nodeTypes = {
  customNode: CustomNode,
  customGroup: CustomGroup,
  customNewEdge: CustomNewEdge,
};
const edgeTypes = {
  customEdge: CustomEdge,
  customNewLine: CustomNewLine,
};
const selectedMarkerEnd = {
  type: MarkerType.ArrowClosed,
  width: 12,
  height: 12,
  color: "#e84e4e",
};
const markerEnd = {
  type: MarkerType.ArrowClosed,
  width: 12,
  height: 12,
  color: "#6299f9",
};

const bottomVariants = {
  init: {
    opacity: 0,
    scale: 0,
  },
  show: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      type: "spring",
      ease: "easeInOut",
    },
  },
  hide: {
    opacity: 0,
    scale: 0,
    transition: {
      duration: 0.5,
      type: "spring",
      ease: "easeOut",
    },
  },
};
// const { Paragraph } = Typography;

// const _ResizeObserver = window.ResizeObserver;
// window.ResizeObserver = class ResizeObserver extends _ResizeObserver{
//   constructor(callback) {
//     callback = debounce(callback, 16);
//     super(callback);
//   }
// }

/**
 *
 * @param {object} props
 * @param {object|undefined} props.page
 * @returns {import('react').ReactNode}
 */
function HomeUsePlugin(props) {
  const homeStoreState = useImagincStore(homeStore).value;
  const homePluginState = useImagincStore(homePluginStore).value;
  const preferenceValue = useImagincStore(preferenceStore).value;
  const {
    preferenceJson,
    nodesState,
    edgesState,
    isNewEdgeRunRef,
    nodeIndexRef,
    edgeIndexRef,
    setDisplayThought,
    reactFlowState,
    figmaMode,
    setFigmaMode,
    setAllowZoomOrPan,
    saasAndThinkState,
    guideTriggerMessageState,
    showNavPageState,
    enableCOTFeedbackPopupState: { setEnableCOTFeedbackPopup },
    newOnboardingFlowState: { showNewOnboardingFlowTips },
    homeReactFlowState,
    aiSaasState,
    runAllLoadingState,
    setGroupDeleteChildNode,
  } = useHomeUsePluginContainer();

  const {
    checkedUIList,
    setCheckedUIList,
    UIConfigRef,
    saasUIData,
    setSaasUIData,
    saasUIDataRef,
    saasV2UIData,
    setSaasV2UIData,
    saasV2UIDataRef,
    thinkingAppUIData,
    setThinkingAppUIData,
    thinkingAppUIDataRef,
    addContentIdRef,
    isClickAddToContentRef,
  } = saasAndThinkState;

  const {
    enableRemovePreview,
    getPublishMetadata,
    showPreviewPanel,
    showPreviewPanelRef,
    setShowPreviewPanel,
    triggerRemovePreviewAppNode,
    openPreviewPage,
    updateCurrentProjectSavedPreviewList,
    saasUiDataToPreview,
  } = usePreviewAppStore();

  const { selectedUI, setSelectedUI, selectedUIRef } = homeReactFlowState;
  const { getNewNodePosition } = useHomeGeneralMethod();
  const { fitCanvas } = useFitCanvas();

  useContainerMounted();

  const { triggerGuideCot, triggerGuideManual } = guideTriggerMessageState;
  const { getSubscriberParamsData } = useSaveSubscriber();
  const webConfigState = useImagincStore(getIt(WebConfigStore)).value;
  const store = useReduxStore();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cotInputBloc = new CotInputBloc(webConfigState.cot.examples);
  const urlParam = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setChatBoxParam } = useHandleChatBox();
  const { createProject } = useCreateProject();
  const feature_tags = useFeatureTags();
  const api = new Api(false);
  const brainClient = getIt(FotClient);
  const showModal = useModal();
  const { isStaticApp } = useIsStaticApp();

  const termsPopuBloc = useCreation(() => new TermsPopupBloc(store), []);

  const deleteKeysPress = useKeyPress(["Backspace", "Del", "Delete"]);
  const { previewAppList } = usePreviewAppStore();

  const homeRef = useRef(null);
  const rightMenuRef = useRef(null);
  const duplicateEdgeRef = useRef(null);
  const nodeContextMenuRef = useRef(null);
  const multipleEdgeHostRef = useRef(null);
  const manuallyConnectedEdges = useRef(null);
  const canvasMovingLoadingRef = useRef(null);

  const dragOverTimer = useRef(null);

  // const nodeIndexRef = useRef(1)
  // const edgeIndexRef = useRef(0)
  const previewIndexRef = useRef(0);
  const sliceLevelRef = useRef(1);
  // const thinkingAppPreviewIndexRef = useRef(0)
  // const saasPreviewIndexRef = useRef(0)
  const historyIndex = useRef(-1);
  // const saasV2PreviewIndexRef = useRef(0)
  const extractDescriptionRef = useRef("");
  const selectedNodeIdRef = useRef("");
  const copyingNodeIdRef = useRef("");
  const cotDescription = useRef("");

  const isMouseInNode = useRef(false);
  const isFireNodeDragStopEvent = useRef(false);
  const isSelectionStart = useRef(false);
  const isLoadPreferences = useRef(false);
  // const nodesRef = useRef([])
  // const edgesRef = useRef([])
  const copyNodes = useRef([]);
  const copyEdges = useRef([]);
  const undoRef = homePluginStore.undoData;
  const currentDragNodes = useRef([]);
  const ideaDescriptionRef = useRef(new Map());
  const singleFlowEdgeArrRef = useRef([]);
  const prevRunAllNodesContent = useRef([]);
  const singleFlowEdgesRef = useRef({
    name: "",
    description: "",
    edgeArr: [],
    nodeArr: [],
  });
  const dragOverClientXY = useRef({
    x: 0,
    y: 0,
  });
  const coreBrainClient = getIt(BrainClient);
  const cotFlow = getIt(CotFlow);

  // const [currentSourceNodeValue, setCurrentSourceNodeValue] = useState('')
  // const [nodes, setNodes, nodesRef] = useState([]);
  // const [edges, setEdges, edgesRef] = useState([]);

  const { nodes, setNodes, nodesRef } = nodesState;
  const { edges, setEdges, edgesRef } = edgesState;

  const [customFunction, setCustomFunction, customFunctionRef] = useState([]);
  const [ideaList, setIdeaList] = useState([]);
  const [saasInputChildNode, setSaasInputChildNode, saasInputChildNodeRef] = useState([]);
  const [saasInputChildEdge, setSaasInputChildEdge, saasInputChildEdgeRef] = useState([]);
  const [saasV2InputChildEdge, setSaasV2InputChildEdge, saasV2InputChildEdgeRef] = useState([]);
  const [saasV2InputChildNode, setSaasV2InputChildNode, saasV2InputChildNodeRef] = useState([]);
  const [myModificationData, setMyModificationData] = useState({});
  const [customFuncRightContextMenu, setCustomFuncRightContextMenu] = useState({});

  const [showUnsupported, setShowUnsupported] = useState(false);
  const [ideaLoading, setIdeaLoading] = useState(false);
  const [showIdeaList, setShowIdeaList] = useState(false);
  const [runAllLoading, setRunAllLoading] = runAllLoadingState;
  const [thinkLoading, setThinkLoading] = useState(false);
  const [edgeRight, setEdgeRight] = useState(false);
  const [nodeRight, setNodeRight] = useState(false);
  const [shrinkIdeaList, setShrinkIdeaList] = useState(true);
  const [createNewFuncModal, setCreateNewFuncModal] = useState(false);
  const [saveFunctionLoading, setSaveFunctionLoading] = useState(false);
  const [showAddInputField, setShowAddInputField] = useState(false);
  const [isGroupNode, setIsGroupNode] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareSelectedLoading, setShareSelectedLoading] = useState(false);
  const [showNavPage, setShowNavPage, showNavPageRef] = showNavPageState;
  const [showMyModification, setShowMyModification] = useState(false);
  const [isPublish, setIsPublish] = useState(false);
  const [showSettingPreview, setShowSettingPreview] = useState(false);
  const [cotAutomaticNode, setCotAutomaticNode, cotAutomaticNodeRef] = useState(true);
  // show tips when share app has no valid datas
  const showInvalidShareAppTip = useSignal(false);

  const newPreviewPaneRef = useRef(null);

  //  add output node, 弹出创建Preview, 等待添加
  const awaitAddNode = useSignal(undefined);
  // const [reviseCreateFunction, setReviseCreateFunction] = useState(false)

  useAlertBeforeunload(showNavPage, homePluginState.isShare);

  const { setInputId, inputId, showSaasOutputListState } = aiSaasState;
  // const [figmaMode, setFigmaMode] = useState(true)
  const [saasLoading, setSaasLoading] = aiSaasState.saasLoadingState;
  const [saasV2Loading, setSaasV2Loading] = aiSaasState.saasV2LoadingState;

  const COTInputOutputNodes = useRef(undefined);

  const [showSaasOutputList, setShowSaasOutputList] = showSaasOutputListState;
  const [showCopyMouseStyl, setShowCopyMouseStyl] = useState(false);
  const [settingPreviewLoading, setSettingPreviewLLoading] = useState(false);
  const [stripePaymentActivated, setStripePaymentActivated] = useState(false);
  const [isSetFeatureTag, setIsSetFeatureTag] = useState(false);

  const [stepDetachment, setStepDetachment] = useState({});
  const [thinkValue, setThinkValue] = useState("");
  const [funcName, setFuncName, funcNameRef] = useState("");
  const [funcDescription, setFuncDescription] = useState("");
  // const [selectedUI, setSelectedUI, selectedUIRef] = useState('')
  const [showCot, setShowCot, showCotRef] = useState(false);
  const [createFunctionTitle, setCreateFunctionTitle] = useState("Create a new function");

  const [subscriptionPrice, setSubscriptionPrice] = useState({});
  const [otherNodeStaging, setOtherNodeStaging] = useState([]);
  const [saasEdgesStaging, setSaasEdgesStaging] = useState([]);

  const [alreadyRunArr, setAlreadyRunArr, alreadyRunRef] = useState([]);
  const [hasClosedMobilePromptView, setHasClosedMobilePromptView] = useState(false);
  // const reviseCustomCode = useSelector((state) => state.editor.reviseCustomCode);
  const developerMode = useSelector(state => state.fot.developerMode);

  const setLineParam = val => {
    dispatch(fotActions.setLineParam(val));
  };

  const editorImageScrollDireaction = useSelector(state => state.fot.editorImageScrollDireaction);
  const setImageScrollDireaction = val => {
    dispatch(fotActions.setImageScrollDireaction(val));
  };

  const checkNodeArr = useSelector(state => state.fot.checkNodeArr);
  const setCheckNodeArr = val => {
    dispatch(fotActions.setCheckNodeArr(val));
  };

  const userMe = useSelector(state => state.editor.me);

  const setCommandEnterNodeLineParam = val => {
    dispatch(editorActions.setCommandEnterNodeLineParam(val));
  };

  const meLoading = useSelector(state => state.editor.meLoading);
  // const setMeLoading = (val) => { dispatch(editorActions.setMeLoading(val)) };
  const setShowImagicaTutoriralLoading = val => {
    dispatch(editorActions.setShowImagicaTutoriralLoading(val));
  };

  const checkEdgeArr = useSelector(state => state.fot.checkEdgeArr);
  const setCheckEdgeArr = val => {
    dispatch(fotActions.setCheckEdgeArr(val));
  };
  const setHasSelectedArea = val => {
    dispatch(fotActions.setHasSelectedArea(val));
  };
  const setEnableTooltipWhatTodo = val => {
    dispatch(editorActions.setEnableTooltipWhatTodo(val));
  };

  const showGuideVideo = useSelector(state => state.editor.showGuideVideo);
  const currentGuideTitlePage = useSelector(state => state.editor.currentGuideTitlePage);
  const setCurrentGuideTitlePage = val => {
    dispatch(editorActions.setCurrentGuideTitlePage(val));
  };
  const setShowTooltip = val => {
    dispatch(editorActions.setShowTooltip(val));
  };
  const setCloseToolTipArr = val => {
    dispatch(editorActions.setCloseToolTipArr(val));
  };

  const imagicaStudioTutorial = useSelector(state => state.fot.imagicaStudioTutorial);
  const setImagicaStudioTutorial = val => {
    dispatch(fotActions.setImagicaStudioTutorial(val));
  };

  const isStaticMode = useSelector(state => state.editor.isStaticMode);
  const setIsStaticMode = val => {
    dispatch(editorActions.setIsStaticMode(val));
  };

  const disableAddPreview = useSelector(state => state.fot.disableAddPreview);
  const [previewHeight, setPreviewHeight, previewHeightRef] = useState(0);
  const setDisableAddPreview = val => {
    dispatch(fotActions.setDisableAddPreview(val));
  };

  // const inputId = useSelector((state) => state.fot.inputId);
  // const setInputId = (val) => { dispatch(fotActions.setInputId(val)) }
  const startNodeIds = useSelector(state => state.fot.startNodeIds);
  const setStartNodeIds = val => {
    dispatch(fotActions.setStartNodeIds(val));
  };

  // const isShare = useSelector((state) => state.fot.isShare);
  const setShare = val => {
    dispatch(fotActions.setShare(val));
  };

  const imageGenSelectOptions = useSelector(state => state.fot.imageGenSelectOptions);
  const setImageGenSelectOptions = val => {
    dispatch(fotActions.setImageGenSelectOptions(val));
  };

  // 首页留一个,为了监听变化更新 sassuidata
  const thoughts = useSelector(state => state.feed.thoughts);

  const selectedTemplate = useSelector(state => state.studio.selectedTemplate);
  const setSelectedTemplate = val => {
    dispatch(studioActions.setSelectedTemplate(val));
  };
  const setIsLogin = val => {
    dispatch(fotActions.setIsLogin(val));
  };
  const enableExperimentalFeature = useSelector(state => state.fot.enableExperimentalFeature);

  const isModelOpen = useSelector(state => state.fot.isModelOpen);
  // const isConnected = useSelector((state) => state.editor.socket.isConnected);
  // useWSConnectWithFibSeq();
  const applicationTheme = useSelector(state => state.editor.applicationTheme);
  const fontSize = useSelector(state => state.editor.fontSize);
  const cotStore = useSelector(state => state.editor.cot);
  const subscriberParamsData = useSelector(state => state.editor.subscriberParamsData);
  const setCotStore = val => {
    dispatch(editorActions.setCot(val));
  };
  const setMe = val => {
    dispatch(editorActions.setMe(val));
  };
  const setMode = val => {
    dispatch(fotActions.setMode(val));
  };
  const setView = val => {
    dispatch(fotActions.setView(val));
  };
  const setStudioProjectName = val => {
    dispatch(fotActions.setStudioProjectName(val));
  };
  const setVariableList = val => {
    dispatch(fotActions.setVariableList(val));
  };
  const setStudioThought = val => {
    dispatch(studioActions.setThought(val));
  };
  const setIsModelOpen = val => {
    dispatch(fotActions.setIsModelOpen(val));
  };
  const setSelectIdArr = val => {
    dispatch(fotActions.setSelectIdArr(val));
  };
  const setShowShareSignin = val => {
    dispatch(fotActions.setShowShareSignin(val));
  };
  const setCreateJSFuncData = val => {
    dispatch(editorActions.setCreateJSFuncData(val));
  };
  const setCreateAPIFuncData = val => {
    dispatch(editorActions.setCreateAPIFuncData(val));
  };
  const setCommandEnterNodeId = val => {
    dispatch(fotActions.setCommandEnterNodeId(val));
  };
  const setTutorialTempProjectData = val => {
    dispatch(editorActions.setTutorialTempProjectData(val));
  };
  const setWSData = res => {
    dispatch(editorActions.setResearchAgentWebsocketData({ key: res.key.topic, data: res.key }));
  };
  const setSameTargetEdgeIds = val => {
    dispatch(editorActions.setSameTargetEdgeIds(val));
  };
  const setMultiSameTargetEdgeIds = val => {
    dispatch(editorActions.setMultiSameTargetEdgeIds(val));
  };
  const setReloadMap = val => {
    dispatch(editorActions.setReloadMap(val));
  };
  const setNeedDisableEdgeIds = val => {
    dispatch(editorActions.setNeedDisableEdgeIds(val));
  };
  const setEnableCOTStartAgain = val => {
    dispatch(editorActions.setEnableCOTStartAgain(val));
  };
  const setPreSelectNodeId = val => {
    dispatch(fotActions.setPreSelectNodeId(val));
  };
  const setHoverEdgeData = val => {
    dispatch(editorActions.setHoverEdgeData(val));
  };
  const setSecondInputNodeId = val => {
    dispatch(editorActions.setSecondNodeId(val));
  };
  const setIsDraggingCreatingNewLine = val => {
    dispatch(editorActions.setIsDraggingCreatingNewLine(val));
  };
  // 判断用户为订阅者
  // const isSubscriber = subscriberParamsData?.attributes?.mode === "subscriber" || searchParams.get('mode') === 'subscriber'

  const { getUserMe, userMeState, isSubscriber } = useUserAuth();
  const { updateEdgeText, updateBlueprintEdge } = useUpdateNodeEdgeInfo();
  const { handleUploadFileRun } = useHandleOtherTypeRun({
    getHomeNodes,
    getSourceOfTruthResults,
    setEdgeLineParam,
    setQueryLoading,
    setNodes,
  });
  const { setConnectAgentNode } = useConnectAgentNode();
  const { onDragOver, onDrop, onDragLeave } = useHandleReactFlowDrag({
    feature_tags,
    dragOverClientXY,
    dragOverTimer,
    needAddNodeByDrag,
    clickAddNodeBtn,
    getHomeNodes,
    setNodes,
  });

  const { focusLastNode, focusPosition } = useFocusNode();
  const { deleteNodeByIds } = useHomeReactFlow();

  /** 用于函数获取-加载状态 */
  const [userFunctionsLoading, setUserFunctionsLoading] = useState(false);

  const { guidePublishData, stepRef, aiSaasV2Ref, handleStep, clickStep, clickBackStep, isLastStepGuidePage } =
    useGuideStep({
      markerEnd,
      nodeIndexRef,
      edgeIndexRef,
      getHomeNodes,
      getHomeEdges,
      setNodes,
      setEdges,
      setShowPreviewPanel,
      setSaasV2UIData,
      setSimplifiedEdgeNode,
      clickToPreviewNode,
      setStepDetachment,
      showDisableStudiosOnboarding: feature_tags.showDisableStudiosOnboarding,
    });

  const { addNewProject } = useCreateNewProject({
    showDisableStudiosOnboarding: feature_tags.showDisableStudiosOnboarding,
    disableCotOnboarding: feature_tags.disableCotOnboarding,
    getSimpleNode,
    clickZoomFitIcon,
    clickStep,
    setShowNavPage,
    initNewProjectV2,
    emptyNodes,
    setShowCot,
    setShowPreviewPanel,
  });

  const { setNodeErrorTip } = useSetNodeErrorTips({
    setNodes,
    getHomeNodes,
  });

  const getAllNodes = useCallback(() => {
    return nodesRef.current || [];
  }, [nodesRef]);

  // nodesRef.current = nodes
  // edgesRef.current = edges
  const groupDataRef = useRef({
    showStopGenerateBtn: false,
    abortNode: {},
    clickGroupAddBtn,
    clickStep,
    handleStep,
    handleDeleteNodes,
    handleDeleteGroupNodeByPreviewBtn,
    addGroupNodeToPreview,
    getNodes: getAllNodes,
    clickGroupToPreview,
    setNodes,
    copyNode,
    stepRef,
  });
  const nodeDataRef = useRef({
    getNodes: getAllNodes,
    nodeIndexRef,
    edgeIndexRef,
    isResized: false,
    loadingNodeHtml: false,
    isAutoDel: false,
    share: homePluginState.isShare,
    showWarning: false,
    showStopGenerateBtn: false,
    showFeedback: false,
    fromList: false,
    textAreaValue: "",
    inputTitle: undefined,
    generatedLabel: undefined,
    displayType: "text",
    originId: "",
    parentNode: "",
    parentNodeId: "",
    errorText: "",
    childNode: [],
    chatBoxParam: [],
    dropUploadFileData: {},
    abortNode: {},
    getProjectData,
    getStartEndNodesContentArr,
    setNodes,
    setEdges,
    setQueryLoading,
    clickAddBtn,
    clickSliceBtn,
    expandBluePrint,
    updatePreviewNodeContent,
    onNodeContentChange,
    onNodesResize,
    clickStep,
    clickBackStep,
    clickZoomFitIcon,
    handleStep,
    loadFromExistingTemplateV2,
    handleDeleteNodes,
    handleDeleteSingleNodeByPreviewBtn,
    addIndividual,
    clickAddInputField,
    addOrRemoveInputFeild,
    // setStepDetachment
    copyNode,
  });
  const edgeDataRef = useRef({
    nodes: nodesRef,
    nodeIndexRef,
    isGetQuery: false,
    getQueryLoading: false,
    isCreatedBySlice: false,
    isSliceAutoRun: false,
    isApiFunction: false,
    isUndoRedoAdd: false,
    isNewEdgeRunRef,
    originSource: "",
    originTarget: "",
    sourceHandle: "b",
    targetHandle: "a",
    selectedApi: {},
    lineParam: {
      loading: false,
      queryValue: "",
      enterText: "",
      lineType: "prompt",
      createFromCot: false,
      // 用来禁用边上的 run 按钮
      disabledRun: false,
    },
    // imageGenSelectOptions,
    // singleFlowEdgeArrRef,
    setNodes,
    setEdges,
    setEdgeLineParam,
    setNodeParam,
    setQueryLoading,
    setNodeErrorTip,
    getAndSetQuery,
    getOtherTypeData,
    getStartEndNodesContentArr,
    getSourceOfTruthResults,
    getHomeNodes,
    getHomeEdges,
    getHomeNextNodeIndex,
    clickAddBtn,
    clickStep,
    groupChanged,
    onEdgeContentChange,
    onNodeContentChange,
    handleSlice,
    handleStep,
    handleUploadFileRun,
    shardSplittingAll,
    autoSetInputField,
    postAIDescription,
    // handleUserSavedFunctions,
    // setReviseCreateFunction,
    // setCurrentSourceNodeValue
  });

  const { themes, saasTheme, showThemeModal, setSaasTheme, setShowThemeModal } = useSaasTheme();
  const { postData, postWithStreaming } = usePostUrlCanAbort();
  const { getNodeEdgeMaxIndex } = useNodeEdgeMaxIndex();
  const { getNodes, getEdges, setCenter, getZoom, getNode, getEdge, getViewport, fitView, fitBounds } = useReactFlow();
  const flowManager = FlowManager.use();
  const { zoomTo } = useHomeReactFlow();
  const { getOpenAiParam, getCompletionsGenerationParam } = useOpenAiParam();
  const { getSharedProjectData } = useSharedLinkToGetProject();
  const { saveProject } = useSaveProject();
  const { gql } = useObjectRelationGqlRequest();
  const { fetchMyFunctions } = useFunctionsPagination();
  const { createFunction } = useCreateFunction();
  const altOptionPressed = useKeyPress(["Alt"]);
  const { handleStockInterfaceFunc } = useShowStockInterfaceFunc({
    postAIDescription,
    setNodeParam,
    setNodeErrorTip,
  });
  const { getAskBrainData } = useHandleAskBrainData({
    postWithStreaming,
    setNodeParam,
    setNodes,
  });

  const { firstRunAddNodeToPreview, firstRunAllAddNodeToPreview } = useRunEdgeAddNodeToPreview({
    handleInputField,
    setInputId,
    clickGroupToPreview,
    addIndividual,
  });

  const {
    newEdgeDataRef,
    newLineDataRef,
    onNodesDeleteV3,
    createNewEdge,
    createNewLine,
    handleUpdateTarget,
    clickAddNewNode,
    getItemRelavantFunctionsV3,
    getSimpleNodeV3,
    getSimpleEdgeV3,
    getAndSetQueryV3,
    setEdgeValueByWriterV3,
    setQueryLoadingV3,
    getSimpleNodeDataV3,
    getSimpleEdgeDataV3,
    getNewEdgeAllLineIdsByNode,
    getNewEdgeDataByLine,
    handleProjectDataV3,
    handleCustomToGroup,
    deleteInputNode,
    removeInputFeild,
    handleGroupToCustom,
    autoAddInputNodeByBluePrint,
  } = useHomeUsePluginUtil({
    distance,
    previewHeightRef,
    nodeIndexRef,
    edgeIndexRef,
    nodeDataRef,
    groupDataRef,
    singleFlowEdgeArrRef,
    customFunctionRef,
    getHomeNodes,
    getHomeEdges,
    getSaveDataFunc,
    getEdgeGeneration,
    setNodes,
    setEdges,
    setEdgeLineParam,
    handleUploadFileRun,
    setStartingPointInput,
    getSaasAction,
    firstRunAddNodeToPreview,
  });
  const { takeProjectSnapshot } = useTakeProjectSnapshot({
    getHomeNodes,
    getHomeEdges,
    getHomeNodesIndex,
    getHomeEdgesIndex,
    getSimpleNode,
    getSimpleEdge,
    getFilterDataAttrsNode,
    getUIListParam,
    getItemRelavantFunctions,
  });
  const { saveAsNewTemplateV2, handleSave, getSaveData, getSaveDataV3 } = useHandleSave({
    newEdgeDataRef,
    showMyModification,
    feature_tags,
    getHomeNodes,
    getHomeEdges,
    getHomeNodesIndex,
    getHomeEdgesIndex,
    getSimpleNode,
    getSimpleEdge,
    getSimpleNodeV3,
    getSimpleEdgeV3,
    getFilterDataAttrsNode,
    getUIListParam,
    getItemRelavantFunctions,
    saveAsExistingTemplateV2,
    saveProject,
    resetUndoData,
    genLink,
    loadFromExistingTemplateV2,
    getItemRelavantFunctionsV3,
  });

  const { corekitRun } = useNewEdgeRun({
    getSaveData: getSaveDataV3,
    getHomeEdges,
    setNodes,
    setEdges,
    handleCustomToGroup,
    errorClosure: () => {
      setRunAllLoading(false);
    },
    handleGroupToCustom,
    handleSave,
  });

  const { handleManualLine } = useManualLine({
    onNodesEdgesAdd,
    createNewEdge,
    createNewLine,
    handleUpdateTarget,
    setNodes,
    corekitRun,
    getHomeNodes,
    getHomeEdges,
    getSimpleNodeV3,
    getSimpleEdgeV3,
    getSimpleNodeDataV3,
    getAndSetQueryV3,
    setStartingPointInput,
  });

  const { showFlowByGroupV3 } = useCotFlowV3({
    getHomeNodes,
    getHomeEdges,
    getSaveDataFunc,
    getAndSetQueryV3,
    setEdgeValueByWriterV3,
    setNodes,
    setEdgeLineParam,
    setQueryLoadingV3,
    handleUploadFileRun,
    nodeIndexRef,
    setCenter,
    corekitRun,
    getSimpleNodeData: getSimpleNodeDataV3,
  });

  const { showAllInputs } = useCotFlow({
    setEnableTooltipWhatTodo,
    setShowTooltip,
    setCenter,
    setNodes,
  });

  const publishStr = () => {
    let str = "Never published";
    if (selectedTemplate?.published_metadata?.published_at) {
      const time = dayjs(selectedTemplate.published_metadata.published_at).fromNow();
      str = `Published ${time}`;
    }
    return str;
  };
  // kill auto save for now unti duplicate project fixed
  // useAutoSavePer5Mins(showNavPage, undoRef, handleSave)
  const { handleUndo, handleRedo } = useCompositionKeypress({
    historyIndex,
    undoRef,
    isNewEdgeRunRef,
    share: homePluginState.isShare,
    setSimplifiedEdgeNode,
  });

  useGeneratorDomTestID();

  useOnSelectionChange({
    onChange: ({ nodes, edges }) => {
      // 有可能选中了组的一些node 没全选
      const nodeIds = nodes.reduce((prev, x) => {
        const arr = [x.id];
        if (x.type === "customGroup") {
          arr.push(...x.data.childNode);
        }
        return prev.concat(arr);
      }, []);
      const allNodes = Array.from(new Set(nodeIds));
      const edgeIds = edges.map(x => x.id) || [];
      // 鼠标拖拽选区选中
      if (isSelectionStart.current) {
        // const edgesInSection = edges.filter(x => allNodes.includes(x.source) && allNodes.includes(x.target))
        setCheckNodeArr(allNodes);
        setCheckEdgeArr(edgeIds);
      }
      if (!func.isEmpty(nodes)) {
        selectedNodeIdRef.current = nodes[0].id;
      }
    },
  });

  const {
    loadPreviewApp,
    addPreview,
    clearCurrentPreviewApp,
    getPreviewAppActiveLable,
    previewAppAcivieId,
    handleSendToAppV2,
    comeInSetting,
  } = usePreviewAppStore();
  // 打开或更新项目时记录当前 attrIndexer, 退出时清空
  useEffect(() => {
    const attrIndexer = showNavPage ? "" : store.getState().studio.selectedTemplate.index ?? "";
    projectStore.setAttrIndexer(attrIndexer);
  }, [showNavPage, selectedTemplate]);

  useEffect(() => {
    setPreviewHeight(disableAddPreview ? 0 : 50);
  }, [disableAddPreview, setPreviewHeight]);

  // 监听cot-input结束时候执行runall逻辑
  useEffect(() => {
    // v2的情况下才走原来的逻辑生成剩下的flow
    if (cotStore.creating === false && cotStore.inputIds.length > 0 && cotAutomaticNodeRef.current) {
      loadingForRendering();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cotStore.creating]);

  // 监听实验属性 如果为false禁用thinkingApp预览
  useEffect(() => {
    if (!enableExperimentalFeature && checkedUIList.find(item => item.value === "thinkingApp")) {
      if (checkedUIList.length === 1) {
        setShowPreviewPanel(false);
        setCheckedUIList([]);
        setSelectedUI("");
      } else {
        const newCheckUIList = checkedUIList.filter(x => x.value !== "thinkingApp");
        setCheckedUIList(newCheckUIList);
        setSelectedUI(newCheckUIList[0].value);
      }
    }
  }, [checkedUIList, enableExperimentalFeature, selectedUI, showPreviewPanel]);

  // handle developer mode
  useEffect(() => {
    const optionsArr = store.getState().fot.imageGenSelectOptions;
    const idx = optionsArr.findIndex(x => x.value === "midjourneyV2" && x.label === "Midjourney");
    if (developerMode) {
      // 添加midjourney
      if (idx === -1) {
        let tempList = [...optionsArr];
        const imageGenIdx = optionsArr.findIndex(x => x.value === "imageGen");
        tempList.splice(imageGenIdx, 0, getMidjourneyV2);
        setImageGenSelectOptions(tempList);
      }
    } else {
      setIsModelOpen(false);
      // 删除midjourney
      if (idx !== -1) {
        let tempList = [...optionsArr];
        tempList.splice(idx, 1);
        setImageGenSelectOptions(tempList);
      }
    }
  }, [developerMode]);

  useEffect(() => {
    PreviewAppUtil.handleChatOutputEdgeId({
      saasUIData,
      previewAppList,
      nodes: nodesRef.current,
    });
  }, [saasUIData.output, previewAppList]);

  // websocket
  // useMessageCenter(`/studios/${store.getState().editor.me.id}/ask_brain`, setWSData);

  function getSaveDataFunc(...props) {
    return getSaveData(...props);
  }
  const onSelectionStart = () => {
    setPreSelectNodeId("");
    setHasSelectedArea(true);
    isSelectionStart.current = true;
    window.getSelection().removeAllRanges();
  };
  const onSelectionEnd = () => {
    if (isSelectionStart.current) {
      window.getSelection().removeAllRanges();
    }
    isSelectionStart.current = false;
  };
  const onSelectionDragStart = (e, nodes) => {
    currentDragNodes.current = nodes.map(x => ({
      id: x.id,
      position: x.position,
    }));
  };
  const onSelectionDragStop = (e, nodes) => {
    if (nodes[0].dragging) {
      const startEndPositionArr = getStartEndPositionArr(nodes);
      onNodesEdgesDrag(startEndPositionArr);
    }
    currentDragNodes.current = [];
  };
  function needAddNodeByDrag() {
    return nodesRef.current.every(
      x =>
        x.data.displayType !== "uploadFile" ||
        (x.data.displayType === "uploadFile" && !func.isEmpty(x.data.textAreaValue))
    );
  }
  const onNodeClick = (e, node) => {
    let arr = [...checkNodeArr];
    let edgeArr = [...checkEdgeArr];

    const target = e.target;
    // class 为空不往下执行, 某些特殊元素 className 不是字符串
    if (!target || func.isEmpty(target.className) || (isString(target.className) && target.className?.includes("app")))
      return;
    const isFocusOnTarget = target === document.activeElement;

    if (isNewEdgeRunRef.current && NEW_EDGE_REG.test(node.id)) {
      const lineIds = getNewEdgeAllLineIdsByNode(node);
      const filterArr = checkEdgeArr.filter(x => !lineIds.includes(x));
      const newArr = filterArr.length < checkEdgeArr.length ? filterArr : [...checkEdgeArr, ...lineIds];
      setCheckEdgeArr(newArr);
      if (!checkNodeArr.find(x => x === node.id)) {
        setCheckNodeArr([...checkNodeArr, node.id]);
      }
      return;
    }

    //没有按shift
    if (!e.shiftKey) {
      if (arr.includes(node.id)) {
        arr = [];
      } else {
        // STUD-1259：在编辑状态点击输入框不显示选中框
        if (target.tagName === "INPUT" && isFocusOnTarget) {
          return;
        }
        if (target.tagName === "TEXTAREA" && isFocusOnTarget) {
          return;
        }
        arr = [node.id];
      }
      // 清空选中的line
      edgeArr = [];
    } else {
      // 是否保存过
      if (arr.includes(node.id)) {
        const index = arr.findIndex(x => x === node.id);
        arr.splice(index, 1);
      } else {
        arr.push(node.id);
      }
    }

    //只选择一个时，显示右侧悬浮窗口
    if (arr.length === 1) {
      clickCustomeNode(node.id);
    }
    setCheckNodeArr(arr);
    setCheckEdgeArr(edgeArr);
  };
  const onNodeDragStart = (e, node) => {
    if (!e?.target?.className?.includes?.("iconfont")) {
      currentDragNodes.current = [
        {
          id: node.id,
          position: node.position,
        },
      ];
    }

    isFireNodeDragStopEvent.current = false;
    if (!showCopyMouseStyl) return;
    copyingNodeIdRef.current = node.id;
    // 复制node
    const newNodeId = `editor-${nodeIndexRef.current++}`;
    const newNode = {
      id: newNodeId,
      type: "customNode",
      data: {
        ...node.data,
      },
      position: {
        x: node.position.x,
        y: node.position.y,
      },
    };
    setNodes(prevList => [...prevList, newNode]);

    // 将被复制的node的边重新使用新node连接，使视觉效果上看起来拖拽复制的是新的node
    setEdges(prevList => {
      return prevList.map(x => {
        if (x.source === node.id) {
          return {
            ...x,
            source: newNodeId,
          };
        }
        if (x.target === node.id) {
          return {
            ...x,
            target: newNodeId,
          };
        }
        return x;
      });
    });
    setNodes(prevList => {
      return prevList.map(x => {
        if (NEW_EDGE_REG.test(x.id)) {
          /// copy edge source
          if (x.data?.flows?.find(x => x.sourceNodeId === node.id)) {
            return {
              ...x,
              data: {
                ...x.data,
                flows: x.data?.flows.map(flow => {
                  if (flow.sourceNodeId === node.id) {
                    return {
                      ...flow,
                      sourceNodeId: newNodeId,
                    };
                  }
                  return flow;
                }),
              },
            };
          }
          /// copy edge target
          if (x.data?.targetNodeId === node.id) {
            return {
              ...x,
              data: {
                ...x.data,
                targetNodeId: newNodeId,
              },
            };
          }
        }
        return x;
      });
    });
  };
  function getStartEndPositionArr(dragNodes) {
    return currentDragNodes.current
      .map(x => {
        const currNode = dragNodes.find(y => y.id === x.id);
        if (!func.isEmpty(currNode)) {
          return {
            id: x.id,
            startPosition: x.position,
            endPosition: currNode.position,
          };
        }
        return {};
      })
      .filter(x => !func.isEmpty(x));
  }
  const onNodeDragStop = (e, node) => {
    // 这里dragging判定是因为点击node也会触发
    if (node.dragging) {
      const startEndPositionArr = getStartEndPositionArr([node]);
      onNodesEdgesDrag(startEndPositionArr);
    }
    currentDragNodes.current = [];
    isFireNodeDragStopEvent.current = true;
    // 先松开alt则判定是否为复制状态拖拽
    if (showCopyMouseStyl || !func.isEmpty(copyingNodeIdRef.current)) {
      copyingNodeIdRef.current = "";
      const newNodeId = `editor-${nodeIndexRef.current - 1}`;
      // 交换新增复制node与被复制node的id
      setNodes(prevList => {
        return prevList.map(x => {
          if (x.id === node.id) {
            return {
              ...x,
              id: newNodeId,
            };
          }
          if (x.id === newNodeId) {
            return {
              ...x,
              id: node.id,
            };
          }

          if (NEW_EDGE_REG.test(x.id)) {
            /// restore copy edge source
            if (x.data?.flows?.find(x => x.sourceNodeId === newNodeId)) {
              return {
                ...x,
                data: {
                  ...x.data,
                  flows: x.data?.flows.map(flow => {
                    if (flow.sourceNodeId === newNodeId) {
                      return {
                        ...flow,
                        sourceNodeId: node.id,
                      };
                    }
                    return flow;
                  }),
                },
              };
            }
            /// restore copy edge target
            if (x.data?.targetNodeId === newNodeId) {
              return {
                ...x,
                data: {
                  ...x.data,
                  targetNodeId: node.id,
                },
              };
            }
          }
          return x;
        });
      });
      // 将新增复制node的边重新连接到被复制的node上
      setEdges(prevList => {
        return prevList.map(x => {
          if (x.source === newNodeId) {
            return {
              ...x,
              source: node.id,
            };
          }
          if (x.target === newNodeId) {
            return {
              ...x,
              target: node.id,
            };
          }
          return x;
        });
      });
    }
  };
  const onNodesChange = useCallback(changes => setNodes(nds => applyNodeChanges(changes, nds)), [setNodes]);
  const onNodeMouseEnter = () => {
    isMouseInNode.current = true;
    setShowCopyMouseStyl(altOptionPressed);
  };
  const onNodeMouseLeave = () => {
    isMouseInNode.current = false;
    setShowCopyMouseStyl(false);
  };
  const onEdgeClick = (e, line) => {
    console.log("onEdgeClick :>> ");
    let arr = [...checkEdgeArr];
    let nodeArr = [...checkNodeArr];
    if (isNewEdgeRunRef.current) {
      const edgeData = getNewEdgeDataByLine(line);
      const lineIds = edgeData.lineIds;
      const edge = edgeData.edge;
      setHoverEdgeData({
        state: true,
        lineIds,
        edgeId: edge.id,
        isSelected: true,
      });
      if (!e.shiftKey) {
        setCheckEdgeArr(lineIds);
        setCheckNodeArr([]);
      }
      if (!checkNodeArr.find(x => x === edge.id)) {
        setCheckNodeArr([...checkNodeArr, edge.id]);
      }
      return;
    }
    //没有按shift
    if (!e.shiftKey) {
      if (arr.includes(line.id)) {
        arr = [];
      } else {
        arr = [line.id];
      }
      // 清空选中的node
      nodeArr = [];
    } else {
      // 是否保存过
      if (arr.includes(line.id)) {
        const index = arr.findIndex(x => x === line.id);
        arr.splice(index, 1);
      } else {
        arr.push(line.id);
      }
    }
    setCheckEdgeArr(arr);
    setCheckNodeArr(nodeArr);
  };
  const onEdgesChange = useCallback(
    changes => {
      setEdges(eds => applyEdgeChanges(changes, eds));
    },
    [setEdges]
  );
  // function handleEdgeMouseEvent(edge, isEnter = false) {
  //   if(!isNewEdgeRunRef.current) return;
  //   let currEdge = {}
  //   if(NEW_EDGE_REG.test(edge.source)) {
  //     currEdge = nodesRef.current.find(x => x.id === edge.source)
  //   } else if(NEW_EDGE_REG.test(edge.target)) {
  //     currEdge = nodesRef.current.find(x => x.id === edge.target)
  //   }
  //   setHoverEdgeData({
  //     state: isEnter,
  //     lineIds: [currEdge.data.targetLineId, ...currEdge.data.flows.map(x=> x.sourceLineId)],
  //     edgeId: currEdge.id
  //   })
  // }
  function onEdgeMouseEnter(e, edge) {
    // handleEdgeMouseEvent(edge, true)
  }
  function onEdgeMouseLeave(e, edge) {
    // handleEdgeMouseEvent(edge)
  }
  const onThinkChange = e => {
    setThinkValue(e.target.value);
  };

  const setNewLinkEdge = customParams => {
    // 判定新建边指向 node 是否存在多条
    const targetNodeAlledge = edgesRef.current.filter(x => x.target === customParams.target);
    if (!func.isEmpty(targetNodeAlledge)) {
      // 存在多条 创建variable
      const node = nodesRef.current.find(x => x.id === customParams.source);

      const variableList = store.getState().fot.variableList;
      const variableNodeData = variableList.find(x => x?.node?.id === node.id);

      nodeContextMenuRef.current = node;
      manuallyConnectedEdges.current = customParams;
      // 多条边 主运行边
      multipleEdgeHostRef.current = targetNodeAlledge.find(x => !func.isEmpty(x.data.lineParam.enterText));
      // 如果已经有对应变量则不需要再显示弹窗
      if (!func.isEmpty(variableNodeData)) {
        setTimeout(() => {
          setManualRecall(variableNodeData.name);
        }, 300);
        return;
      }
      // 打开变量弹窗
      clickToCreateVariable(true);
    }
  };

  // 手动连线触发
  const onConnect = useCallback(
    params => {
      if (isNewEdgeRunRef.current) {
        // v3
        handleManualLine(params);
        // 第二个input node连线后 关闭tooltip
        const secondInputId = store.getState().editor.secondInputNodeId;
        if (secondInputId === params.source) {
          setCloseToolTipArr([secondInputId]);
        }
        return;
      }
      let newEdges = [];
      setEdges(eds => {
        const customParams = Object.assign({}, params, {
          id: `edge-${edgeIndexRef.current++}`,
          type: "customEdge",
          animated: true,
          markerEnd,
          data: {
            ...edgeDataRef.current,
          },
        });

        const enableManualConnectionPromptGeneration =
          get(store.getState().editor.me, "feature_tags", []).includes(
            "disable_manual_connection_prompt_generation"
          ) === false;
        if (enableManualConnectionPromptGeneration) {
          // 处理新链接边
          setNewLinkEdge(customParams);
        }

        // 取消重复连线
        if (
          eds.some(
            j =>
              (j.source === params.source && j.target === params.target) ||
              (params.source === j.target && params.target === j.source)
          )
        )
          return [...eds];

        newEdges = [customParams, ...eds];
        return newEdges;
      });
      judgePlaceHolderDisplay(nodesRef.current, newEdges);
    },
    [setEdges]
  );

  const onConnectStart = useCallback((e, params) => {
    setIsDraggingCreatingNewLine({
      state: true,
      nodeId: params.nodeId,
    });
  }, []);

  const onConnectEnd = useCallback(() => {
    setIsDraggingCreatingNewLine({
      state: false,
      nodeId: "",
    });
  }, []);

  // const getNeedModeNode = (otherProps, direction) => {
  //   const nodes = getNodes()
  //   const edges = getEdges()
  //   const allConnectedNodeIds = edges.filter(x => x.source === otherProps.id).map(x => x.target)
  //   const allConnectedNodes = nodes.filter(x => allConnectedNodeIds.includes(x.id))
  //   return allConnectedNodes.filter(n => {
  //     if(direction === 'right') {
  //       return n.position.x > otherProps.xPos
  //     } else if (direction === 'left') {
  //       return n.position.x < otherProps.xPos
  //     } else if(direction === 'top') {
  //       return n.position.y < otherProps.yPos
  //     } else if (direction === 'bottom') {
  //       return n.position.y > otherProps.yPos
  //     }
  //     return false
  //   })
  // }
  function expandBluePrint(id, bluePrintValue) {
    const nodes = getExpandedBluePrintNodes({ id, bluePrintValue, nodeDataRef, nodeIndexRef, nodesRef });
    setNodes(nodes);
  }

  function clickAddBtn(direction, otherProps, setCenter, getZoom, displayType) {
    if (canvasMovingLoadingRef.current) return;
    const isStickie = displayType === "inputTextarea";
    let offsetDistance = !isStickie ? 370 : 270;
    offsetDistance += otherProps.width;
    const startPointValue = 300;
    let newAddEdge = {};
    if (isNewEdgeRunRef.current) {
      newAddEdge = createNewEdge(otherProps);
    }
    const newNodeId = `editor-${nodeIndexRef.current++}`;
    const needMoveNode = getNodes().filter(x => !NEW_EDGE_REG.test(x.id));

    //获取最下方或者最右方的node位置
    const adjacentNodepPosition = utilIndex.getBottomOrRightPosition(needMoveNode, direction, offsetDistance, {
      x: otherProps.xPos,
      y: otherProps.yPos,
    });

    //  通过AIChat模板创建的chatInterface，需要show tooltip
    const isChatAiOfNode = otherProps.autoSelectFunc === "chatInterface" ? { showChatInterfaceTooltip: true } : {};
    const newNode = {
      id: newNodeId,
      type: "customNode",
      data: Object.assign(
        {},
        { ...nodeDataRef.current },
        { displayType: isStickie ? "inputTextarea" : "text" },
        { showFeedback: false },
        { ...isChatAiOfNode }
      ),
      position: {
        x: otherProps.xPos,
        y: otherProps.yPos,
      },
    };
    //新node按照已有的输出node位置调整
    if (direction === "left" || direction === "right") {
      newNode.position.y = adjacentNodepPosition;
    } else {
      newNode.position.x = adjacentNodepPosition;
    }

    // 计算中心点
    const sourceCenterPos = {
      width: otherProps.width,
      height: otherProps.height,
      position: {
        x: otherProps.xPos,
        y: otherProps.yPos,
      },
    };
    // 620 是动画完成后真实的位置
    const targetCenterPos = {
      width: otherProps.width,
      height: otherProps.height,
      position: {
        x: newNode.position.x + 620,
        y: newNode.position.y,
      },
    };
    const centerData = focusPosition(sourceCenterPos, targetCenterPos);
    // 创建边时边伸出的动画和边居中的动画需要同时进行
    if (centerData) {
      canvasMovingLoadingRef.current = true;
      // FIXME: 创建边伸出去的动画和边聚焦的动画坐标计算， x 有 52 左右的偏差, 导致两个动画都被执行
      flowManager
        .focus(centerData, {
          duration: 2000,
          zoom: 1,
          // FIXME: clickAddBtn 可能被缓存，导致 setCenter 和 showPreviewPanel 状态无法更新
          setCenter,
          showPreviewPanel: showPreviewPanelRef.current,
        })
        .finally(() => {
          canvasMovingLoadingRef.current = false;
        });
    }
    // setCenter(centerData.x, centerData.y, { duration: 2000, zoom: 1 });

    // 从300开始缓慢移动
    if (direction === "top") {
      newNode.position.y -= startPointValue;
    } else if (direction === "right") {
      newNode.position.x += startPointValue;
    } else if (direction === "bottom") {
      newNode.position.y += startPointValue;
    } else if (direction === "left") {
      newNode.position.x -= startPointValue;
    }
    // const arr = [...nodesRef.current, newNode].reverse()
    // const newNodes = utilIndex.autoSort(arr, needMoveNode, direction, newNode)
    // const oldNodePostionData = needMoveNode.map(x => ({
    //   id: x.id,
    //   startPosition: nodesRef.current.find(y => y.id === x.id)?.position || {},
    //   endPosition: newNodes.find(y => y.id === x.id)?.position || {},
    // }))
    // setNodes(newNodes)
    // setNodes(prev => {
    //   return [...prev, newNode]
    // })

    if (isNewEdgeRunRef.current) {
      handleUpdateTarget({
        newNode,
        edgeNode: newAddEdge,
      });
      const sourceLine = createNewLine({
        source: otherProps.id,
        target: newAddEdge.id,
        type: "source",
      });
      const targetLine = createNewLine({
        sourceNode: otherProps,
        source: newAddEdge.id,
        target: newNode.id,
        type: "target",
      });
      // 移动节点
      translateNode(newNode, direction, offsetDistance - startPointValue, 10);
      logEvent("create_edge");
      // 这里需要重新获取最新的node, 因为flows及id等数据存在更新
      const updatedNewNode = nodesRef.current.find(x => x.id === newNodeId);
      const updatedNewEdge = nodesRef.current.find(x => x.id === newAddEdge.id);
      onNodesEdgesAdd(
        [getSimpleNodeV3(updatedNewNode), getSimpleNodeV3(updatedNewEdge)],
        [getSimpleEdgeV3(sourceLine), getSimpleEdgeV3(targetLine)]
      );
      return;
    }

    setNodes(prev => {
      return [...prev, newNode];
    });

    // 新增连线
    /**
     * @type {import('../../base/util/CustomEdgeUtil').CustomEdgeType}
     */
    const newEdge = {
      id: `edge-${edgeIndexRef.current++}`,
      type: "customEdge",
      animated: true,
      source: otherProps.id,
      target: newNode.id,
      sourceHandle: DIRECTIONHANDLE[direction].source,
      targetHandle: DIRECTIONHANDLE[direction].target,
      markerEnd,
      data: {
        ...edgeDataRef.current,
        sourceHandle: DIRECTIONHANDLE[direction].source,
        targetHandle: DIRECTIONHANDLE[direction].target,
        // 添加边时的方向
        fromDirection: direction,
        lineParam: {
          ...edgeDataRef.current.lineParam,
          enterText: otherProps.enterText || "",
          queryValue: otherProps.queryValue || edgeDataRef.current.lineParam.queryValue,
        },
        isSliceAutoRun: otherProps?.isSliceAutoRun || false,
        selectedApi: otherProps?.selectedApi || {},
      },
    };
    setEdges(prevList => [...prevList, newEdge]);
    logEvent("create_edge");
    onNodesEdgesAdd([getSimpleNode(newNode)], [getSimpleEdge(newEdge)]);

    // 移动节点
    translateNode(newNode, direction, offsetDistance - startPointValue, 10);
  }

  function translateNode(newNode, direction, offsetDistance, count = 10) {
    let times = 1;
    const newNodePosition = newNode.position;
    const timer = setInterval(() => {
      if (times > count) {
        clearInterval(timer);
        return;
      }
      const currentDistance = (easeInOutExpo(times / count) - easeInOutExpo((times - 1) / count)) * offsetDistance;
      if (direction === "top") {
        newNodePosition.y -= currentDistance;
      } else if (direction === "right") {
        newNodePosition.x += currentDistance;
      } else if (direction === "bottom") {
        newNodePosition.y += currentDistance;
      } else if (direction === "left") {
        newNodePosition.x -= currentDistance;
      }
      updateNodePostion(newNode.id, newNodePosition);
      times += 1;
    }, 10);
  }
  function updateNodePostion(nodeId, position) {
    setNodes(prevList => {
      return prevList.map(x => {
        if (x.id === nodeId) {
          return {
            ...x,
            position,
          };
        }
        return x;
      });
    });
  }
  function setNodeParam(newNodeParam) {
    setNodes(prevList => {
      return prevList.map(l => {
        if (l.id === newNodeParam.id) {
          return {
            ...l,
            data: {
              ...l.data,
              ...newNodeParam.data,
            },
          };
        }
        return l;
      });
    });
  }
  function setEdgeLineParam(newLineParam) {
    if (isNewEdgeRunRef.current) {
      setNodes(prevList => {
        return prevList.map(l => {
          if (l.id === newLineParam.id) {
            return {
              ...l,
              data: {
                ...l.data,
                lineParam: {
                  ...l.data.lineParam,
                  ...newLineParam,
                },
              },
            };
          }
          return l;
        });
      });
      return;
    }
    setEdges(prevList => {
      return prevList.map(l => {
        if (l.id === newLineParam.id) {
          return {
            ...l,
            data: {
              ...l.data,
              lineParam: {
                ...l.data.lineParam,
                ...newLineParam,
              },
            },
          };
        }
        return l;
      });
    });
  }

  function setVarilableNames(edge, variables) {
    setEdges(prevList => {
      return prevList.map(l => {
        const arr = l?.data?.lineParam?.varilableNames || [];
        if (l.id === edge.id && !arr.includes(variables)) {
          return {
            ...l,
            data: {
              ...l.data,
              lineParam: {
                ...l.data.lineParam,
                varilableNames: [...arr, variables],
              },
            },
          };
        }
        return l;
      });
    });
  }

  async function getAndSetQuery(id, text) {
    try {
      setQueryLoading(id, "getQueryLoading", true);
      const identifier = await getEdgeGeneration(text, id);
      setQueryLoading(id, "getQueryLoading", false);
      if (identifier) {
        setEdges(prevList => {
          return prevList.map(l => {
            if (l.id === id) {
              return {
                ...l,
                data: {
                  ...l.data,
                  lineParam: {
                    ...l.data.lineParam,
                    lineType: "identifier",
                    identifier,
                    enterText: text,
                  },
                },
              };
            }
            return l;
          });
        });
        setQueryLoading(id, "isGetQuery", true);
      }
      return {
        lineType: "identifier",
        identifier,
      };
    } catch (error) {
      func.messageUtil(error);
      console.error("get And Set Query", error);
      setQueryLoading(id, "getQueryLoading", false);
    }
  }
  function setQueryLoading(id, keyStr, value) {
    setEdges(prevList => {
      return prevList.map(l => {
        if (l.id === id) {
          return {
            ...l,
            data: {
              ...l.data,
              [keyStr]: value,
            },
          };
        }
        return l;
      });
    });
  }

  const postBrainOpenai = async (apiParam, onMessage) => {
    const { sourceEdgeId, sourceNodeId, textAreaValue, lineParam, targetNodeId } = apiParam;
    try {
      if (feature_tags.enableSkipEmptyVerification && skipEmptyVerification(lineParam?.enterText)) {
        // Skip empty verification
      } else if (func.isEmpty(textAreaValue)) {
        func.customMsg({
          content: "Need input value",
          type: "warning",
        });
        return {};
      }

      // 请求前重置错误提示, 有可能 targetNodeId 并非是节点ID，可能是组，ID需要设置组ID下面的节点ID
      setNodeErrorTip(targetNodeId, {}, true);

      const isUploadFile = await handleUploadFileRun(lineParam.enterText, sourceNodeId, targetNodeId, sourceEdgeId);
      if (isUploadFile) return;

      // 下拉框选择模式
      if (selectApiReg.test(lineParam.enterText)) {
        const edgeParam = {
          id: sourceEdgeId,
          target: targetNodeId,
          source: sourceNodeId,
          lineParam,
        };
        const builtInFunctionObj = defaultApis.find(x => x.label === lineParam.enterText.replace("/", ""));
        let type = builtInFunctionObj?.value || lineParam.enterText.replace("/", "");
        return await getOtherTypeData(edgeParam, type, textAreaValue);
      }
      // 是聊天框类型
      const isChat = await setChatBoxParam({
        edgeId: sourceEdgeId,
        target: targetNodeId,
        lineParams: lineParam,
        setNodes,
        setEdgeLineParam,
      });
      if (isChat) return;

      // If it is Agent type
      if (feature_tags.showStudiosAgent) {
        const isAgent = await setConnectAgentNode({
          selectValueParam: lineParam?.enterText,
          nodeText: textAreaValue,
          edgeId: sourceEdgeId,
          target: targetNodeId,
          lineParams: lineParam,
          setNodes,
          setEdgeLineParam,
        });
        if (isAgent) return;
      }

      // 默认模式
      let postParam = {};
      let url = "/be/bas-demo-v4/nlp/brain_openai";
      if (lineParam.lineType === "identifier") {
        url = "/stream/bas-demo-v4/nlp/completions_generation";
        postParam = await getCompletionsGenerationParam("object_id", lineParam.identifier.value, {
          input: textAreaValue,
        });
      } else {
        postParam = getOpenAiParam({
          lineParams: lineParam,
          prompt: lineParam.queryValue || "",
          nodeText: textAreaValue,
        });
      }
      let temp = "";
      await postWithStreaming(
        url,
        postParam,
        msg => {
          temp += msg.choices?.[0]?.text || msg.choices?.[0]?.delta?.content || "";
          onMessage(targetNodeId, temp);
        },
        false,
        postInstance => {
          setNodeParam({
            id: targetNodeId,
            data: {
              showStopGenerateBtn: true,
              abortNode: {
                source: sourceNodeId,
                target: targetNodeId,
                edgeId: sourceEdgeId,
                apiInstance: postInstance,
              },
            },
          });
        }
      );
    } catch (error) {
      console.log("postBrainOpenai", error);
      setNodeErrorTip(targetNodeId, error);
    } finally {
      setNodeParam({
        id: targetNodeId,
        data: {
          showStopGenerateBtn: false,
          abortNode: {},
        },
      });
    }
  };
  const groupPostApi = async (apiArr, onMessage) => {
    if (isEmpty(apiArr)) {
      return;
    }
    let index = 0;
    let results = [];
    const requestingCounts = 4;
    while (index < apiArr.length) {
      const newArr = apiArr.slice(index, index + requestingCounts).map(x => postBrainOpenai(x, onMessage));
      let edgeIds = apiArr.slice(index, index + requestingCounts).map(x => x.sourceEdgeId);
      edgeIds = [...new Set(edgeIds)];
      // 显示run loading
      edgeIds.forEach(x => {
        setEdgeLineParam({
          id: x,
          loading: true,
        });
      });
      // 已经执行过的数据
      setAlreadyRunArr([...alreadyRunRef.current, ...edgeIds]);
      runAllDisable();
      const currResults = await Promise.allSettled(newArr);
      edgeIds.forEach(x => {
        setEdgeLineParam({
          id: x,
          loading: false,
        });
      });
      results = [...results, ...currResults];
      index += requestingCounts;
    }
    // TODO: Hardcoded
    const edge = apiArr[0];
    const funcName = edge.lineParam.enterText.replace("/", "");
    const builtInFunctionObj = defaultApis.find(x => x.label === funcName);
    const type = builtInFunctionObj?.value || funcName;
    const isShowStockInterface = type === "showStockInterface";
    if (results.length > 0 && isShowStockInterface) {
      const resData = results
        .filter(x => x.status === "fulfilled")
        .map(x => x.value)
        .flat();
      shardSplittingAll(edge, resData, "showStockInterface");
    }
    return results;
  };
  function groupChanged(id) {
    setEdgeLineParam({
      id: id,
      loading: true,
    });
    const nodes = getNodes();
    const edges = getEdges();

    const groupEdges = edges.find(x => x.id === id);
    const delGroupNode = nodes.find(x => x.id === groupEdges.target);
    const nodeTextAreaValue = nodes.find(x => x.id === groupEdges.source);
    const groupChildNode = delGroupNode?.data?.childNode || [];
    const newNodeId = `editor-${nodeIndexRef.current++}`;

    const newNode = {
      id: newNodeId,
      type: "customNode",
      data: {
        ...nodeDataRef.current,
      },
      position: {
        x: delGroupNode.position.x,
        y: delGroupNode.position.y,
      },
    };

    // 分组被改变
    setNodes(prevNodes => {
      const prevList = prevNodes.filter(x => x.id !== groupEdges.target && !groupChildNode.includes(x.id));
      return [...prevList, newNode];
    });

    // 更改子node链接父级
    setEdges(prevEdges => {
      const prevList = prevEdges.filter(x => !groupChildNode.includes(x.source));
      return prevList.map(x => {
        if (x.id === groupEdges.id) {
          return Object.assign({}, { ...x }, { target: newNodeId });
        }
        return x;
      });
    });

    const edgesData = edgesRef.current.find(x => x.id === groupEdges.id);

    const apiParam = {
      lineParam: edgesData.data.lineParam,
      sourceEdgeId: edgesData.id,
      sourceNodeId: edgesData.source,
      targetNodeId: edgesData.target,
      textAreaValue: nodeTextAreaValue.data.textAreaValue,
    };
    postBrainOpenai(apiParam, (target, tempText) => {
      setNodes(prevList => {
        return prevList.map(l => {
          if (l.id === target) {
            // 自动判定获取内容 displayType
            const displayType = getAutomaticDisplayType(l.data, tempText);
            return {
              ...l,
              data: {
                ...l.data,
                textAreaValue: tempText.trim(),
                displayType: displayType,
              },
            };
          }
          return l;
        });
      });
    });
    setEdgeLineParam({
      id: id,
      loading: false,
    });
  }
  function handleSlice(currentNode, target) {
    const groupNode = nodesRef.current.find(x => x.id === target);
    const targetArr = nodesRef.current.filter(n => n.parentNode === target) || [];
    let childNode = groupNode?.data?.childNode || [];
    const targetIds = targetArr.map(x => x.id);

    childNode = [...new Set([...childNode, ...targetIds])];
    //获取切分后的文本数组
    let array = splitText(currentNode?.data?.textAreaValue).filter(x => !func.isEmpty(x));
    //删除全部source，重新添加child node
    const moreChildNode = (targetArr || []).map(x => x.id);
    setNodes(prevList => prevList.filter(x => !moreChildNode.includes(x.id)));
    //给child node赋值
    const newNodes = [];
    const sliceLevel = targetArr[0]?.data?.sliceLevel || 1;
    array.forEach((x, index) => {
      let newNodeId = "";

      if (!func.isEmpty(childNode[index])) {
        newNodeId = childNode[index];
      } else {
        newNodeId = `editor-${nodeIndexRef.current++}`;
        childNode.push(newNodeId);
      }
      const newNode = {
        id: newNodeId,
        type: "customNode",
        parentNode: target,
        extent: "parent",
        data: {
          ...currentNode.data,
          textAreaValue: x.trim(),
          sliceLevel,
          isSliceNode: true,
          parentNodeId: target,
        },
        position: {
          x: distance,
          y: distance * (index + 1) + index * 200,
        },
      };

      newNodes.push(newNode);
    });
    setNodes(prevList => [...prevList, ...newNodes]);
    const source = [...targetArr.slice(0, array.length), ...newNodes];
    //group重新计算高度
    // const groupHeight = array.length * 200 + (array.length + 1) * distance

    // const obj = {
    //   style: {
    //     width: 200 + 4 * distance,
    //     height: groupHeight
    //   },
    // }
    setNodes(prevList => {
      return prevList.map(pre => {
        if (pre.id === target) {
          return {
            ...pre,
            // ...obj,
            data: {
              ...pre.data,
              childNode,
            },
          };
        }
        return pre;
      });
    });
    return {
      sourceArr: source,
    };
  }
  const handelGroupNode = edge => {
    const sourceArr = nodesRef.current.filter(n => n.parentNode === edge.source) || [];
    const targetArr = nodesRef.current.filter(n => n.parentNode === edge.target) || [];
    //按Y轴排序
    sourceArr.sort((a, b) => a.position.y - b.position.y);
    targetArr.sort((a, b) => a.position.y - b.position.y);

    //给child node赋值
    const sourceLen = sourceArr.length;
    const targetLen = targetArr.length;
    let result = {
      sourceArr,
      targetArr: targetArr.slice(0, sourceLen),
    };

    if (sourceLen === targetLen) {
      return result;
    } else if (sourceLen < targetLen) {
      //删除多余的node
      // source nodes数量比target少
      // TODO: Hardcoded
      const funcName = edge?.data?.lineParam?.enterText.replace("/", "");
      const builtInFunctionObj = defaultApis.find(x => x.label === funcName);
      const type = builtInFunctionObj?.value || funcName;
      const isShowStockInterface = type === "showStockInterface";
      if (!isShowStockInterface) {
        const moreChildNode = (targetArr.slice(sourceArr.length) || []).map(x => x.id);
        setNodes(prevList => prevList.filter(x => !moreChildNode.includes(x.id)));
      }
    } else {
      // source nodes比target多
      const newNodes = [];
      const sliceLevel = targetArr[0]?.data?.sliceLevel || 1;
      const targetGroup = nodesRef.current.find(x => x.id === edge.target);
      let targetChildNode = targetGroup?.data?.childNode || [];
      const targetId = targetArr.map(x => x.id);
      targetChildNode = Array.from(new Set([...targetChildNode, ...targetId]));
      for (let i = targetLen; i < sourceLen; i++) {
        let newNodeId = "";

        if (!func.isEmpty(targetChildNode[i])) {
          newNodeId = targetChildNode[i];
        } else {
          newNodeId = `editor-${nodeIndexRef.current++}`;
          targetChildNode.push(newNodeId);
        }
        const newNode = {
          id: newNodeId,
          type: "customNode",
          parentNode: edge.target,
          extent: "parent",
          data: {
            ...nodeDataRef.current,
            textAreaValue: "",
            sliceLevel,
            isSliceNode: true,
            parentNodeId: edge.target,
          },
          position: {
            x: 2 * distance,
            y: distance * (i + 1) + i * 200,
          },
        };
        newNodes.push(newNode);
      }
      setNodes(prevList => [...prevList, ...newNodes]);
      setNodes(prevList => {
        return prevList.map(pre => {
          if (pre.id === edge.target) {
            return {
              ...pre,
              data: {
                ...pre.data,
                childNode: targetChildNode,
              },
            };
          }
          return pre;
        });
      });
      result = { sourceArr, targetArr: [...targetArr, ...newNodes] };
    }

    return result;
  };
  const handleApiCall = async sourceNodes => {
    if (func.isEmpty(sourceNodes)) return;
    // 当前sourceNodes对应的targetNodes
    let targetNodeIds = [];
    let apiArr = [];
    // 如果当前sourceNodes包含group，则需要查找出来当前group下面所有有边的nodes
    const sourceGroups = sourceNodes.filter(x => GROUPID_REG.test(x.id));
    if (!func.isEmpty(sourceGroups)) {
      const currGroupNodes = nodes.filter(x => sourceGroups.some(y => x.parentNode === y.id));
      const hasEdgeNodes = currGroupNodes.filter(x => edgesRef.current.filter(y => y.source === x.id));
      sourceNodes = [...sourceNodes, ...hasEdgeNodes];
    }

    sourceNodes.forEach(x => {
      let currSourceEdges = edgesRef.current.filter(y => y.source === x.id);
      // 过滤掉 disabledRun 的 edge
      currSourceEdges = currSourceEdges.filter(item => !item.data.lineParam.disabledRun);
      if (func.isEmpty(currSourceEdges)) return;
      targetNodeIds = [...targetNodeIds, ...currSourceEdges.map(y => y.target)];
      currSourceEdges.forEach(y => {
        if (y.data.isCreatedBySlice) {
          handleSlice(x, y.target);
          // const { sourceArr } = handleSlice(x, y.target)
          // const targetNode = sourceArr.map(n => n.id)
          // targetNodeIds = [...targetNodeIds, ...targetNode]
        } else if (GROUPID_REG.test(y.source)) {
          const { sourceArr, targetArr } = handelGroupNode(y);
          // const targetNode = (targetArr || []).map(n => n.id)
          // targetNodeIds = [...targetNodeIds, ...targetNode].filter(x => !GROUPID_REG.test(x))
          // bsf-2176 当 lineType 为 identifier 时, 过滤掉没用的边
          if (!CustomEdgeUtil.hasRunWithLineParam(y.data.lineParam)) {
            return;
          }
          sourceArr.forEach((source, index) => {
            const targetNode = targetArr[index];
            apiArr.push({
              sourceEdgeId: y.id,
              // sourceNodeId: y.source,
              sourceNodeId: source.id,
              targetNodeId: targetNode.id,
              textAreaValue: source.data.textAreaValue,
              lineParam: Object.assign({}, y.data.lineParam, { isFromGroup: true }),
              source: y.source,
              target: y.target,
              id: y.id,
            });
          });
        } else {
          // bsf-2176 当 lineType 为 identifier 时, 过滤掉没用的边
          if (!CustomEdgeUtil.hasRunWithLineParam(y.data.lineParam)) {
            return;
          }
          apiArr.push({
            sourceEdgeId: y.id,
            sourceNodeId: y.source,
            targetNodeId: y.target,
            textAreaValue: x.data.textAreaValue,
            lineParam: y.data.lineParam,
          });
        }
      });
    });

    if (func.isEmpty(apiArr) && func.isEmpty(targetNodeIds)) return;
    // 分组请求
    await groupPostApi(apiArr, (target, tempText) => {
      setNodes(prevList => {
        return prevList.map(l => {
          if (l.id === target) {
            // 自动判定获取内容 displayType
            const displayType = getAutomaticDisplayType(l.data, tempText);
            return {
              ...l,
              data: {
                ...l.data,
                textAreaValue: tempText.trim(),
                displayType: displayType,
              },
            };
          }
          return l;
        });
      });
    });
    // const resValues = res
    //   .filter(x => x.status === 'fulfilled' && !util.isEmpty(x.value))
    //   .map(x => x.value)
    // if (!util.isEmpty(resValues)) {
    //   // 更新targetNode数据
    //   setNodes(prevList => {
    //     return prevList.map(l => {
    //       const currRes = resValues.find(x => x.targetNodeId === l.id)
    //       if (!util.isEmpty(currRes)) {
    //         return {
    //           ...l,
    //           data: {
    //             ...l.data,
    //             textAreaValue: currRes.text
    //           }
    //         }
    //       }
    //       return l
    //     })
    //   })
    // }
    const targetNodes = nodesRef.current.filter(x => targetNodeIds.includes(x.id));
    // 递归调用
    await handleApiCall(targetNodes);
  };
  async function runSingleEdge(edgeId) {
    const edge = edgesRef.current.find(x => x.id === edgeId);
    if (isEmpty(edge)) {
      return;
    }
    const node = nodesRef.current.find(x => x.id === edge.source);
    if (isEmpty(node)) {
      return;
    }
    const apiArr = [
      {
        sourceEdgeId: edge.id,
        sourceNodeId: edge.source,
        targetNodeId: edge.target,
        textAreaValue: node.data.textAreaValue,
        lineParam: edge.data.lineParam,
      },
    ];
    await groupPostApi(apiArr, (target, tempText) => {
      setNodes(prevList => {
        return prevList.map(l => {
          if (l.id === target) {
            // 自动判定获取内容 displayType
            const displayType = getAutomaticDisplayType(l.data, tempText);
            return {
              ...l,
              data: {
                ...l.data,
                textAreaValue: tempText.trim(),
                displayType: displayType,
              },
            };
          }
          return l;
        });
      });
    });
    await setTargetNodeShowFeedback(edge.target); //show feedback buttons after edge is run
  }

  function clickAddNodeBtn(e, data = {}) {
    const newNodeId = `editor-${nodeIndexRef.current++}`;
    saveSecondInputNodeId(newNodeId);
    const position = getNewNodePosition(e);
    const newNode = {
      id: newNodeId,
      type: "customNode",
      data: {
        ...nodeDataRef.current,
        ...data,
      },
      position: {
        x: position.x,
        y: position.y,
      },
    };

    setNodes(prevList => {
      return [...prevList, newNode];
    });
    onNodesEdgesAdd([getSimpleNode(newNode)], []);
    logEvent("create_node");

    // 有 todotip 的节点移动视口到合适的位置
    if (data.isEnableTooltipNode) {
      setTimeout(() => {
        // 居中节点，但是需要减掉 todoTip 的高度
        focusLastNode({ height: -160 });
      }, 200);
    } else {
      // 聚焦节点
      setTimeout(() => {
        focusLastNode();
      }, 200);
    }

    return {
      nodeId: newNode.id,
    };
  }

  const handleClickUpload = event => {
    logEvent("create_file_upload");
    clickAddNodeBtn(event, {
      displayType: "uploadFile",
    });
  };

  const generateNewNode = (e, displayType) => {
    const { x, y, zoom } = getViewport();

    const newOriginX = (e.clientX - x) / zoom;
    const newOriginY = (e.clientY - y) / zoom;
    const newNode = {
      id: `editor-${nodeIndexRef.current++}`,
      type: "customNode",
      data: {
        ...nodeDataRef.current,
        displayType,
      },
      position: {
        x: newOriginX,
        y: newOriginY,
      },
    };

    setNodes(prevList => {
      return [...prevList, newNode];
    });
    onNodesEdgesAdd([getSimpleNode(newNode)], []);
  };

  const clickAddNodeAgent = e => {
    generateNewNode(e, "Agent");
  };

  const handleClickCreateGenUINode = e => {
    generateNewNode(e, "genUI");
  };

  function getStartEndNodesContentArr(prevNodes, currNodes) {
    currNodes =
      currNodes ||
      nodesRef.current.map(x => ({
        id: x.id,
        textAreaValue: x.data.textAreaValue,
        displayType: x.data.displayType,
      }));
    return prevNodes
      .map(x => {
        const currNode = currNodes.find(y => y.id === x.id);
        if (!func.isEmpty(currNode)) {
          return {
            startNodeContent: x,
            endNodeContent: currNode,
          };
        }
        return {};
      })
      .filter(x => !func.isEmpty(x));
  }
  // 通过 id 查找目标 node id
  const getTargetNodeId = id => {
    return edges.find(x => !func.isEmpty(x.data?.lineParam?.enterText) && x.source === id)?.target;
  };
  const multipleInputsEdges = sourceNodes => {
    let targetIdArr = [];
    sourceNodes.map(x => {
      const targetId = getTargetNodeId(x.id);
      // 过滤出目标相同的 input 只取第一个
      if (!func.isEmpty(targetId) && !targetIdArr.some(y => y.targetId === targetId)) {
        return targetIdArr.push({
          sourceId: x.id,
          targetId,
        });
      }
    });
    return sourceNodes.filter(x => targetIdArr.some(y => y.sourceId === x.id));
  };
  const clickRunAllBtn = async (sourceNodeArr, isFromPreview = false) => {
    prevRunAllNodesContent.current = nodesRef.current.map(x => ({
      id: x.id,
      textAreaValue: x.data.textAreaValue,
      displayType: x.data.displayType,
    }));
    //使用corekit的run
    if (isNewEdgeRunRef.current) {
      /// 检查有input为空的话，不run并提示
      const emptyInput = ProjectUtil.emptyInput(
        store.getState().studio.selectedTemplate,
        getHomeEdges(),
        store.getState().fot.variableList,
        getSaveDataV3
      );
      const emptyAddDataSource = ProjectUtil.emptyAddDataSourceParams(getHomeNodes(), saasUIData?.input, isFromPreview);
      if (emptyAddDataSource) {
        func.customMsg({
          content: "Need Add Data Source",
          type: "warning",
        });
        return;
      }
      if (emptyInput && !isFromPreview) {
        func.customMsg({
          content: "Need input value",
          type: "warning",
        });
        return;
      }

      const customNewEdgeArr = nodesRef.current.filter(x => x.type === "customNewEdge").map(x => x.id);
      setNeedDisableEdgeIds(customNewEdgeArr);
      setRunAllLoading(true);
      try {
        await corekitRun({
          getSimpleNodeData,
          isFromPreview,
          isRunAll: true,
        });
        // run all完的时候重置group折叠状态
        homeStore.cleanGroupCOEData();
        this.edgeData.setNodes(l => {
          return l.map(x => {
            if (x.type === "customNode") {
              return {
                ...x,
                data: {
                  ...x.data,
                  showGroupCollapseData: false,
                },
              };
            }
            return x;
          });
        });
        firstRunAllAddNodeToPreview(customNewEdgeArr[0]);
        onNodeContentChange(getStartEndNodesContentArr(prevRunAllNodesContent.current));
      } catch (error) {
      } finally {
        setRunAllLoading(false);
        showNewOnboardingFlowTips(getHomeNodes);
      }
      const eventProperties = {
        source_nodes: sourceNodeArr,
      };
      logEvent("run_all", eventProperties);
      return;
    }
    setRunAllLoading(true);
    // 过滤掉由切片创建的边
    // const filterEdges = edgeArr || edges
    // const filterEdges = (edgeArr || edges).filter(x => !x.data.isCreatedBySlice)
    let sourceNodes = nodesRef.current.filter(x => !edges.some(y => y.target === x.id) && func.isEmpty(x.parentNode));
    sourceNodes = multipleInputsEdges(sourceNodes);

    await handleApiCall(sourceNodeArr || sourceNodes);
    // 清空 disable 选中数据
    setAlreadyRunArr([]);
    setNeedDisableEdgeIds([]);
    onNodeContentChange(getStartEndNodesContentArr(prevRunAllNodesContent.current));
    setRunAllLoading(false);
    const eventProperties = {
      source_nodes: sourceNodeArr || sourceNodes,
    };
    logEvent("run_all", eventProperties);
  };

  const handleHiddenNode = thoughtArr => {
    let arr = [];

    thoughtArr.forEach(x => {
      const noDelArr = x.results.filter(r => {
        // 如果有报错，那么也需要保留并显示出来。
        if (!func.isEmpty(r?.errorText)) return r;
        switch (r.type) {
          case "stock":
            return !func.isEmpty(r?.content?.symbol) && !func.isEmpty(r?.content?.value);
          case "code":
            return !func.isEmpty(r?.content?.code?.[0]?.codex_generation);
          case "chatBox":
            return r;
          default:
            return !func.isEmpty(r.content);
        }
      });
      if (!func.isEmpty(noDelArr)) {
        arr.push({
          ...x,
          results: noDelArr,
        });
      }
    });
    return arr;
  };

  const { props: aiSaasProps } = useAiSaasStore({
    clickRunAllBtn,
    handleHiddenNode,
  });

  // 实现 runall 禁用
  function runAllDisable() {
    const edges = getHomeEdges();
    const nextEdges = edges.filter(x => !alreadyRunRef.current.includes(x?.id));
    const nextEdgeIdsArr = nextEdges.map(x => x.id);
    setNeedDisableEdgeIds(nextEdgeIdsArr);
  }

  function addToPreviewList(format) {
    // 传入进来的 preview panel 选择项
    if (format) {
      format && addPreview({ format });
    }
  }

  function autoSelectTemplate(byUnpublish = false, autoAddInput = true) {
    /// 手动构造选中 UI 模板数据
    const arr = [
      {
        label: "AI App",
        value: "saas",
        isStaticApp: false,
      },
    ];

    setIsPublish(false);
    setShowPreviewPanel(true);
    setCheckedUIList(arr);
    setSelectedUI(arr[0].value);

    if (byUnpublish) {
      // setThoughts([]); bsf-4912 已删除,使用下列 setSaasUIData
      setInputId([]);
      // stud-1735 default saasvalue
      setSaasUIData(PreviewAppUtil.genDefaultUISaasValue());
    }

    if (!autoAddInput) return;

    if (arr.some(x => x.value === "saasV2")) {
      autoLoadInputField("saasV2");
    }

    if (arr.some(x => x.value === "saas")) {
      autoLoadInputField("saas");
    }
  }

  function emptyNodes() {
    setNodes([]);
    setEdges([]);
  }

  function emptyPreviewApp(byUnpublish = false) {
    setCheckedUIList([]);
    setShowSaasOutputList(true);
    // setThoughts([]); bsf-4912 已删除,使用下列 setSaasUIData
    // bsf-4912 setDisplayThought
    setDisplayThought([]);
    setInputId([]);
    setSelectedUI("");
    // stud-1735 default saasvalue
    console.log("emptyPreviewApp");
    setSaasUIData(PreviewAppUtil.genDefaultUISaasValue());
    setSaasV2UIData({
      appName: "",
      appTitle: "",
      appUseCase1: "",
      appUseCase2: "",
      appUseCase3: "",
      newPreviewCase: "",
      appTagline: "",
      inputSuggestion: "",
      placeholder: "Enter something",
      input: [],
      output: [],
    });
    setThinkingAppUIData({
      title: "",
      subTitle: "",
      input: [],
      output: [],
    });

    if (byUnpublish) return;
    // 清空 v2 数据
    const data = Object.assign({}, selectedTemplate, { v2: {} });
    setSelectedTemplate(data);
  }

  const addThink = async thinkValue => {
    if (func.isEmpty(thinkValue)) {
      func.customMsg({
        content: "Data cannot be empty.",
        type: "warning",
      });
      return;
    }
    emptyNodes();
    emptyPreviewApp();
    if (feature_tags.showFeatureCot) {
      try {
        setThinkLoading(true);
        /// STUD-1975 使用corekit里的cot解析方法
        const { inputs, previewData, extraSaasUIData, flowGroup } = await cotFlow.getTemplate(
          thinkValue,
          nodeDataRef.current,
          newEdgeDataRef.current,
          newLineDataRef.current
        );
        const saasInputNodeIds = inputs.map(node => node.id);
        UIConfigRef.current.saas.UIData.current.fromCotTitle = extraSaasUIData.title;
        UIConfigRef.current.saas.UIData.current.fromCotSubtitle = extraSaasUIData.subTitle;
        setTimeout(() => {
          COTInputOutputNodes.current = previewData;
          setNodes(inputs);
          setSaasEdgesStaging(flowGroup);
          setThinkLoading(false);
          if (typeof thinkValue === "string") {
            cotDescription.current = thinkValue;
          } else {
            const item = cotInputBloc.defaultOperationList.value.find(li => li.value === thinkValue?.option) || {};
            cotDescription.current = item?.label || "";
          }

          if (func.isEmpty(saasInputNodeIds)) {
            // util.messageUtil('Empty node');
            logEvent("empty_cot_result", previewData.templateValue);
            setShowUnsupported(true);
            return;
          }
          setTimeout(() => {
            setCotStore({
              creating: true,
              inputIds: saasInputNodeIds,
              currentInputId: saasInputNodeIds[0],
            });
            // bsf-1919 重置状态
            setCotAutomaticNode(true);

            // link: https://github.com/brain/micro-apps/pull/1761 去掉 tooltip
            // setEnableTooltipWhatTodo(true)
            // setShowTooltip('editor-0')

            console.log("showAllInputs :>> ");

            showAllInputs(saasInputNodeIds, inputs);
          }, 300);
        });
      } catch (error) {
        setThinkLoading(false);
      }
    }
  };

  const setTargetNodeShowFeedback = async targetNodeId => {
    setNodes(prevList => {
      return prevList.map(l => {
        if (l.id === targetNodeId) {
          return {
            ...l,
            data: {
              ...l.data,
              showFeedback: true,
            },
          };
        }
        return l;
      });
    });
  };
  const setEdgeValueByWriter = async edge => {
    const id = edge.id;
    const fullText = edge.data.lineParam.enterText;
    const write = async i => {
      await sleep(20);
      const txt = fullText.slice(0, i);
      setEdges(prevList => {
        return prevList.map(l => {
          if (l.id === id) {
            return {
              ...l,
              data: {
                ...l.data,
                lineParam: {
                  ...l.data.lineParam,
                  enterText: txt,
                },
              },
            };
          }
          return l;
        });
      });
      if (i < fullText.length) {
        await write(i + 2);
      }
    };
    await write(1);
  };
  const setIdentifier = (id, identifier) => {
    setEdges(prevList => {
      return prevList.map(l => {
        if (l.id === id) {
          return {
            ...l,
            data: {
              ...l.data,
              lineParam: {
                ...l.data.lineParam,
                identifier,
              },
            },
          };
        }
        return l;
      });
    });
  };
  const showEdges = async (edges, targetNode) => {
    const edge = edges[0];
    if (targetNode.data && !func.isEmpty(targetNode.data.identifier)) {
      // 后台接口返回了identifier
      const { identifier_type: type, identifier_value: value } = targetNode.data.identifier;
      setIdentifier(edge.id, { type, value });
      setQueryLoading(edge.id, "isGetQuery", true);
    } else if (selectApiReg.test(edge.data.lineParam.enterText) === false) {
      // function
      const { identifier } = await getAndSetQuery(edge.id, edge.data.lineParam.enterText);
      setIdentifier(edge.id, identifier);
    } else {
      // 普通边
      setQueryLoading(edge.id, "isGetQuery", true);
    }
    await setEdgeValueByWriter(edge);
  };
  const closeUnsupported = () => {
    setShowUnsupported(false);
    setShowCot(true);
    clickClosePreview();
    setEnableCOTStartAgain(null);
    // logEvent("click_cot_ai_icon");
  };
  const defaultOpenWebApp = () => {
    // Cot 完成加载 默认打开 Web App
    // Cot Input 打开状态，不自动展开 Preview app
    if (showCotRef.current) return;
    const webData = [
      {
        label: "AI App",
        value: "saas",
        isStaticApp: false,
      },
    ];

    setIsPublish(false);
    setShowPreviewPanel(true);
    setCheckedUIList(webData);
    setSelectedUI(webData[0].value);
  };
  // 点击 AI 图标，关闭已有弹出层
  const clickClosePreview = () => {
    setIsPublish(false);
    setShowPreviewPanel(false);
  };

  const loadingForRendering = async () => {
    const end = () => {
      dispatch(editorActions.setProcessingCOT(false));
      if (feature_tags.showCotUserFeedback) {
        takeProjectSnapshot(); // when the graph is generated, take a snapshot for internal COT evaluation
      }
      setCotAutomaticNode(false);
      const sourceNode = nodesRef.current.find(x => x.id === cotStore.inputIds[0]);
      setTimeout(() => {
        clickZoomFitIcon();
      }, 800);
      setTimeout(async () => {
        // await clickRunAllBtn([sourceNode]);
        if (isEmpty(COTInputOutputNodes.current) === false) {
          defaultOpenWebApp();
          const platforms = ["saas", "saasV2"];

          // Empty input and output first
          platforms.forEach(platform => {
            const currPreviewUI = UIConfigRef.current[platform];
            currPreviewUI.setUIDataFunc(prevData => {
              return {
                ...prevData,
                output: [],
                input: [],
              };
            });
          });

          const { input, output, templateValue } = COTInputOutputNodes.current;
          const uniqNodes = uniqBy(nodesRef.current, node => node.id);
          const inputNodes = uniqNodes.filter(node => input.map(item => `editor-${item}`).includes(node.id));
          const outPutNodes = uniqNodes.filter(node => output.map(item => `editor-${item}`).includes(node.id));
          const outPutGroupNodes = uniqNodes.filter(node => output.map(item => `group-${item}`).includes(node.id));

          const inputIds = store.getState().fot.inputId;
          const addInputs = inputNodes.map(x => x.id);
          const newInputId = [...inputIds, ...addInputs];
          setInputId(newInputId);

          platforms.forEach(platform => {
            const needAddInput = inputNodes.filter(node =>
              (UIConfigRef?.current?.[platform]?.UIData?.current?.input || []).every(input => input.id !== node.id)
            );
            needAddInput.forEach(input => {
              platform === "saasV2"
                ? handleSaasV2InputField(input, platform, cotDescription.current)
                : handleInputField(input, platform, cotDescription.current);
            });
            outPutNodes.forEach(node => addIndividual(node, platform, true));
            outPutGroupNodes.forEach(node => clickGroupToPreview(node, platform, true));
          });
          openPreviewPage();
          addPreview({ format: getDefaultPreviewAppTypeList()[0] });
          setEnableCOTFeedbackPopup(true);
        }
      }, 300);
    };

    /**
     * TODO: 流程中的 `setCetner` 与节点和边的聚焦出现重复行为，需要优化
     * @see `useFocusNode`
     */
    const showNode = async i => {
      let timer = setTimeout(async () => {
        if (i > saasEdgesStaging.length - 1) {
          clearTimeout(timer);
          end();
        }
        const current = saasEdgesStaging[i];

        // 退出当前项目停止流程
        if (!cotAutomaticNodeRef.current) return;
        if (current) {
          if (isNewEdgeRunRef.current) {
            // v3
            setEdges(prevList => [...prevList, ...current.edges]);
            setNodes(prevList => [...prevList, current.node, current.edgeNode]);
            HomeUsePluginUtil.hanleNodeOverlapping({
              setNode: setNodes,
              allNodes: getHomeNodes().filter(x => !NEW_EDGE_REG.test(x.id)),
              currentNode: current.node,
            });
            await showFlowByGroupV3(current);
            //运行后id有可能改变了, 重新获取最后一个node作为当前node
            const mainNode = getHomeNodes().filter(node => !NEW_EDGE_REG.test(node.id) && isBlank(node.parentNode));
            const currentNodeNew = mainNode.last();
            HomeUsePluginUtil.hanleNodeOverlapping({
              setNode: setNodes,
              allNodes: getHomeNodes().filter(x => !NEW_EDGE_REG.test(x.id)),
              currentNode: currentNodeNew,
            });
            await showNode(i + 1);
          } else {
            // v2
            setEdges(prevList => [...prevList, ...current.edges]);
            setNodes(prevList => [...prevList, current.node]);

            setCenter(current.node.position.x - 200, current.node.position.y, { duration: 300, zoom: 1.2 });
            await showEdges(current.edges, current.node);
            await sleep(800);
            setCenter(current.node.position.x, current.node.position.y, { duration: 300, zoom: 1.2 });
            await runSingleEdge(current.edges[0].id);
            await sleep(800);
            await showNode(i + 1);
          }
        }
      }, 1000);
    };

    setCotAutomaticNode(true);
    await showNode(0);
    clickZoomFitIcon();
  };

  const postImagicaStudioApi = obj => {
    if (userMe?.id && !isSubscriber && isLoadPreferences.current) {
      const data = { key: "imagica_studio_tutorial", value: JSON.stringify(obj) };
      api.postWithError(`/api/users/me/preferences.json`, data);
    }
  };

  async function getEdgeGeneration(aiValue, id) {
    // const query = `The system generates a system description based on the Description of the AI.
    //   ###
    //   Description of AI: AI that generates HTML and inline CSS code based on a description of a UI.
    //   System  Description: You are an AI that generates HTML and inline CSS code based on a given description of a UI.
    //   ###
    //   Description of AI: ${aiValue}
    //   System Description:`
    const postParam = await getCompletionsGenerationParam(
      "file",
      "brain_studios_edge_prompt_generation.model",
      {
        input: aiValue,
      },
      false,
      "brain_studios"
    );
    // todo: 这里可能还要处理新的边的类型的情况
    // cot额外需要传递变量

    // isNewEdgeRunRef.current v3
    const lineParam = isNewEdgeRunRef.current
      ? nodesRef.current.find(x => x.id === id)?.data.lineParam
      : edgesRef.current.find(x => x.id === id)?.data.lineParam;
    // v2才传递边上的变量
    if (isNewEdgeRunRef.current === false) {
      if (lineParam && !isEmpty(lineParam.varilableNames)) {
        postParam.extra_inputs = arrayKeyToObject(lineParam.varilableNames || []);
      }
    }

    // 多连线不创建变量 直接获取sourceNode的title
    if (isNewEdgeRunRef.current) {
      const edgeNode = nodesRef.current.find(x => x.id === id);
      const sourceIdArr = edgeNode.data.flows.map(x => x.sourceNodeId);
      if (sourceIdArr.length > 1) {
        // multiple input
        const sourceLabels = nodesRef.current
          .filter(x => sourceIdArr.includes(x.id))
          .map(x => x.data.inputTitle || x.data.generatedLabel || "Input");

        postParam.extra_inputs = Object.assign(postParam.extra_inputs || {}, arrayKeyToObject(sourceLabels));
      }
    }

    // const postParam = {
    //   identifier_type: "file",
    //   identifier_value: "brain_studios_edge_prompt_generation.model",
    //   variables: {
    //     input: aiValue
    //   }
    // }

    const res = await postData("/be/bas-demo-v4/nlp/prompt_generation", postParam, false);
    if (res.status !== 200 || !res?.data?.identifier) {
      func.messageUtil("Failed to create AI");
      console.error("Failed to generate prompt", res.statusCode);
      return;
    }
    const resultQuery = res?.data?.identifier;
    return resultQuery;
  }

  /** 保存第二个input node id*/
  const saveSecondInputNodeId = nodeId => {
    const onlyOneInputNode = getInputNodesForNodeLabel(getNodes(), getEdges()).length === 1;
    const secondInputId = store.getState().editor.secondInputNodeId;
    if (onlyOneInputNode && isNewEdgeRunRef.current && isEmpty(secondInputId)) {
      setSecondInputNodeId(nodeId);
    }
  };

  const addNewNode = e => {
    // cot状态下不允许添加node
    if (feature_tags.showFeatureCot && (showCot || cotStore.creating === true)) return;
    clickAddNodeBtn(e);
  };

  const setNodeAndLine = resultQuery => {
    let newNodes = [];
    nodeIndexRef.current++;
    const oneNode = {
      id: `editor-${nodeIndexRef.current}`,
      type: "customNode",
      data: {
        ...nodeDataRef.current,
      },
      position: {
        x: 0,
        y: 0,
      },
    };

    setNodes(prevList => {
      const lowNode = getLowPotionY(prevList);
      oneNode.position.x = lowNode.position.x;
      oneNode.position.y = lowNode.position.y - 320;
      newNodes = [...prevList, oneNode];
      return newNodes;
    });
    nodeIndexRef.current++;
    const twoNode = {
      id: `editor-${nodeIndexRef.current}`,
      type: "customNode",
      data: {
        ...nodeDataRef.current,
      },
      position: {
        x: 0,
        y: 0,
      },
    };

    setNodes(prevList => {
      const lowNode = getLowPotionY(prevList);
      twoNode.position.x = lowNode.position.x + 420;
      twoNode.position.y = lowNode.position.y;
      newNodes = [...prevList, twoNode];
      return newNodes;
    });

    // 新增连线
    let lineParam = {
      ...edgeDataRef.current.lineParam,
      queryValue: resultQuery,
      enterText: thinkValue,
    };
    if (resultQuery.type) {
      lineParam = {
        lineType: "identifier",
        identifier: resultQuery,
        enterText: thinkValue,
      };
    }

    const newEdge = {
      id: `edge-${edgeIndexRef.current++}`,
      type: "customEdge",
      animated: true,
      source: oneNode.id,
      target: twoNode.id,
      markerEnd,
      data: {
        ...edgeDataRef.current,
        isGetQuery: true,
        lineParam,
      },
    };
    setEdges(prevList => [...prevList, newEdge]);

    return {
      nodes: [getSimpleNode(oneNode), getSimpleNode(twoNode)],
      edges: [getSimpleEdge(newEdge)],
    };
  };

  const getLowPotionY = prevList => {
    // FIXME: 当节点不存，返回一个只带坐标点的node
    if (isEmpty(prevList)) {
      return { position: getCenterPositionByElement() };
    }

    const lowY = Math.min.apply(
      Math,
      prevList.map(item => item.position.y)
    );
    const lowNode = prevList.find(item => lowY === item.position.y);
    return lowNode;
  };

  const judgePlaceHolderDisplay = (newNodes, newEdges) => {
    if (newNodes.length <= 0) return;
    const sourceNodes = [];
    const targetNodes = [];
    const sourceTargetNodes = [];
    const noSourceTargetNodes = [];

    newNodes.forEach(x => {
      let isSourceNode = newEdges.some(y => y.source === x.id);
      let isTargetNode = newEdges.some(y => y.target === x.id);
      if (x.parentNode) {
        isSourceNode = false;
        isTargetNode = true;
      }
      if (isSourceNode && !isTargetNode) {
        sourceNodes.push(x);
      } else if (!isSourceNode && isTargetNode) {
        targetNodes.push(x);
      } else if (isSourceNode && isTargetNode) {
        sourceTargetNodes.push(x);
      } else if (!isSourceNode && !isTargetNode) {
        noSourceTargetNodes.push(x);
      }
    });
    // 修改placeholder
    setNodes(prevList => {
      return prevList.map(l => {
        const isSourceNodes = sourceNodes.some(x => x.id === l.id);
        const isTargetNodes = targetNodes.some(x => x.id === l.id);
        const isSourceTargetNodes = sourceTargetNodes.some(x => x.id === l.id);
        const isNonSourceTargetNodes = noSourceTargetNodes.some(x => x.id === l.id);
        // cot过来的input需要显示自己的placeholder
        if (isSourceNodes || isNonSourceTargetNodes) {
          return {
            ...l,
            data: {
              ...l.data,
              placeholder: l.data.placeholder || "Enter input",
            },
          };
        }
        if (isTargetNodes) {
          return {
            ...l,
            data: {
              ...l.data,
              placeholder: "Output shown here",
            },
          };
        }
        if (isSourceTargetNodes) {
          return {
            ...l,
            data: {
              ...l.data,
              placeholder: "Output used as input to the right ",
            },
          };
        }
        return l;
      });
    });
  };
  async function postAIDescription(query, identifier, variables = {}, abort, isNeedHandelRes = true, edgeParam = {}) {
    try {
      let url = "/be/bas-demo-v4/nlp/brain_openai";
      let postParam = {
        engine: "text-davinci-003",
        frequency_penalty: 0,
        max_tokens: 500,
        presence_penalty: 0,
        stop: [],
        prompt: query,
      };
      if (identifier) {
        postParam = await getCompletionsGenerationParam("file", identifier, variables, false);
        url = "/be/bas-demo-v4/nlp/completions_generation";
      }
      const { source = "", target = "", id = "" } = edgeParam;
      const res = await postData(url, postParam, abort, postInstance => {
        setNodeParam({
          id: target,
          data: {
            abortNode: {
              source,
              target,
              edgeId: id,
              apiInstance: postInstance,
            },
          },
        });
      });
      if (!isNeedHandelRes) {
        return res;
      }
      const text = res?.data?.choices?.[0]?.text || res?.data?.choices?.[0]?.message?.content || "";
      const keyValues = text.split("\n").filter(x => !func.isEmpty(x));
      const results = keyValues.map(x => {
        const arr = x.split(": ");
        return {
          title: arr?.[0] || "",
          content: arr?.[1] || "",
        };
      });
      return results;
    } catch (error) {
      return Promise.reject(error);
    }
  }
  // 点击小块
  async function clickCustomeNode(nodeId) {
    let errorObj = null;
    try {
      if (func.isEmpty(edgesRef.current) || isNewEdgeRunRef.current) return;
      // selectedNodeIdRef.current = nodeId
      setIdeaLoading(true);
      let queryValue = "";
      // 查找nodeid作为target的那条边
      const targetEdge = edgesRef.current.find(x => x.target === nodeId);
      if (!func.isEmpty(targetEdge)) {
        queryValue = targetEdge.data.lineParam.queryValue;
      } else {
        // target没找到，则查找nodeid作为source的边（可能有多个，取最左侧的那条）
        const sourceEdges = edgesRef.current.filter(x => x.source === nodeId);
        if (func.isEmpty(sourceEdges)) return;
        const targetNodeIds = sourceEdges.map(x => x.target);
        const targetNodes = nodesRef.current.filter(x => targetNodeIds.includes(x.id));
        targetNodes.sort((a, b) => a.position.x - b.position.x);
        const leftNodeId = targetNodes[0]?.id;
        const leftSourceEdge = sourceEdges.find(x => x.target === leftNodeId);
        queryValue = leftSourceEdge?.data?.lineParam?.queryValue || "";
      }
      if (func.isEmpty(queryValue)) return;
      // 从query中提取AI描述
      const reg = /You are(.*)\n?Input:/gi;
      const extractDescription = reg.exec(queryValue)?.[1] || "";
      if (func.isEmpty(extractDescription)) return;
      // 有缓存则走缓存
      const existedIdeaArr = ideaDescriptionRef.current.get(extractDescription);
      if (!func.isEmpty(existedIdeaArr)) {
        extractDescriptionRef.current = extractDescription;
        setIdeaList(existedIdeaArr);
        return;
      }
      setShowIdeaList(true);
      // 获取idea列表
      //       const query = `The system generates a list of possible applications with explanations of why the market needs something like that based on a description of an AI.
      // Query: ${extractDescription}
      // Output:`
      const resArr = await postAIDescription(null, "brain_studios_application_ideas_generation.model", {
        input: extractDescription,
      });
      // 保存提取的ai描述，列表点击请求时需要
      extractDescriptionRef.current = extractDescription;
      // 缓存
      ideaDescriptionRef.current.set(extractDescription, [resArr]);
      setIdeaList([resArr]);
    } catch (error) {
      console.error("clickCustomeNode", error);
      errorObj = error;
    } finally {
      if (errorObj?.name !== "AbortError") {
        setIdeaLoading(false);
      }
    }
  }
  function clickGroupAddBtn(direction, otherProps, setCenter, getZoom, displayType) {
    let Interval = 200;
    let widthDistance = 200;
    if (displayType === "text") {
      Interval = 250;
      widthDistance = 250;
    }
    const offsetDistance = 370 + otherProps.width;

    let newAddEdge = {};
    if (isNewEdgeRunRef.current) {
      newAddEdge = createNewEdge(otherProps);
    }

    const recentNodes = getNodes();
    const currGroupNodes = recentNodes.filter(x => x.parentNode === otherProps.id);
    const newNodes = [];
    const childNode = [];
    const groupId = `group-${nodeIndexRef.current++}`;
    const needMoveNode = getNodes().filter(x => !NEW_EDGE_REG.test(x.id));

    sliceLevelRef.current++;
    currGroupNodes.forEach((x, index) => {
      const newNodeId = `editor-${nodeIndexRef.current++}`;
      const newNode = {
        id: newNodeId,
        type: "customNode",
        parentNode: groupId,
        extent: "parent",
        data: {
          ...nodeDataRef.current,
          sliceLevel: sliceLevelRef.current,
          isSliceNode: true,
          parentNodeId: groupId,
          displayType: displayType,
        },
        position: {
          x: distance,
          y: distance * (index + 1) + index * Interval,
        },
      };
      newNodes.push(newNode);
      childNode.push(newNodeId);
    });
    // 新增group
    const groupHeight =
      currGroupNodes.length * Interval + ((currGroupNodes.length + 1) * distance) / 2 + previewHeightRef.current;
    const getCenterY = otherProps.yPos + otherProps.offsetHeight / 2;

    //获取最下方或者最右方的node位置
    const adjacentNodepPosition = utilIndex.getBottomOrRightPosition(
      needMoveNode,
      direction,
      offsetDistance,
      { x: otherProps.xPos, y: otherProps.yPos },
      widthDistance + 2 * distance,
      groupHeight
    );

    const newGroupNode = {
      id: groupId,
      type: "customGroup",
      markerEnd,
      data: {
        ...groupDataRef.current,
        childNode,
      },
      style: {
        width: widthDistance + 2 * distance,
        height: groupHeight,
      },
      position: {
        x: otherProps.xPos + 100,
        y: getCenterY - groupHeight / 2,
      },
    };
    //新node按照已有的输出node位置调整
    if (direction === "left" || direction === "right") {
      newGroupNode.position.y = adjacentNodepPosition;
    } else {
      newGroupNode.position.x = adjacentNodepPosition;
    }

    if (direction === "right") {
      newGroupNode.position.x += 350;
    } else if (direction === "left") {
      newGroupNode.position.x -= 350;
    }

    //使输入框居中，zoom不变
    const positionX = (otherProps.xPos + newGroupNode.position.x) / 2 + 100;
    const positionY = (otherProps.yPos + newGroupNode.position.y) / 2 + groupHeight / 2;
    setCenter(positionX, positionY, { duration: 300, zoom: getZoom() });

    if (isNewEdgeRunRef.current) {
      handleUpdateTarget({
        newNode: newGroupNode,
        edgeNode: newAddEdge,
      });
      setNodes(prevList => {
        return [...prevList, ...newNodes];
      });

      const updatedNewEdge = nodesRef.current.find(x => x.id === newAddEdge.id);
      const sourceLine = createNewLine({
        source: otherProps.id,
        target: newAddEdge.id,
        type: "source",
      });

      const targetLine = createNewLine({
        sourceNode: otherProps,
        source: newAddEdge.id,
        target: newGroupNode.id,
        type: "target",
      });

      /// 移动节点
      translateNode(newGroupNode, direction, offsetDistance - 350, 30);
      const newSimpleNodes = newNodes.map(getSimpleNode);
      onNodesEdgesAdd(
        [newGroupNode, getSimpleNodeV3(updatedNewEdge), ...newSimpleNodes],
        [getSimpleEdgeV3(sourceLine), getSimpleEdgeV3(targetLine)]
      );
      return;
    }

    newNodes.unshift(newGroupNode);
    setNodes(prevList => {
      return [...prevList, ...newNodes];
    });

    const newEdge = {
      id: `edge-${edgeIndexRef.current++}`,
      type: "customEdge",
      source: otherProps.id,
      target: groupId,
      animated: true,
      markerEnd,
      sourceHandle: DIRECTIONHANDLE[direction].source,
      targetHandle: DIRECTIONHANDLE[direction].target,
      data: {
        ...edgeDataRef.current,
        sourceHandle: DIRECTIONHANDLE[direction].source,
        targetHandle: DIRECTIONHANDLE[direction].target,
      },
    };
    setEdges(prevList => [...prevList, newEdge]);
    const newSimpleNodes = newNodes.map(getSimpleNode);
    onNodesEdgesAdd(newSimpleNodes, [getSimpleEdge(newEdge)]);
    // 移动节点
    // 620 - 350 = 270
    translateNode(newGroupNode, direction, offsetDistance - 350, 30);
  }
  const clickToShare = (e, url) => {
    e.stopPropagation();
    window.open(url, "_blank");
    logEvent("open_published_link", { URL: url });
  };
  function shardSplittingAll(param, text, type) {
    // 获取当前 node
    const currentNode = nodesRef.current.find(x => x.id === param.target);
    if (func.isEmpty(currentNode)) return;
    const isShowStockInterface = type === "showStockInterface";
    const splitArray = isShowStockInterface ? text : splitText("", text).filter(x => !func.isEmpty(x));
    if (func.isEmpty(splitArray)) return;
    sliceLevelRef.current = 1;
    const newNodes = [];
    const groupId = `group-${currentNode.id.replace(/[^\d]/g, "")}`;
    const isCurrGroupExisted = nodesRef.current.some(x => x.id === groupId);
    const childNode = [];
    let currentGroupChildNodes = [];
    if (currentNode.type === "customGroup") {
      currentGroupChildNodes = nodesRef.current.filter(x => x.parentNode === groupId);
    }
    // 新增node
    splitArray.forEach((x, index) => {
      const newNodeId = !func.isEmpty(currentGroupChildNodes[index])
        ? currentGroupChildNodes[index].id
        : `editor-${nodeIndexRef.current++}`;
      const newNode = {
        id: newNodeId,
        type: "customNode",
        parentNode: groupId,
        extent: "parent",
        data: {
          ...nodeDataRef.current,
          textAreaValue: isShowStockInterface ? x : x.trim(),
          displayType: isShowStockInterface ? "stockInfo" : nodeDataRef.current.displayType,
          sliceLevel: sliceLevelRef.current,
          isSliceNode: true,
          parentNodeId: groupId,
          childNode: [],
        },
        position: {
          x: distance,
          y: distance * (index + 1) + index * 250,
        },
      };
      childNode.push(newNodeId);
      newNodes.push(newNode);
    });
    // 新增group
    const groupHeight = splitArray.length * 250 + ((splitArray.length + 1) * distance) / 2 + previewHeightRef.current;
    const newGroupNode = {
      id: groupId,
      type: "customGroup",
      data: {
        ...groupDataRef.current,
        childNode,
      },
      style: {
        width: 250 + 2 * distance,
        height: groupHeight,
      },
      position: {
        x: isCurrGroupExisted ? currentNode.position.x : currentNode.position.x + 200,
        y: currentNode.position.y,
      },
    };

    // undo监听
    const newSimpleNodes = [newGroupNode, ...newNodes].map(getSimpleNode);
    // 只有来自于切分的param才包含edge所有数据
    const currentNewEdge = edgesRef.current.find(x => x.id === param.id) || {};
    const newEdge = Object.assign(
      {},
      {
        ...currentNewEdge,
        target: groupId,
        data: {
          ...(currentNewEdge?.data || {}),
          lineParam: {
            ...(currentNewEdge?.data?.lineParam || {}),
            loading: false,
          },
        },
      }
    );
    onNodesEdgesAdd(newSimpleNodes, [getSimpleEdge(newEdge)]);
    // 进入修改流程
    if (currentNode.type === "customGroup") {
      // 替换分组的边
      replaceGroupEdge(currentNode.data.childNode, newNodes);

      setNodes(prevNodes => {
        // 删除组旧的 node
        const parentNodeId = prevNodes.find(x => x.id === currentNode.data.childNode[0]).parentNode;
        const preserveNodes = prevNodes.filter(x => x?.parentNode !== parentNodeId);
        const prevList = preserveNodes.map(l => {
          if (l.id === currentNode.id) {
            return Object.assign({}, { ...l }, { ...newGroupNode });
          }
          return l;
        });
        // 需要删除旧的 node, 添加新拆分的 node
        return [...prevList, ...newNodes];
      });
      return;
    }
    // 改变其类型为分组
    setNodes(prevNodes => {
      const preserveNodes = prevNodes.filter(x => !currentNode.data.childNode.includes(x.id));
      const prevList = preserveNodes.map(l => {
        if (l.id === currentNode.id) {
          return Object.assign({}, { ...l }, { ...newGroupNode });
        }
        return l;
      });
      // 需要删除旧的 node 加入新拆分的 node
      return [...prevList, ...newNodes];
    });

    const allEdgeArr = edgesRef.current.filter(edge => edge.target === param.target) || [param];
    // 修改边的指向
    setEdges(prevList => {
      return prevList.map(l => {
        if (allEdgeArr.some(edge => edge.id === l.id)) {
          const newEdge = Object.assign({}, { ...l }, { target: groupId });
          // BSF-1578: https://brain-ai.atlassian.net/browse/BSF-1578
          // 组节点没有上和下的边，但是普通节点点击上和下的边 变成组节点时会丢失边
          return CustomEdgeUtil.assignNodeToGroupNodeHandle(newEdge);
        }
        return l;
      });
    });
    if (groupId === "group-2") {
      setShowTooltip(groupId);
      setCloseToolTipArr(["editor-1", "edge-0"]);
    }
  }
  function replaceGroupEdge(oldArr, newArr) {
    oldArr.forEach((x, index) => {
      const currSourceEdges = edgesRef.current.filter(y => y.source === x);
      if (func.isEmpty(currSourceEdges)) return;
      currSourceEdges.forEach(y => {
        setEdges(prevList => {
          return prevList.map(l => {
            if (l.id === y.id) {
              return Object.assign({}, { ...l }, { source: newArr[index].id });
            }
            return l;
          });
        });
      });
    });
  }

  async function clickSliceBtn(id) {
    //使用corekit的run
    if (isNewEdgeRunRef.current) {
      const sourceNode = nodesRef.current.find(x => x.id === id);
      const prevNode = {
        id,
        xPos: sourceNode.position.x,
        yPos: sourceNode.position.y,
      };
      const { newEdge, sourceLine, targetLine } = clickAddNewNode({ direction: "right", prevNode, isStickie: false });
      setNodes(prevList => {
        return prevList.map(x => {
          if (x.id === newEdge.id) {
            return {
              ...x,
              data: {
                ...x.data,
                isGetQuery: true,
                lineParam: {
                  ...x.data.lineParam,
                  enterText: "/Split Output",
                },
              },
            };
          }
          return x;
        });
      });
      const updateParam = {
        edgeId: newEdge.id,
        type: "lineParam",
        lineParam: {
          enterText: "/Split Output",
        },
      };
      const { transformData } = await corekitRun({ getSimpleNodeData, edgeId: newEdge.id, updateParam });

      // undo/redo监听处理
      const updatedNewEdge = nodesRef.current.find(x => x.id === newEdge.id);
      const groupNodes = nodesRef.current.filter(x => transformData.some(y => y.id === x.id)).map(getSimpleNode);
      const updatedLines = edgesRef.current
        .filter(x => x.id === sourceLine.id || x.id === targetLine.id)
        .map(getSimpleEdgeV3);
      onNodesEdgesAdd([...groupNodes, getSimpleNodeV3(updatedNewEdge)], updatedLines);
      firstRunAddNodeToPreview({ sourceNode, outputNode: groupNodes[0] });
      return;
    }
    const newNodeId = `editor-${nodeIndexRef.current++}`;
    const newEdgeId = `edge-${edgeIndexRef.current++}`;
    const offsetDistance = 600;

    try {
      let currentNode = nodesRef.current.find(x => x.id === id);
      if (func.isEmpty(currentNode)) return;
      const otherProps = {
        id,
        xPos: currentNode.position.x,
        yPos: currentNode.position.y,
      };
      const needMoveNode = getNodes();
      //获取最下方或者最右方的node位置
      const adjacentNodepPosition = utilIndex.getBottomOrRightPosition(
        needMoveNode,
        "right",
        offsetDistance,
        currentNode.position
      );

      // 创建 node
      const newNode = {
        id: newNodeId,
        type: "customNode",
        data: {
          ...nodeDataRef.current,
        },
        position: {
          x: currentNode.position.x + 350,
          y: currentNode.position.y,
        },
      };
      //新node按照已有的输出node位置调整
      newNode.position.y = adjacentNodepPosition;

      setNodes(prevList => [...prevList, newNode]);
      // 移动节点
      // 250 = 600 - 350
      translateNode(newNode, "right", 600 - 350, 30);

      // 创建边
      const newEdge = {
        id: newEdgeId,
        type: "customEdge",
        source: currentNode.id,
        target: newNodeId,
        animated: true,
        markerEnd,
        sourceHandle: "b",
        targetHandle: "a",
        data: {
          ...edgeDataRef.current,
          isGetQuery: true,
          lineParam: {
            ...edgeDataRef.current.lineParam,
            enterText: "/Split Output",
            loading: true,
          },
        },
      };
      setEdges(prevList => [...prevList, newEdge]);

      await getOtherTypeData(newEdge, "splitOutput", currentNode.data.textAreaValue);

      // setEdgeLineParam({
      //   id: newEdgeId,
      //   loading: false
      // })
    } catch (error) {
      setNodeErrorTip(newNodeId, error);
    } finally {
      setEdgeLineParam({
        id: newEdgeId,
        loading: false,
      });
    }
    // const newSimpleNodes = newNodes.map(x => {
    //   return getSimpleNode(x)
    // })
    // onNodesEdgesAdd(newSimpleNodes, [getSimpleEdge(newEdge)])

    // let array = splitText(currentNode).filter(x => !util.isEmpty(x))
    // if (util.isEmpty(array)) return;

    // sliceLevelRef.current = 1
    // const newNodes = []
    // const groupId = `group-${nodeIndexRef.current++}`
    // const childNode = []
    // 新增node
    // array.forEach((x, index) => {
    //   const newNodeId = `editor-${nodeIndexRef.current++}`
    //   const newNode = {
    //     id: newNodeId,
    //     type: 'customNode',
    //     parentNode: groupId,
    //     extent: 'parent',
    //     data: {
    //       ...currentNode.data,
    //       textAreaValue: x.trim(),
    //       sliceLevel: sliceLevelRef.current,
    //       isSliceNode: true,
    //       parentNodeId: groupId
    //     },
    //     position: {
    //       x: distance,
    //       y: (distance * (index + 1)) + index * 200
    //     },
    //   }
    //   childNode.push(newNodeId)
    //   newNodes.push(newNode)
    // })
    // // 新增group
    // const groupHeight = array.length * 200 + (array.length + 1) * distance
    // const newGroupNode = {
    //   id: groupId,
    //   type: 'customGroup',
    //   data: {
    //     ...groupDataRef.current,
    //     childNode
    //   },
    //   style: {
    //     width: 200 + 2 * distance,
    //     height: groupHeight
    //   },
    //   position: {
    //     x: currentNode.position.x + 400,
    //     y: currentNode.position.y - groupHeight / 2 + 100
    //   },
    // }
    // newNodes.unshift(newGroupNode)
    // setNodes(prevList => [...prevList, ...newNodes])

    // // 连线
    // const newEdgeId = `edge-${edgeIndexRef.current++}`
    // const newEdge = {
    //   id: newEdgeId,
    //   type: 'customEdge',
    //   source: currentNode.id,
    //   target: groupId,
    //   animated: true,
    //   markerEnd,
    //   data: {
    //     ...edgeDataRef.current,
    //     isCreatedBySlice: true
    //   }
    // }
    // setEdges(prevList => [...prevList, newEdge])

    // onNodesEdgesAdd(newNodes.map(x => x.id), [newEdgeId])
  }
  function getSourceOfTruthResults(query, edgeParam) {
    const requestParam = edgeParam?.lineParam?.requestParam || edgeParam;
    const data = Object.assign({}, requestParam, { query });
    return api.postWithError("/be/vectordb/indexers/", { data }).catch(error => Promise.reject(error));
  }

  async function getOtherTypeData(edgeParam, type, query, isCreatedFunction) {
    const meId = store.getState().editor.me?.id;
    const selectedTemplateId = store.getState().studio.selectedTemplate?.id;

    const { target, id, source } = edgeParam;

    const topic = `/studios/${meId}/wsuid_${
      selectedTemplateId ? selectedTemplateId + "_" : ""
    }${target}/midjourneyV2/${Date.now()}`;

    const getStockInfoData = symbol => {
      return api.getWithErrorCanAbort(`/be/svc-adapter/stock/symbol?text=${symbol}`);
    };
    const getImageApiData = text => {
      const url = `/be/lambda/function/stableai`;
      return api.postWithErrorCanAbort(url, { prompt: text, json: true });
    };
    const getImageSearchData = text => {
      const url = `/be/shopping/serpapi/search?engine=google_images&q=${text}`;
      return api.getWithErrorCanAbort(url);
    };
    const getMidjourneyData = text => {
      const url = `/be/lambda/function/stableai&from=midjourney`;
      return api.postWithErrorCanAbort(url, { prompt: text, json: true });
    };

    const getMidjourneyV2Data = text => {
      let fetchController;
      const callback = controller => {
        fetchController = controller;
      };
      let reject = () => {};
      let resolve = () => {};

      const prePromise = api
        .postWithErrorCanAbort(
          `/be/svc-adapter/midjourney/v2/imagine`,
          {
            topic,
            prompt: text,
          },
          undefined,
          callback
        )
        .catch(error => {
          setNodeErrorTip(target, error);
          messageCenter.off(topic, () => {});
          reject("");
        });

      const promise = new Promise((_resolve, _reject) => {
        reject = _reject;
        resolve = _resolve;
        if (topic) {
          messageCenter.on(topic, res => {
            const imageUrl = get(res, "imageUrls.0");
            if (imageUrl) {
              messageCenter.off(topic, () => {});
              _resolve(imageUrl);
            }
          });
        }
      });

      promise.cancel = () => {
        fetchController.abort();
        messageCenter.off(topic, () => {});
        reject("");
      };
      promise.resolve = imageUrl => {
        messageCenter.off(topic, () => {});
        resolve(imageUrl);
      };

      prePromise.then(res => {
        const messageId = get(res, "data.data.messageId");
        setNodeParam({
          id: target,
          data: {
            displayType: type,
            textAreaValue: { loading: true, messageId, meId },
          },
        });
      });

      return promise;
    };

    const getProteinData = text => {
      const url = `/be/svc-adapter/alphafold/search?text=${text}`;
      return api.getWithErrorCanAbort(url);
    };

    const getVideoData = text => {
      const url = `/be/svc-adapter/youtube/v2/search?keyword=${text}`;
      return api.getWithErrorCanAbort(url);
    };

    const getJdShoppingData = keyword => {
      const url = `/be/svc-adapter/jd/search?keyword=${keyword}`;
      return api.getWithErrorCanAbort(url);
    };

    const getAmazonShoppingData = keyword => {
      const url = `/be/svc-adapter/purchasing/shop-search?query=${keyword}`;
      return api.getWithErrorCanAbort(url);
    };

    const getWeeeShoppingData = keyword => {
      const url = `/be/svc-adapter/sayweee/search?keyword=${keyword}`;
      return api.getWithErrorCanAbort(url);
    };
    const getShardSplitting = (query, edgeParam) => {
      return postAIDescription(
        null,
        "brain_studios_xxx_shard_splitting.model",
        { input: query },
        false,
        false,
        edgeParam
      );
    };
    const getMapData = query => {
      const url = `/be/bas-demo-v4/nlp/completions_generation`;
      const data = {
        variables: {
          input: query,
        },
        identifier_type: "file",
        identifier_value: "json_list_converter.model",
        domain: "brain_studios",
      };
      return api.postWithErrorCanAbort(url, data);
    };

    const getFoodData = async query => {
      return brainClient.foodApi.getRestaurantList({ query, offset: 0 });
    };

    const chatInterface = () => {
      return Promise.resolve({ data: { data: "interface value" } });
    };

    const apiObj = {
      imageGen: getImageApiData,
      imageSearch: getImageSearchData,
      stockInfo: getStockInfoData, //兼容旧的Stock API可以运行
      showStockInterface: handleStockInterfaceFunc,
      midjourney: getMidjourneyData,
      midjourneyV2: getMidjourneyV2Data,
      protein: getProteinData,
      video: getVideoData,
      jdShopping: getJdShoppingData,
      amazonShopping: getAmazonShoppingData,
      weeeShopping: getWeeeShoppingData,
      SourceOfTruth: getSourceOfTruthResults,
      splitOutput: getShardSplitting,
      showFoodInterface: getFoodData,
      map: getMapData,
      askBrain: getAskBrainData,
      chatInterface: chatInterface,
    };
    const callApi = apiObj[type];

    if (typeof callApi !== "function") {
      // 执行自定义创建的func
      await handleCreatedFunctionRun(type, edgeParam, query);
      return;
    }

    if (type === "splitOutput") {
      const splitedResult = splitText(query);
      if (splitedResult.length > 1) {
        return new Promise(reslove => {
          shardSplittingAll(edgeParam, query);
          reslove();
        });
      }
    }

    if (type === "map") {
      setReloadMap(false);
    }

    if (IMAGE_TYPES.includes(type)) {
      setNodeParam({
        id: target,
        data: {
          displayType: type,
          textAreaValue: { loading: true },
        },
      });
    }

    if (STOCK_TYPES.includes(type)) {
      setNodeParam({
        id: target,
        data: {
          displayType: "stockInfo",
          stockLoading: true,
        },
      });
    }

    const defaultData = {
      showWarning: false,
      showStopGenerateBtn: false,
      abortNode: {},
    };
    const currentPromise = callApi(query, edgeParam, defaultData);
    // askBrain不需要重新保存promise cancel
    if (type !== "askBrain") {
      setNodeParam({
        id: target,
        data: {
          showStopGenerateBtn: true,
          abortNode: {
            source,
            target,
            edgeId: id,
            apiInstance: currentPromise,
          },
        },
      });
    }
    return currentPromise
      .then(async res => {
        let param = null;

        if (type === "splitOutput") {
          shardSplittingAll(edgeParam, res?.data?.choices?.[0]?.text);
          return;
        } else if (type === "imageGen" || type === "midjourney") {
          const s3Path = res.data.s3_path || "";
          param = {
            loading: false,
            value: `${settings.S3CDN}${encodeURI(s3Path)}`,
          };
        } else if (type === "midjourneyV2") {
          param = {
            loading: false,
            value: res,
          };
        } else if (type === "imageSearch") {
          // 加载列表中最近一张可用的图片
          param = await utilIndex.loadAvailableImg(res.data?.images_results || [], "original");
        } else if (type === "showStockInterface") {
          if (!edgeParam.lineParam?.isFromGroup) {
            shardSplittingAll(edgeParam, res.formatRes, type);
          }
          return res.formatRes;
        } else if (type === "stockInfo") {
          const result = res.data?.data?.data || {};
          param = {
            logo: result.logo,
            name: result.name,
            symbol: result.symbol,
            price: `$ ${result.price}`,
            volume: result.volume,
            businessSummary: result.business_summary,
          };
        } else if (type === "protein") {
          param = {
            type: type,
            value: res?.data?.data?.items?.[0]?.entryId || "",
          };
          setNodeParam({
            id: target,
            data: {
              ...defaultData,
              displayType: "customJsFunction",
              textAreaValue: param || "",
            },
          });
          return;
        }
        if (type === "video") {
          setNodeParam({
            id: target,
            data: {
              ...defaultData,
              displayType: type,
              textAreaValue: res.data.data,
            },
          });
          return;
        }
        if (type === "askBrain") {
          return;
        }
        if (type === "jdShopping") {
          setNodeParam({
            id: target,
            data: {
              ...defaultData,
              displayType: type,
              textAreaValue: res.data.data,
            },
          });
          return;
        }
        if (type === "amazonShopping") {
          setNodeParam({
            id: target,
            data: {
              ...defaultData,
              displayType: type,
              textAreaValue: res.data.data,
            },
          });

          return;
        }
        if (type === "weeeShopping") {
          setNodeParam({
            id: target,
            data: {
              ...defaultData,
              displayType: type,
              textAreaValue: res.data.data,
            },
          });
          return;
        }
        if (type === "chatInterface") {
          setNodeParam({
            id: target,
            data: {
              ...defaultData,
              displayType: type,
              textAreaValue: res.data.data,
            },
          });
          return;
        }
        if (type === "showFoodInterface") {
          setNodeParam({
            id: target,
            data: {
              displayType: type,
              textAreaValue: res,
            },
          });
          return;
        }
        if (type === "SourceOfTruth") {
          setNodeParam({
            id: target,
            data: {
              ...defaultData,
              displayType: "text",
              textAreaValue: res.data?.data?.response,
            },
          });
          return;
        }
        if (type === "map") {
          const results = get(res, "data.choices.[0].text", "");
          setNodeParam({
            id: target,
            data: {
              displayType: type,
              textAreaValue: results,
            },
          });
          setReloadMap(true);
          return;
        }
        if (!isCreatedFunction) {
          setNodeParam({
            id: target,
            data: {
              ...defaultData,
              displayType: type,
              textAreaValue: param,
            },
          });
        } else {
          return {
            ...defaultData,
            displayType: type,
            textAreaValue: param,
          };
        }
        return res;
      })
      .catch(error => {
        if (func.isEmpty(error.fromApiError)) {
          const errText = typeof error === "string" ? error : error.message;
          error = {
            specifyErrorText: errText || "Get content failed",
          };
        }
        if (IMAGE_TYPES.includes(type)) {
          setNodeParam({
            id: target,
            data: {
              textAreaValue: {
                loading: false,
              },
            },
          });
        }
        if (STOCK_TYPES.includes(type)) {
          setNodeParam({
            id: target,
            data: {
              displayType: "stockInfo",
              stockLoading: false,
            },
          });
        }
        return Promise.reject(error);
      })
      .finally(() => {
        setNodeParam({
          id: target,
          data: {
            ...defaultData,
          },
        });
      });
  }
  const getHtmlStr = (currentSingleFlow, currentSourceNodeValue) => {
    const variableList = store.getState().fot.variableList;
    let cssValue = currentSingleFlow?.codeData?.cssStr || "";
    let htmlValue = currentSingleFlow?.codeData?.htmlStr || "";
    let jsValue = currentSingleFlow?.codeData?.jsStr || "";

    // if(currentSourceNodeValue) {
    //   htmlValue = htmlValue.replaceAll('${currentSourceNodeValue}', currentSourceNodeValue)
    //   jsValue = jsValue.replaceAll('currentSourceNodeValue', JSON.stringify(currentSourceNodeValue))
    // }
    if (
      currentSourceNodeValue instanceof Object &&
      currentSourceNodeValue.hasOwnProperty("data") &&
      !func.isEmpty(currentSourceNodeValue.data.value)
    ) {
      currentSourceNodeValue = JSON.stringify(currentSourceNodeValue.data.value);
    }
    const brainAuthToken = authStore.state.brainToken;
    htmlValue = htmlValue.replaceAll(CUSTOM_FUNCTION_VARS_REG, match => {
      if (match === "${currentSourceNodeValue}") return currentSourceNodeValue;
      if (match === "${brainAuthToken}") return brainAuthToken;
    });
    jsValue = jsValue.replaceAll(CUSTOM_FUNCTION_VARS_JS_REG, match => {
      // if (match === 'currentSourceNodeValue') return `'${currentSourceNodeValue}'`
      if (match === "currentSourceNodeValue") return JSON.stringify(currentSourceNodeValue);
      if (match === "brainAuthToken") return `'${brainAuthToken}'`;
      return match;
    });
    variableList.map(x => {
      htmlValue = htmlValue.replaceAll("${" + `${x.name}` + "}", x?.node?.data?.textAreaValue);
      cssValue = cssValue.replaceAll("${" + `${x.name}` + "}", x?.node?.data?.textAreaValue);
      jsValue = jsValue.replaceAll(x.name, JSON.stringify(x?.node?.data?.textAreaValue));
      return x;
    });
    const lineFeed = htmlValue.match(/^<html>/) ? "" : "white-space: pre-wrap;";
    return `<html>
        <style>${cssValue}</style>
        <body style="${lineFeed}">${htmlValue}</body>
        <script>{${jsValue}}</script>
      </html>`;
  };

  const getVariableList = currentSourceNodeValue => {
    const brainAuthToken = authStore.state.brainToken;
    return [
      {
        name: "currentSourceNodeValue",
        node: {
          data: {
            textAreaValue: currentSourceNodeValue,
          },
        },
      },
      {
        name: "brainAuthToken",
        node: {
          data: {
            textAreaValue: brainAuthToken,
          },
        },
      },
      ...store.getState().fot.variableList,
    ];
  };

  const evaluateFinalBodyStr = (currentSourceNodeValue, bodyParam) => {
    if (func.isEmpty(bodyParam)) {
      return "";
    }
    // 注入currentSourceNodeValue
    const variableList = getVariableList(currentSourceNodeValue);

    // 替换body变量
    // bodyParam can either be a string or object, if JSON.parse(bodyParam) works then it's an object otherwise string
    let bodyStr = bodyParam;
    if (typeof bodyParam === "object") {
      bodyStr = JSON.stringify(bodyParam);
    }
    // trim is moved here as part of post processing
    bodyStr = bodyStr.trim();
    const varFormatReg = /\${\w+\}/g;
    variableList.forEach(y => {
      const varReg = new RegExp(`\\\$\\\{${y.name}\\\}`, "g");
      bodyStr = bodyStr && bodyStr.replace(varReg, y.node.data.textAreaValue);
    });
    // 将剩下的没有的变量全部替换为空字符串
    bodyStr =
      bodyStr &&
      bodyStr
        .replace(varFormatReg, "")
        .replace(/\n/g, "")
        .replace(/(\r\n|\n|\r)/g, "")
        .replace(/^"/, "")
        .replace(/"$/, "");
    return bodyStr;
  };

  function handleVarInject(currentSourceNodeValue, requestData) {
    let { url, method, headerList, bodyParam = {} } = requestData;
    // 注入currentSourceNodeValue
    // currentSourceNode value is not always string, stringify it if it's not
    if (typeof currentSourceNodeValue !== "string") {
      currentSourceNodeValue = JSON.stringify(currentSourceNodeValue) || "";
    }
    const variableList = getVariableList(currentSourceNodeValue);
    // 替换url
    const varFormatReg = /\${\w+\}/g;
    variableList.forEach(y => {
      const varReg = new RegExp(`\\\$\\\{${y.name}\\\}`, "g");
      url = url.replace(varReg, y.node.data.textAreaValue);
    });
    url = url.replace(varFormatReg, "");
    // replace body变量
    // finalized body string must be derived from this function, DO NOT MAKE CHANGES before discussing with @David
    const bodyStr = evaluateFinalBodyStr(currentSourceNodeValue, bodyParam);
    try {
      bodyParam = func.isEmpty(bodyStr) ? null : JSON.parse(bodyStr);
    } catch (error) {
      console.error(error);
      func.messageUtil("Incorrect JSON format in request body");
      return;
    }
    // 替换header变量
    headerList = headerList.map(x => {
      let { name, value, description } = x;
      variableList.forEach(y => {
        const varReg = new RegExp(`\\\$\\\{${y.name}\\\}`, "g");
        name = name?.replace(varReg, y.node.data.textAreaValue);
        value = value?.replace(varReg, y.node.data.textAreaValue);
        description = description.replace(varReg, y.node.data.textAreaValue);
      });
      // 将剩下的没有的变量全部替换为空字符串
      name = name?.replace(varFormatReg, "");
      value = value?.replace(varFormatReg, "");
      description = description.replace(varFormatReg, "");

      return {
        ...x,
        name,
        value,
        description,
      };
    });

    return {
      url,
      method,
      headerList,
      bodyParam,
    };
  }
  const sendRequest = async (currentSourceNodeValue, requestData, setLoadingFunc = () => {}) => {
    if (func.isEmpty(requestData.url)) return;
    const params = handleVarInject(currentSourceNodeValue, requestData);
    if (func.isEmpty(params)) return;
    const { url, method, headerList, bodyParam } = params;
    const headers = {};
    headerList.forEach(x => {
      headers[x.name] = x.value;
    });
    const start = Date.now();
    let result = null;
    try {
      setLoadingFunc(true);
      const res = await api.commonRequest(url, method.toUpperCase(), {
        headers,
        body: bodyParam,
      });
      if (!res.ok) {
        throw res;
      }
      if (store.getState().editor.createAPIFuncData.open) {
        result = res;
      } else {
        await res.json().then(r => {
          result = r;
        });
      }
    } catch (error) {
      if (!func.isEmpty(error.name)) {
        result = error.response;
      }
      console.error("send Request", error);
      return Promise.reject(error);
      // util.messageUtil(error.message || 'Failed to send request')
    } finally {
      setLoadingFunc(false);
      if (store.getState().editor.createAPIFuncData.open) {
        return await utilIndex.handleResponseStatusData(result, start);
      }
      return {
        data: { type: "json", value: result },
      };
    }
  };
  const customMethod = async (nodeText, customFunc, lineParams, takeLastDisplay, edgeParam) => {
    try {
      let lastRunNodeText = nodeText;
      if (customFunc?.type) {
        return methodType(customFunc, nodeText);
      }
      lastRunNodeText = await normalCall(lineParams, lastRunNodeText, takeLastDisplay, edgeParam);
      return lastRunNodeText;
    } catch (error) {
      return Promise.reject(error);
    }
  };
  const normalCall = async (lineParams, nodeText, takeLastDisplay, edgeParam, isRecursiveCall = false) => {
    const { source, target, id } = edgeParam;
    try {
      let postParam = {};
      let url = "/be/bas-demo-v4/nlp/brain_openai";
      if (lineParams.lineType === "identifier") {
        url = `/${takeLastDisplay ? "stream" : "be"}/bas-demo-v4/nlp/completions_generation`;
        postParam = await getCompletionsGenerationParam(
          "object_id",
          lineParams.identifier.value,
          {
            input: nodeText,
          },
          takeLastDisplay ? true : false
        );
      } else {
        postParam = getOpenAiParam({
          lineParams,
          prompt: lineParams.queryValue || "",
          nodeText,
        });
      }

      let temp = "";
      await postWithStreaming(
        url,
        postParam,
        msg => {
          temp += msg.choices?.[0]?.text || msg.choices?.[0]?.delta?.content || "";
          // takeLastDisplay，作为自定义func最后一项时才使用streaming输出
          // isRecursiveCall 是最外部的自定义func调用
          if (takeLastDisplay && !isRecursiveCall) {
            setNodes(prevNodes => {
              return prevNodes.map(l => {
                const displayType = getAutomaticDisplayType(l.data, temp);
                if (l.id === target) {
                  return {
                    ...l,
                    data: {
                      ...l.data,
                      textAreaValue: temp,
                      displayType,
                    },
                  };
                }
                return l;
              });
            });
          }
        },
        false,
        postInstance => {
          setNodeParam({
            id: target,
            data: {
              showStopGenerateBtn: true,
              abortNode: {
                source,
                target,
                edgeId: id,
                apiInstance: postInstance,
              },
            },
          });
        }
      );

      return temp;
    } catch (error) {
      return Promise.reject(error);
    } finally {
      setNodeParam({
        id: target,
        data: {
          showStopGenerateBtn: false,
          abortNode: {},
        },
      });
    }
  };
  const methodType = async (currentSingleFlow, nodeText) => {
    try {
      let obj = {};
      switch (currentSingleFlow?.type) {
        case "CustomCode":
          const htmlSrc = getHtmlStr(currentSingleFlow, nodeText);
          obj = {
            textAreaValue: htmlSrc,
            displayType: "customJsFunction",
            loadingNodeHtml: true,
          };
          break;
        case "customApi":
          const requestData = currentSingleFlow.apiData.requestData;
          const responseData = await sendRequest(nodeText, requestData);
          obj = {
            textAreaValue: responseData || {},
            displayType: "customApiFunction",
          };
        default:
          break;
      }
      return obj;
    } catch (error) {
      return Promise.reject(error);
    }
  };
  const getFinalResult = async (currentSingleFlow, nodeText, edgeParam, isRecursiveCall = false, outerEdgeId) => {
    try {
      let prevResult = {
        textAreaValue: nodeText || "",
        displayType: "text",
      };

      // 判定方法类型
      if (currentSingleFlow?.type) {
        return methodType(currentSingleFlow, prevResult.textAreaValue);
      }

      // 处理多个方法调用
      if (!func.isEmpty(currentSingleFlow?.edgeArr)) {
        const flowLen = currentSingleFlow?.edgeArr?.length || 0;

        for (let index = 0; index < currentSingleFlow.edgeArr.length; index++) {
          const x = currentSingleFlow.edgeArr[index];
          const takeLastDisplay = flowLen - 1 === index;
          const edgeParamData = {
            id: outerEdgeId,
            // id: x.id,
            source: x.source,
            // target:x.target,
            target: edgeParam.target,
            lineParam: x.lineParam,
          };
          // 07/28/2023 get latest function name from alias - temp fix by @david but needs to revisit
          const funcName = getFunctionNameByAlias(x.data.lineParam.enterText.replace("/", ""));

          if (selectApiReg.test(x.data.lineParam.enterText)) {
            // 方法调用
            const customFunc = singleFlowEdgeArrRef.current.find(x => x.name === funcName);
            const builtInFunctionObj = defaultApis.find(x => x.label === funcName);
            const type = builtInFunctionObj?.value || funcName;
            const isShowStockInterface = type === "showStockInterface";
            // ShowStockInterface需要直接走getOtherTypeData方法
            if (!func.isEmpty(customFunc?.edgeArr) && !isShowStockInterface) {
              // 多层调用
              prevResult = await getFinalResult(customFunc, prevResult.textAreaValue, edgeParam, true, outerEdgeId);
            } else if (!func.isEmpty(customFunc) && !isShowStockInterface) {
              // 自定义方法
              prevResult = await customMethod(
                prevResult.textAreaValue,
                customFunc,
                x.data.lineParam,
                takeLastDisplay,
                edgeParam
              );
            } else {
              // 默认方法
              const imageGenSelectOptionsArr = store.getState().fot.imageGenSelectOptions;
              // 如果权限不可见，但是仍然可以调用这个function
              const selectOption = imageGenSelectOptionsArr.find(x => x.label.trim() === funcName);
              if (func.isEmpty(selectOption)) {
                return {
                  textAreaValue: "",
                  displayType: "text",
                };
              }
              const defaultData = await getOtherTypeData(
                edgeParamData,
                selectOption.value,
                prevResult.textAreaValue,
                true
              );

              // 有的类型没有返回值
              if (!func.isEmpty(defaultData)) {
                prevResult = defaultData;
              } else {
                prevResult = {
                  textAreaValue: "",
                };
              }
            }
          } else {
            // 普通调用
            const result = await normalCall(
              x.data.lineParam,
              prevResult.textAreaValue,
              takeLastDisplay,
              edgeParam,
              isRecursiveCall
            );
            prevResult = {
              textAreaValue: result || "",
              displayType: "text",
            };
          }
        }
      }
      return prevResult;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const handleCreatedFunctionRun = async (edgeText, edgeParam, context) => {
    const { id, target, source } = edgeParam;
    try {
      let customFunctionObj = {};
      let singleFlowEdgeObj = {};
      // project分享functions
      if (!func.isEmpty(customFunctionRef.current)) {
        customFunctionObj = customFunctionRef.current.find(x => x.name === edgeText);
      }
      // 账号下functions
      if (!func.isEmpty(singleFlowEdgeArrRef.current)) {
        singleFlowEdgeObj = singleFlowEdgeArrRef.current.find(x => x.name === edgeText);
      }

      const currentSingleFlow = Object.assign({}, customFunctionObj, singleFlowEdgeObj);
      const currentSourceNode = nodesRef.current.find(x => x.id === source);
      if (func.isEmpty(currentSingleFlow)) return;
      const { textAreaValue, displayType } = await getFinalResult(
        currentSingleFlow,
        currentSourceNode.data.textAreaValue,
        edgeParam,
        false,
        id
      );
      // 如果为空则在其他地方有直接赋值
      if (func.isEmpty(textAreaValue)) return;

      if (typeof textAreaValue !== "string") {
        textAreaValue.context = JSON.stringify(context);
      }
      setNodes(prevNodes => {
        return prevNodes.map(l => {
          if (l.id === target) {
            return {
              ...l,
              data: {
                ...l.data,
                textAreaValue,
                displayType,
                context,
              },
            };
          }
          return l;
        });
      });
    } catch (error) {
      return Promise.reject(error);
    } finally {
      // 这里需要屏蔽，原因是例如生成图片function会过早停止loading
      // setEdgeLineParam({
      //   id,
      //   loading: false
      // })
    }
  };
  const menuCancel = () => {
    rightMenuRef.current.style.visibility = "hidden";
    document.removeEventListener("keydown", onEscRightMenu);
    setAllowZoomOrPan(true);
  };
  function copyNode(nodeArr, isToolBarCopy = false) {
    copyNodes.current = [];
    nodesRef.current.forEach(x => {
      if (nodeArr.includes(x.id)) {
        const newNode = {
          ...x,
          id: "",
          type: x.type,
          data: {
            ...x.data,
            originId: x.id,
          },
          position: {
            ...x.position,
          },
          style: x.style,
        };

        copyNodes.current.push(newNode);
      }
    });

    if (isToolBarCopy) {
      logEvent("click", {
        target: "rightmenu_copy",
        content: {
          copyNodes: copyNodes.current,
        },
      });
    }
  }

  function copyLine(lineArr) {
    copyEdges.current = [];
    // 将选中的边以及边链接的node储存起来
    edgesRef.current.forEach(x => {
      if (lineArr.includes(x.id)) {
        const newEdge = {
          id: "",
          type: x.type,
          animated: true,
          source: "",
          target: "",
          markerEnd,
          data: {
            ...x.data,
            originId: x.id,
            originSource: x.source,
            originTarget: x.target,
          },
        };
        copyEdges.current.push(newEdge);
      }
    });
  }

  const copyNodeAndEdge = useCallback(() => {
    const nodes = getNodes();
    const edges = getEdges();
    const { nodeIds, lineIds } = CopyUtil.copyFlow(checkEdgeArr, checkNodeArr, nodes, edges);

    copyNode(nodeIds);
    copyLine(lineIds);
    setCheckEdgeArr(lineIds);

    if (!func.isEmpty(nodeIds) || !func.isEmpty(lineIds)) {
      func.customMsg({
        content: "Copied",
        type: "info",
      });
    } else {
      func.customMsg({
        content: "No node or edge selected",
        type: "info",
      });
    }

    logEvent("click", {
      target: "rightmenu_copy",
      content: {
        copyNodes: copyNodes.current,
        copyEdges: copyEdges.current,
      },
    });
  }, [checkEdgeArr, checkNodeArr]);

  const handleMenuDelete = () => {
    deleteSelectedElements(() => onPaneClick());
  };

  function getSaasAction(type) {
    const saasDataMap = {
      saas: {
        type: "saas",
        resetData: setSaasUIData,
        oldData: saasUIDataRef.current,
      },
      saasV2: {
        type: "saasV2",
        resetData: setSaasV2UIData,
        oldData: saasV2UIDataRef.current,
      },
      thinkingApp: {
        type: "thinkingApp",
        resetData: setThinkingAppUIData,
        oldData: { input: [] },
      },
    };
    return saasDataMap[type];
  }

  function handleDeleteGroupNodeByPreviewBtn(groupId) {
    const uiType = selectedUIRef.current;
    if (func.isEmpty(uiType)) return;
    // bsf-4912 replace thoughts
    let copyThoughts = cloneDeep(saasUIDataRef.current.output);
    copyThoughts = copyThoughts.filter(x => !x?.groupId || x.groupId !== groupId);
    const saasActionObj = getSaasAction(uiType);
    if (func.isEmpty(saasActionObj)) return;
    handleSaasDeleteNodes(saasActionObj, copyThoughts);
  }

  function handleDeleteSingleNodeByPreviewBtn(nodeId) {
    const uiType = selectedUIRef.current;
    if (func.isEmpty(uiType)) return;
    // const nodeIds = nodes.map(x => x.id);
    // bsf-4912 replace thoughts
    let copyThoughts = cloneDeep(saasUIDataRef.current.output);
    copyThoughts = copyThoughts
      .map(item => {
        if (item.groupId) {
          return item;
        }
        item.results = item.results.filter(child => child.nodeId !== nodeId);
        return item;
      })
      .filter(x => x.results.length > 0);
    const saasActionObj = getSaasAction(uiType);
    if (func.isEmpty(saasActionObj)) return;
    handleSaasDeleteNodes(saasActionObj, copyThoughts);
  }

  function handleDeleteNodes(nodes) {
    deleteNodeByIds(map(nodes, "id"));
    if (nodes.length === 1) {
      const nodeitem = nodes[0];
      const parentNodeId = getNodes().find(item => {
        return item.id === nodeitem.id;
      })?.data?.parentNodeId;
      setTimeout(() => {
        setGroupDeleteChildNode({ id: nodeitem.id, parentNodeId: parentNodeId });
      }, 300);
    }
    const uiType = selectedUIRef.current;
    if (func.isEmpty(uiType)) return;
    const nodeIds = nodes.map(x => x.id);
    // bsf-4912 replace thoughts
    let copyThoughts = cloneDeep(saasUIDataRef.current.output);
    copyThoughts = copyThoughts
      .map(item => {
        item.results = item.results.filter(child => nodeIds.includes(child.nodeId) === false);
        return item;
      })
      .filter(x => x.results.length > 0);
    const saasActionObj = getSaasAction(uiType);
    if (func.isEmpty(saasActionObj)) return;
    handleSaasDeleteNodes(saasActionObj, copyThoughts, nodeIds);
    logEvent("delete_node", {
      nodes,
    });
  }

  function handleSaasDeleteNodes(saasDataObj, newThoughts, nodeIds = []) {
    const { resetData } = saasDataObj;
    const newInputNode = deleteInputNode(saasDataObj, nodeIds);

    resetData(prevData => {
      return {
        ...prevData,
        input: newInputNode,
        output: newThoughts,
      };
    });
    const newInputNodeId = newInputNode.map(x => x.id);
    setInputId(newInputNodeId);
    // setThoughts(newThoughts); bsf-4912 use setSassUIData 已弃用,使用上面的 resetData
  }

  const pasteNodeAndEdge = e => {
    // 3123
    if (func.isEmpty(copyNodes.current)) return;

    const oldCopyNodes = copyNodes.current;
    const oldCopyEdges = copyEdges.current;

    handleCopyId();

    const { x, y, zoom } = getViewport();
    //找到最后一个node和edge的id序号
    copyNodes.current.sort((a, b) => last(a.id.split("-")) - last(b.id.split("-")));
    const {
      id: leftNodeId,
      x: prevX,
      y: prevY,
    } = copyNodes.current.reduce(
      (result, current) => {
        if (func.isEmpty(current.parentNode) && current.position.x < result.x) {
          return {
            id: current.id,
            x: current.position.x,
            y: current.position.y,
          };
        }
        return result;
      },
      { id: "", x: Infinity, y: Infinity }
    );
    // 存下最左边节点位置
    // const prevX = copyNodes.current[0].position.x
    // const prevY = copyNodes.current[0].position.y
    // 新的粘贴原点位置
    const newOriginX = (e.clientX - x) / zoom;
    const newOriginY = (e.clientY - y) / zoom;

    copyNodes.current.forEach(n => {
      // 将最左侧节点位置设置为粘贴时鼠标所在位置
      if (n.id === leftNodeId) {
        n.position.x = newOriginX;
        n.position.y = newOriginY;
        return;
      }
      if (func.isEmpty(n.data.parentNodeId)) {
        // 其他节点依据最左侧节点重新定位
        n.position.x = n.position.x - prevX + newOriginX;
        n.position.y = n.position.y - prevY + newOriginY;
      }
    });

    setNodes(prevList => [...prevList, ...copyNodes.current]);

    if (!func.isEmpty(copyEdges.current)) {
      setEdges(prevList => [...prevList, ...copyEdges.current]);
    }

    const newSimpleNodes = copyNodes.current.map(getSimpleNode);
    const newSimpleEdges = copyEdges.current.map(getSimpleEdge);
    onNodesEdgesAdd(newSimpleNodes, newSimpleEdges);

    logEvent("click", {
      target: "rightmenu_paste",
      content: {
        copyNodes: copyNodes.current,
        copyEdges: copyEdges.current,
      },
    });
    // 由于修改了copyNodes 需要重制粘贴板数据
    // 否则再次粘贴会id错乱
    copyNodes.current = oldCopyNodes;
    copyEdges.current = oldCopyEdges;
  };

  const deleteSelectedElements = (callback = () => {}) => {
    if (func.isEmpty(checkNodeArr)) return;
    const nodes = checkNodeArr.reduce((prev, x) => {
      const arr = [{ id: x }];
      if (GROUPID_REG.test(x)) {
        const node = getNode(x);
        arr.push(...node.data.childNode.map(n => ({ id: n })));
      }
      return prev.concat(arr);
    }, []);

    const normalNodes = nodes.filter(node => !NEW_EDGE_REG.test(node.id));

    const edgeNodes = nodes.filter(node => NEW_EDGE_REG.test(node.id));

    // stud-1639: delete node
    triggerRemovePreviewAppNode({
      ids: map(normalNodes, "id"),
      edgeNodeIds: map(edgeNodes, "id"),
      onSuccess() {
        handleDeleteNodes(nodes);
        callback();
      },
    });
  };

  // 点击空白面白清空选中nodes及edges
  const onPaneClick = () => {
    setPreSelectNodeId("");
    setCheckNodeArr([]);
    setCheckEdgeArr([]);
    setHasSelectedArea(false);
    const hoverEdgeData = store.getState().editor.hoverEdgeData;
    setHoverEdgeData({
      state: false,
      lineIds: hoverEdgeData.lineIds,
      edgeId: hoverEdgeData.id,
    });
  };
  const handleFuncContextMenu = edge => {
    const imageGenSelectOptionsArr = store.getState().fot.imageGenSelectOptions;
    const enterText = edge.data?.lineParam?.enterText || "";
    const reg = /^\//;
    const aiStr = enterText.replace(reg, "");
    const select = imageGenSelectOptionsArr.find(x => x.label === aiStr);
    const functionTypes = ["CustomCode", "customApi"];
    // 进入编辑页面
    if (functionTypes.includes(select?.type)) {
      setCustomFuncRightContextMenu({
        open: true,
        edgeId: edge.id,
        sourceNodeId: edge.source,
        targetNodeId: edge.target,
        select,
      });
    }
    setEdgeRight(true);
    duplicateEdgeRef.current = edge;
  };
  const onEdgeContextMenu = (ev, edge) => {
    if (edge.type === "customNewLine") {
      return;
    }
    setTimeout(() => {
      handleFuncContextMenu(edge);
    }, 0);
  };
  const editCustomJsApiFunction = () => {
    const data = Object.assign(
      {},
      { ...customFuncRightContextMenu },
      {
        state: "modify",
        parameter: {
          name: customFuncRightContextMenu.select.label,
          description: customFuncRightContextMenu.select.description,
        },
      }
    );
    if (customFuncRightContextMenu.select?.type === "CustomCode") {
      setCreateJSFuncData(data);
      // setMyModificationData(customFuncRightContextMenu.select)
      // const currentSourceNode = nodes.find(x => x.id === customFuncRightContextMenu.sourceNodeId)
      // setCurrentSourceNodeValue(currentSourceNode.data.textAreaValue)
      // setReviseCreateFunction(true)
      return;
    }
    if (customFuncRightContextMenu.select?.type === "customApi") {
      setCreateAPIFuncData(data);
    }
  };
  const duplicateEdge = () => {
    if (func.isEmpty(duplicateEdgeRef.current)) return;
    const prevTargetNode = nodesRef.current.find(x => x.id === duplicateEdgeRef.current.target);
    nodeIndexRef.current++;
    const newTargetNodeId = `editor-${nodeIndexRef.current}`;
    const newTargetNode = {
      id: newTargetNodeId,
      type: "customNode",
      data: {
        ...nodeDataRef.current,
      },
      position: {
        x: prevTargetNode.position.x,
        y: prevTargetNode.position.y - 210,
      },
    };

    setNodes(prevList => [...prevList, newTargetNode]);
    // 新增连线
    edgeIndexRef.current++;
    const newEdge = {
      id: `edge-${edgeIndexRef.current}`,
      type: "customEdge",
      animated: true,
      source: duplicateEdgeRef.current.source,
      target: newTargetNodeId,
      markerEnd,
      data: {
        ...duplicateEdgeRef.current.data,
      },
    };
    setEdges(prevList => [...prevList, newEdge]);

    onNodesEdgesAdd([getSimpleNode(newTargetNode)], [getSimpleEdge(newEdge)]);
    // duplicateEdgeRef.current = null
    logEvent("duplicate_edge");
  };
  function autoSetInputField() {
    if (
      func.isEmpty(selectedUIRef.current) ||
      !isEmpty(UIConfigRef.current[selectedUIRef.current]?.UIData?.current?.input)
    )
      return;
    autoLoadInputField(selectedUIRef.current);
  }
  const handleCopyId = () => {
    const edgeIdMap = {};
    const nodeIdMap = {};
    copyNodes.current = copyNodes.current.map(x => {
      nodeIndexRef.current++;
      const nodeId = utilIndex.createIdByNodeType(x.type, nodeIndexRef.current);
      nodeIdMap[x.data.originId] = nodeId;
      const nodeObj = {
        ...x,
        id: nodeId,
        type: x.type,
        selected: false,
        data: {
          ...x.data,
        },
        position: {
          ...x.position,
        },
      };
      return nodeObj;
    });
    if (!func.isEmpty(copyEdges.current)) {
      copyEdges.current = copyEdges.current.map((x, index) => {
        edgeIndexRef.current++;
        const source = copyNodes.current.find(n => n.data.originId === x.data.originSource);
        const target = copyNodes.current.find(n => n.data.originId === x.data.originTarget);
        if (func.isEmpty(source) || func.isEmpty(target)) {
          return null;
        }
        const newEdgeId = utilIndex.createIdByEdgeType(x.type, edgeIndexRef.current);
        edgeIdMap[x.data.originId] = newEdgeId;
        const newEdge = {
          id: newEdgeId,
          type: x.type,
          animated: true,
          source: source.id,
          sourceHandle: x.sourceHandle,
          target: target.id,
          targetHandle: x.targetHandle,
          markerEnd,
          style: x.style,
          selected: false,
          data: x.data,
          // data: {
          //   ...edgeDataRef.current,
          //   ...x.data
          // }
        };
        return newEdge;
      });
      copyEdges.current = copyEdges.current.filter(x => !func.isEmpty(x));
    }
    for (let x of copyNodes.current) {
      if (x.type === "customNewEdge") {
        x.data.targetLineId = edgeIdMap[x.data.targetLineId];
        x.data.targetNodeId = nodeIdMap[x.data.targetNodeId];
        x.data.flows = x.data.flows.map(({ sourceLineId, sourceNodeId, creationMethod }) => {
          return {
            sourceNodeId: nodeIdMap[sourceNodeId],
            sourceLineId: edgeIdMap[sourceLineId],
            creationMethod: creationMethod || false,
          };
        });
      }
      if (x.type === "customNode" && !func.isEmpty(x.parentNode)) {
        const newParentNodeId = nodeIdMap[x.data.parentNodeId];
        x.data.parentNodeId = newParentNodeId;
        x.parentNode = newParentNodeId;
        x.zIndex = 1;
      }
      if (x.type === "customGroup" && !func.isEmpty(x.data.childNode)) {
        x.data.childNode = x.data.childNode.map(i => {
          return nodeIdMap[i];
        });
      }
    }
  };

  const isCurrentGroupNode = () => {
    const nodeId = nodeContextMenuRef.current.id;
    setIsGroupNode(GROUPID_REG.test(nodeId));
  };
  const onNodeContextMenu = (ev, node) => {
    setTimeout(() => {
      if (isNewEdgeRunRef.current && node.type === "customNewEdge") {
        handleFuncContextMenu(node);
        // 点击右键菜单，此时跟点击左键一样，先选中。然后弹窗
        const lineIds = getNewEdgeAllLineIdsByNode(node);
        const filterArr = checkEdgeArr.filter(x => !lineIds.includes(x));
        const newArr = filterArr.length < checkEdgeArr.length ? filterArr : [...checkEdgeArr, ...lineIds];
        setCheckEdgeArr(newArr);
        if (!checkNodeArr.find(x => x === node.id)) {
          setCheckNodeArr([...checkNodeArr, node.id]);
        }
        setHoverEdgeData({
          state: true,
          lineIds,
          edgeId: node.id,
          isSelected: true,
        });
      } else {
        setNodeRight(true);
        nodeContextMenuRef.current = node;
        PreviewAppUtil.disabledPublishToOutput({
          nodeId: node.id,
          saasUIData: saasUIDataRef.current,
          nodes: nodesRef.current,
        });
        const isStartNodeContextMenu = startNodeIds.includes(node.id);
        setShowAddInputField(isStartNodeContextMenu);
        isCurrentGroupNode();
      }
    }, 0);
  };
  const addSingleNodeToPreview = (node, previewUI, isCotAddToPreview) => {
    if (func.isEmpty(node)) return;
    if (func.isEmpty(node.data.textAreaValue) && isCotAddToPreview === false) {
      func.customMsg({
        content: "The node is empty",
        type: "info",
      });
      return;
    }
    const currPreviewUI = UIConfigRef.current[previewUI];
    const list = PreviewAppUtil.getPreviewOutputBySingleNode({
      saasUIData: currPreviewUI.UIData.current,
      output: currPreviewUI.UIData.current.output,
      previewIndexRef: currPreviewUI.previewIndexRef,
      addContentId: addContentIdRef.current,
      node,
    });
    // stud-1832 chat ai input
    currPreviewUI.setUIDataFunc(prevData => {
      return {
        ...prevData,
        output: list,
      };
    });
    return list;
  };
  function addGroupNodeToPreview(groupChildeNodes, previewUI, getResult) {
    const currPreviewUI = UIConfigRef.current[previewUI];
    const list = PreviewAppUtil.getPreviewOutputByGroupNode({
      output: currPreviewUI.UIData.current.output,
      previewIndexRef: currPreviewUI.previewIndexRef,
      groupChildeNodes,
      getResult,
      addContentId: addContentIdRef.current,
    });
    currPreviewUI.setUIDataFunc(prevData => {
      return {
        ...prevData,
        output: list,
      };
    });
    return list;
  }
  const isGroupChildeNodes = node => {
    return nodesRef.current.filter(x => !func.isEmpty(node.parentNode) && x.parentNode === node.parentNode);
  };

  function awaitAddNodeFunc() {
    if (awaitAddNode.value) {
      setTimeout(() => {
        awaitAddNode.value.type === "customGroup"
          ? clickGroupToPreview(awaitAddNode.value)
          : addIndividual(awaitAddNode.value);
        awaitAddNode.value = undefined;
      }, 1000);
    }
  }

  function cancelCreatePreview() {
    awaitAddNode.value = undefined;
  }

  // 添加单个到 preview
  function addIndividual(node, previewUI = selectedUIRef.current, isCotAddToPreview = false) {
    if (previewUI === "") {
      setShowPreviewPanel(true);
      awaitAddNode.value = node;
      return;
    }
    if (disablePublishDisplayType.includes(node.data.displayType)) {
      return;
    }
    // bsf-4915 single 外部去掉了 parentNode, 未来可将下一行去掉， getPreviewOutputBySingleNode 内部实现了优先级
    const obj = Object.assign({}, { ...node }, { parentNode: "" });
    const list = addSingleNodeToPreview(obj, previewUI, isCotAddToPreview);
    if (func.isEmpty(list)) return;
    // bsf-4912 use setSassUIData
    setSaasUIData(prev => ({ ...prev, output: list }));
    if (isCotAddToPreview === false) {
      setShowPreviewPanel(true);
    }
    nodeContextMenuRef.current = null;
  }
  // 添加group到 preview
  function clickGroupToPreview(node, previewUI = selectedUIRef.current, isCotAddToPreview = false) {
    if (!GROUPID_REG.test(node.id)) return;
    if (previewUI === "") {
      setShowPreviewPanel(true);
      awaitAddNode.value = node;
      return;
    }
    const groupChildeNodes = nodesRef.current.filter(x => x.parentNode === node.id);

    // group
    const list = addGroupNodeToPreview(groupChildeNodes, previewUI);
    console.log("list :>> ", list);
    // bsf-4912 use setSassUIData
    setSaasUIData(prev => ({ ...prev, output: list }));
    if (isCotAddToPreview === false) {
      setShowPreviewPanel(true);
    }
    nodeContextMenuRef.current = null;
  }
  // 添加多个到 preview
  function clickToPreviewNode(node, previewUI = selectedUIRef.current) {
    let list = [];
    const groupChildeNodes = isGroupChildeNodes(node);
    if (!func.isEmpty(groupChildeNodes)) {
      // group
      list = addGroupNodeToPreview(groupChildeNodes, previewUI);
    }
    // bsf-4912 use setSassUIData
    setSaasUIData(prev => ({ ...prev, output: list }));
    setShowPreviewPanel(true);
    nodeContextMenuRef.current = null;
  }
  const generateNewData = (data, nodeId, content, contentType, errorText) => {
    const newArr = data.map(x => {
      const resultArr = x.results.map(card => {
        if (card.nodeId === nodeId) {
          return {
            ...card,
            content,
            errorText,
            type: contentType,
          };
        }
        return card;
      });
      return {
        ...x,
        results: resultArr,
      };
    });
    return newArr;
  };

  const getNewInputData = (data, nodeId, textAreaValue) => {
    if (homePluginState.isShare) return data;
    const newArr = data.map(node => {
      if (node.id === nodeId && EDITORABLE_INPUT_TYPE.includes(node.data.displayType)) {
        return {
          ...node,
          data: {
            ...node.data,
            textAreaValue,
          },
        };
      }
      return node;
    });
    return newArr;
  };

  const handleOnboarding = async () => {
    const disableCotOnboarding = get(store.getState(), "editor.me.feature_tags", []).includes("disable_cot_onboarding");
    const disableManualOnboarding = get(store.getState(), "editor.me.feature_tags", []).includes(
      "disable_manual_onboarding"
    );

    if (isSubscriber === false) {
      await getImagicaStudioTutorial();

      if (disableManualOnboarding === false) {
        triggerGuideManual();
      }

      if (disableCotOnboarding === false) {
        triggerGuideCot();
      }
    }
  };

  const handleImagicaData = async () => {
    try {
      /// 获取function v2列表
      if (!homePluginState.isShare) {
        getIt(PreferencesStore).renew();
        getIt(SubscriptionStore).renew();
        getIt(WebConfigStore).get();
        const storeDataPromise = [getIt(FunctionV2Store).get()];
        if (feature_tags.showEdgeBluePrint) {
          storeDataPromise.push(getIt(ToolPieceStore).get(), getIt(BlueprintsStore).get());
        }
        await Promise.all(storeDataPromise);
      }
    } catch (error) {
      console.log("error::::", error);
      throw error;
    }
  };

  function updatePreviewNodeContent(param) {
    let { nodeId, textAreaValue, displayType, chatBoxParam, errorText } = param;
    // bsf-4912 replace thoughts
    const tempThoughts = [...saasUIDataRef.current.output];
    // const currentThought = tempThoughts.find(x => x?.results?.[0]?.nodeId === nodeId)
    // if (util.isEmpty(currentThought)) return;
    let value = textAreaValue;
    if (displayType === "chatBox") {
      value = {
        textAreaValue,
        chatBoxParam,
        nodeId,
      };
    }
    const { content, contentType } = PreviewAppUtil.getContentByParam(displayType, value);
    // 更新ui数据
    const newSaasArr = generateNewData(saasUIDataRef.current.output, nodeId, content, contentType, errorText);
    // 更新SaasUIData
    const newSaasInput = getNewInputData(saasUIDataRef.current.input, nodeId, textAreaValue);
    setSaasUIData(prevData => {
      return {
        ...prevData,
        input: newSaasInput,
        output: newSaasArr,
      };
    });
    const newSaasV2Arr = generateNewData(saasV2UIDataRef.current.output, nodeId, content, contentType, errorText);
    const newSaasV2Input = getNewInputData(saasV2UIDataRef.current.input, nodeId, textAreaValue);
    setSaasV2UIData(prevData => {
      return {
        ...prevData,
        input: newSaasV2Input,
        output: newSaasV2Arr,
      };
    });
    const newThinkingAppArr = generateNewData(
      thinkingAppUIDataRef.current.output,
      nodeId,
      content,
      contentType,
      errorText
    );
    setThinkingAppUIData(prevData => {
      return {
        ...prevData,
        output: newThinkingAppArr,
      };
    });
    // 更新显示数据
    const newArr = generateNewData(tempThoughts, nodeId, content, contentType, errorText);
    // bsf-4912 use setSassUIData
    setSaasUIData(prev => ({ ...prev, output: newArr }));
  }
  function resetHistoryIndex() {
    undoRef.current = undoRef.current.slice(historyIndex.current + 1);
    historyIndex.current = -1;
  }
  function onNodeContentChange(nodesData) {
    if (func.isEmpty(nodesData)) return;
    resetHistoryIndex();
    undoRef.current = [
      {
        nodes: nodesData,
        edges: [],
        type: "nodeContentChange",
      },
      ...undoRef.current,
    ];
  }
  function onEdgeContentChange(edgesData) {
    resetHistoryIndex();
    undoRef.current = [
      {
        nodes: [],
        edges: edgesData,
        type: "edgeContentChange",
      },
      ...undoRef.current,
    ];
  }
  function onNodesEdgesDrag(dragNodes) {
    resetHistoryIndex();
    undoRef.current = [
      {
        nodes: dragNodes,
        edges: [],
        type: "drag",
      },
      ...undoRef.current,
    ];
  }
  function onNodesResize(resizeNode) {
    resetHistoryIndex();
    undoRef.current = [
      {
        node: resizeNode,
        type: "resize",
      },
      ...undoRef.current,
    ];
  }
  function setAutoDelNodeIds(delNodes) {
    const hasAutoDelNodeIds = [];
    // 获取被删除node是否为autodel
    nodesRef.current.forEach(x => {
      if (delNodes.some(y => y.id === x.id) && x.data.isAutoDel) {
        hasAutoDelNodeIds.push(x.id);
      }
    });
    // 重置isAutoDel的值
    setNodes(prevList => {
      return prevList.map(x => {
        if (delNodes.some(y => y.id === x.id) && x.data.isAutoDel) {
          return {
            ...x,
            data: {
              ...x.data,
              isAutoDel: false,
            },
          };
        }
        return x;
      });
    });
    return hasAutoDelNodeIds;
  }
  const getEdgeNode = delEdges => {
    const target = delEdges?.[0]?.target || null;
    const edgeNode = nodesRef.current.find(x => x.id === target);
    return edgeNode?.data?.lineParam || {};
  };

  const onNodesDelete = async delNodes => {
    let delEdges = [];
    delEdges = edges.filter(x => delNodes.some(y => y.id === x.source || y.id === x.target));
    if (isNewEdgeRunRef.current) {
      delEdges = onNodesDeleteV3(delNodes, edges);
      const lineParam = getEdgeNode(delEdges);
      if (!func.isEmpty(lineParam)) {
        const edgeEnterText = await updateEdgeText(lineParam.id);
        if (!func.isEmpty(lineParam.blueJson)) {
          await updateBlueprintEdge(lineParam.id, edgeEnterText || lineParam.enterText);
        } else {
          await getAndSetQueryV3(lineParam.id, edgeEnterText || lineParam.enterText);
        }
        setCommandEnterNodeLineParam(lineParam);
      }
    }
    const hasAutoDelNodeIds = setAutoDelNodeIds(delNodes);
    // 只有用户手动删除才加入undo
    if (func.isEmpty(hasAutoDelNodeIds)) {
      onNodesEdgesDel(delNodes, delEdges);
    } else {
      // 自动删除的node做一次undo操作去掉缓存数据
      handleUndo();
      resetHistoryIndex();
    }
    // 如果组里的node都删除了, 把组也删除掉
    watchDelGroupNode(delNodes);
  };
  function onNodesEdgesAdd(addNodes, addEdges, relevantNodes = []) {
    resetHistoryIndex();
    undoRef.current = [
      {
        nodes: addNodes,
        relevantNodes,
        edges: addEdges,
        type: "add",
      },
      ...undoRef.current,
    ];
  }
  function onNodesEdgesDel(delNodes, delEdges, relevantNodes = []) {
    resetHistoryIndex();
    undoRef.current = [
      {
        nodes: delNodes,
        relevantNodes,
        edges: delEdges,
        type: "delete",
      },
      ...undoRef.current,
    ];
  }
  function getUIListParam(checkedList = []) {
    const obj = {
      saas: {
        label: "",
        value: "saas",
        previewV2: true,
        title: saasUIData.title,
        titleV3: saasUIData?.titleV3 || {},
        subTitle: saasUIData.subTitle,
        subTitleV3: saasUIData?.subTitleV3 || {},
        placeholder: saasUIData.placeholder,
        input: saasUIData.input,
        output: [...saasUIData.output],
        showTitle: saasUIData.showTitle,
        showSubTitle: saasUIData.showSubTitle,
        titleStyle: JSON.stringify(saasUIData.titleStyle),
        subTitleStyle: JSON.stringify(saasUIData.subTitleStyle),
        theme: saasUIData.theme || applicationTheme,
        settings: saasUIData.settings || {},
        fontSize,
        staticMode: isStaticMode,
      },
      saasV2: {
        label: "",
        value: "saasV2",
        appName: saasV2UIData.appName,
        appTitle: saasV2UIData.appTitle,
        appUseCase1: saasV2UIData.newPreviewCase ? "" : saasV2UIData.appUseCase1,
        appUseCase2: saasV2UIData.newPreviewCase ? "" : saasV2UIData.appUseCase2,
        appUseCase3: saasV2UIData.newPreviewCase ? "" : saasV2UIData.appUseCase3,
        newPreviewCase: saasV2UIData.newPreviewCase,
        appTagline: saasV2UIData.appTagline,
        inputSuggestion: saasV2UIData.inputSuggestion,
        placeholder: saasV2UIData.placeholder,
        input: saasV2UIData.input,
        output: [...saasV2UIData.output],
      },
      thinkingApp: {
        label: "",
        value: "thinkingApp",
        input: thinkingAppUIData.input,
        output: [...thinkingAppUIData.output],
      },
    };

    const targetList = checkedList.length === 0 ? checkedUIList : checkedList;

    const UIList = targetList.map(x => {
      const currObj = obj[x.value] || {};
      currObj.label = x.label;
      return currObj;
    });
    return UIList;
  }
  function getFilterDataAttrsNode(node, needFilterAttrs) {
    const needSaveDataAttrs = Object.entries(node.data).filter(([key]) => !needFilterAttrs.includes(key));
    const needSaveData = Object.fromEntries(needSaveDataAttrs);
    return {
      ...node,
      data: needSaveData,
    };
  }
  function getSimpleNode(node) {
    const nodeData = getSimpleNodeData(node);
    return {
      ...node,
      data: nodeData,
    };
  }
  function getSimpleEdge(edge) {
    const edgeData = getSimpleEdgeData(edge);
    return {
      ...edge,
      data: edgeData,
    };
  }
  const getSimpleNodeData = node => {
    const simpleNodeData = {};
    Object.keys(nodeDataRef.current).forEach(x => {
      if (typeof nodeDataRef.current[x] !== "function") {
        simpleNodeData[x] = node.data[x];
      }
    });
    return simpleNodeData;
  };
  const getSimpleEdgeData = edge => {
    const simpleEdgeData = {};
    const noNeedAttrs = ["nodes", "singleFlowEdgeArrRef"];
    if (func.isEmpty(edgeDataRef.current) || func.isEmpty(edge.data)) return simpleEdgeData;
    Object.keys(edgeDataRef.current).forEach(x => {
      if (typeof edgeDataRef.current[x] !== "function" && !noNeedAttrs.includes(x)) {
        simpleEdgeData[x] = edge.data[x];
      }
    });
    return simpleEdgeData;
  };
  function setSimplifiedEdgeNode(obj) {
    if (obj.type === "customNode") {
      return {
        ...obj,
        data: {
          ...nodeDataRef.current,
          ...obj.data,
        },
      };
    }
    if (obj.type === "customEdge") {
      return {
        ...obj,
        data: {
          ...edgeDataRef.current,
          ...obj.data,
          lineParam: {
            ...edgeDataRef.current.lineParam,
            ...(obj.data?.lineParam || {}),
          },
        },
      };
    }
    if (obj.type === "customGroup") {
      return {
        ...obj,
        data: {
          ...groupDataRef.current,
          ...obj.data,
        },
      };
    }
    if (obj.type === "customNewEdge") {
      return {
        ...obj,
        data: {
          ...newEdgeDataRef.current,
          ...obj.data,
          lineParam: {
            ...newEdgeDataRef.current.lineParam,
            ...(obj.data?.lineParam || {}),
          },
        },
      };
    }
    if (obj.type === "customNewLine") {
      return {
        ...obj,
        data: {
          ...newLineDataRef.current,
          ...obj.data,
        },
      };
    }
    return obj;
  }
  function getItemRelavantFunctions() {
    // 本地所有自定义 function
    let funcArrs = singleFlowEdgeArrRef.current || [];

    // 当前接口所保存自定义 function
    if (!func.isEmpty(customFunctionRef.current)) {
      // 去重以本地创建 function 为主
      const noDuplicateArr = customFunctionRef.current.filter(x => {
        return !funcArrs.some(y => y.name === x.name);
      });
      funcArrs.push(...noDuplicateArr);
    }

    // 所有用到的自定义 func
    const edgesNameArr = edges
      .map(x => {
        return selectApiReg.test(x.data.lineParam?.enterText) && x.data.lineParam.enterText.replace("/", "");
      })
      .filter(x => x);
    const usedFunctions = funcArrs.filter(x => edgesNameArr.includes(x.name));
    return usedFunctions;
  }

  async function saveAsExistingTemplateV2(
    templateObj = store.getState().studio.selectedTemplate,
    edges = edgesRef.current,
    // shareObj = undefined,
    // variableList = store.getState().fot.variableList,
    // params,
    data = {}
  ) {
    if (showMyModification) {
      if (func.isEmpty(edges)) {
        func.customMsg({
          content: "Function cannot be empty.",
          type: "warning",
        });
        return;
      }
      const sourceEdgeData = edges
        .map(x => x.source)
        .filter((el, index, self) => {
          return self.indexOf(el) !== index;
        });
      if (!func.isEmpty(sourceEdgeData)) {
        func.customMsg({
          content: "Functions can only contain one-way flow edges, and the current structure cannot be saved.",
          type: "info",
        });
        return;
      }
      // 跳过弹窗
      // setCreateFunctionTitle('Update Function')
      // setCreateNewFuncModal(true)
      setFuncName(templateObj.name);
      // setFuncDescription(myModificationData.description)

      // 异步触发 弹窗确认事件
      return new Promise((reslove, reject) => {
        setTimeout(() => {
          clickModalSaveBtn().then(reslove).catch(reject);
        }, 0);
      });
    }
    if (func.isEmpty(templateObj.id)) {
      throw new Error("Unable to save project, empty id", "error");
    }
    // TODO: revisit
    // if (util.isEmpty(saasUIData.title) || util.isEmpty(saasUIData.subTitle)) {
    //   throw new Error('title or subtitle cannot be empty', 'error')
    // }
    try {
      const updatedProject = await saveProject(data, templateObj.id);
      func.messageUtil("Successfully saved", "success");
      return updatedProject;
    } catch (error) {
      return Promise.reject(error);
    }
  }
  function getProjectData(name = "requirement_1") {
    try {
      return api
        .get(`/be/bas-demo-v4/brain_studios/templates?template_name=${name}&v2=true`)
        .then(response => response.json())
        .then(data => {
          let generateParam = {};
          let summarizeParam = {};
          const { nodesFromProject, edgesFromProject } = handleProjectData(data, true);
          //没有获取到node或edge，返回空
          if (func.isEmpty(nodesFromProject) || func.isEmpty(edgesFromProject)) {
            return { generateParam, summarizeParam };
          }
          const firstNode =
            nodesFromProject.find(
              n => edgesFromProject.every(e => e.target !== n.id) && edgesFromProject.some(e => e.source === n.id)
            ) || {};
          //没有找到首个node
          if (func.isEmpty(firstNode)) {
            return { generateParam, summarizeParam };
          }
          const fistEdge = edgesFromProject.find(e => e.source === firstNode.id);
          if (func.isEmpty(fistEdge)) {
            return { generateParam, summarizeParam };
          }
          generateParam = fistEdge?.data?.lineParam || {};
          const secondEdge = edgesFromProject.find(e => e.source === fistEdge.target);
          if (func.isEmpty(secondEdge)) {
            return { generateParam, summarizeParam };
          }
          summarizeParam = secondEdge?.data?.lineParam || {};
          return { generateParam, summarizeParam };
        });
    } catch (error) {
      console.error("get requirement_1 failed:", error);
      return { generateParam: {}, summarizeParam: {} };
    }
  }
  const handleProjectData = (selectedExistingProject, noTip) => {
    let nodesFromProject = "";
    if (typeof selectedExistingProject?.v2?.nodes === "string") {
      nodesFromProject = JSON.parse(selectedExistingProject?.v2?.nodes);
    }
    if (Array.isArray(selectedExistingProject?.v2?.nodes)) {
      nodesFromProject = selectedExistingProject?.v2?.nodes;
    }

    let edgesFromProject = "";
    if (typeof selectedExistingProject?.v2?.edges === "string") {
      edgesFromProject = JSON.parse(selectedExistingProject?.v2?.edges);
    }
    if (Array.isArray(selectedExistingProject?.v2?.edges)) {
      edgesFromProject = selectedExistingProject?.v2?.edges;
    }

    let variablesList = [];
    if (typeof selectedExistingProject?.v2?.variablesList === "string") {
      variablesList = JSON.parse(selectedExistingProject?.v2?.variablesList);
    }

    let UIData = [];
    if (!func.isEmpty(selectedExistingProject?.v2?.UI)) {
      UIData = selectedExistingProject.v2.UI.map(x => {
        let inputObj = x.input;
        let outputArr = x.output;
        if (!func.isEmpty(x.input)) {
          if (typeof x.input === "string") {
            inputObj = JSON.parse(x.input);
          } else {
            inputObj = inputObj.map(x => {
              const nodeData = HomeUsePluginUtil.getFunctionData(nodeDataRef.current);
              const data = Object.assign({}, x.data, { ...nodeData });
              return Object.assign({}, x, { data });
            });
          }
        }
        if (!func.isEmpty(x.output) && typeof x.output === "string") {
          outputArr = JSON.parse(x.output);
        }

        let titleStyle = {};
        if (!func.isEmpty(x.titleStyle) && typeof x.titleStyle === "string") {
          titleStyle = JSON.parse(x.titleStyle);
        }

        let subTitleStyle = {};
        if (!func.isEmpty(x.subTitleStyle) && typeof x.subTitleStyle === "string") {
          subTitleStyle = JSON.parse(x.subTitleStyle);
        }

        let showTitle = x?.showTitle;
        if (typeof x?.showTitle != "boolean") {
          showTitle = true;
        }
        let showSubTitle = x?.showSubTitle;
        if (typeof x?.showSubTitle != "boolean") {
          showSubTitle = true;
        }

        return {
          ...x,
          input: inputObj,
          output: outputArr,
          titleStyle,
          subTitleStyle,
          showTitle,
          showSubTitle,
        };
      });
    }

    // let thoughtsArr = []
    // if(typeof selectedExistingProject?.v2?.thoughts === 'string') {
    //   thoughtsArr = JSON.parse(selectedExistingProject?.v2?.thoughts);
    // }
    // if(Array.isArray(selectedExistingProject?.v2?.thoughts)) {
    //   thoughtsArr = selectedExistingProject?.v2?.thoughts
    // }

    const nodeIndex = selectedExistingProject.v2?.nodeIndex;
    const edgeIndex = selectedExistingProject.v2?.edgeIndex;
    if (!nodesFromProject || !edgesFromProject || isNaN(nodeIndex) || isNaN(edgeIndex)) {
      throw Error("Failed to load from project, incorrect data format");
    }
    nodesFromProject = nodesFromProject.map(x => {
      let text = x.data?.textAreaValue;
      if (typeof text === "string") {
        text = x.data?.textAreaValue.trim();
      }
      const nodeData = GROUPID_REG.test(x.id) ? groupDataRef.current : nodeDataRef.current;
      const data = Object.assign({}, { ...nodeData }, x.data, { textAreaValue: text });
      return Object.assign({}, x, { data });
    });

    edgesFromProject = edgesFromProject.map(x => {
      const data = Object.assign({}, { ...edgeDataRef.current }, x.data, { isFromSaveData: true });
      return Object.assign({}, x, {
        // 由于edge连接形式更改，兼容旧数据指定默认edge起始/终点为左右连接
        sourceHandle: func.isEmpty(x.sourceHandle) ? "b" : x.sourceHandle,
        targetHandle: func.isEmpty(x.targetHandle) ? "a" : x.targetHandle,
        data,
        animated: true,
        type: "customEdge",
      });
    });
    const hasOldSliceData = edgesFromProject.some(x => x.data.isCreatedBySlice && !GROUPID_REG.test(x.target));
    if (hasOldSliceData) {
      func.customMsg({
        content:
          "Your project is using a older version of sharding that is no longer supported, please remove your splitted nodes and redo sharding for your nodes.",
        type: "warning",
      });
    }

    let customFunctions = [];
    if (!func.isEmpty(selectedExistingProject?.v2?.functions)) {
      customFunctions = selectedExistingProject.v2.functions.map(x => {
        if (!func.isEmpty(x.edgeArr)) {
          const newEdgeArr = x.edgeArr.map(y => {
            return {
              ...y,
              data: {
                ...edgeDataRef.current,
                ...y.data,
              },
            };
          });
          return {
            ...x,
            edgeArr: newEdgeArr,
          };
        }
        return x;
      });
    }

    return {
      nodeIndex,
      edgeIndex,
      nodesFromProject,
      edgesFromProject,
      // thoughtsArr
      UIData,
      variablesList,
      customFunctions,
    };
  };
  // 重置右侧preview数据
  function resetPreviewData() {
    Object.keys(UIConfigRef.current).forEach(keyName => {
      UIConfigRef.current[keyName].previewIndexRef.current = 0;
      // stud-1735 default saasvalue
      // preview v2 版本已经去掉了 saas 和saasv2 的区分，使用v2的默认值
      let defaultObj = PreviewAppUtil.genDefaultUISaasValue();

      if (keyName === "saasV2")
        defaultObj = {
          appName: "",
          appTitle: "",
          appUseCase1: "",
          appUseCase2: "",
          appUseCase3: "",
          newPreviewCase: "",
          appTagline: "",
          inputSuggestion: "",
          placeholder: "Enter something ...",
          input: [],
          output: [],
        };
      UIConfigRef.current[keyName].setUIDataFunc(defaultObj);
    });
    setShowPreviewPanel(false);
    setCheckedUIList([]);
    setSelectedUI("");
  }

  async function genLink(index, uuid) {
    const link = await previewService.getLink({
      index,
      structureLinkParams: {
        appId: previewAppAcivieId,
        enablePreviewApp: true,
        selectedUI,
        stripe_price_id: selectedTemplate?.published_metadata?.stripe_price_id,
        type: getPreviewAppActiveLable(),
        uuid,
      },
    });
    navigator.clipboard.writeText(link);
    return link;
  }

  function compatibleInput(oldInput, isShare, isStaticMode) {
    const oldInputArr = Array.isArray(oldInput) ? oldInput : isEmpty(oldInput) ? [] : [oldInput];
    if (!isShare) return oldInputArr;

    return oldInputArr.map(x => {
      return {
        ...x,
        data: {
          ...x.data,
          textAreaValue: isStaticMode ? x.data.textAreaValue : "",
        },
      };
    });
  }

  function loadFromExistingTemplateV2(selectedExistingProject, selectedUIParam, isShare) {
    // 拦截新老数据,为 v2 版本的数据
    // fixme 1.5 和 2.0 的 tags 没有控制到
    if (!func.isEmpty(selectedExistingProject?.v3 && func.isEmpty(selectedExistingProject?.published_metadata_v2))) {
      selectedExistingProject = PreviewAppUtil.toV2PreviewProject(selectedExistingProject);
    }
    resetPreviewData();
    setDisableAddPreview(true);
    const { nodeIndex, edgeIndex, nodesFromProject, edgesFromProject, UIData, customFunctions, variablesList } =
      !func.isEmpty(selectedExistingProject.v3)
        ? handleProjectDataV3(selectedExistingProject)
        : handleProjectData(selectedExistingProject);
    // 这里直接重新计算index，保存的nodeIndex, edgeIndex存在问题
    const { maxNodeIndex, maxEdgeIndex } = getNodeEdgeMaxIndex(nodesFromProject, edgesFromProject);

    edgesFromProject.forEach(x => {
      if (x?.data?.lineParam?.loading) x.data.lineParam.loading = false;
      // bsf-1578
      CustomEdgeUtil.assignNodeToGroupNodeHandle(x);
    });
    setNodes(nodesFromProject);
    setEdges(edgesFromProject);
    setVariableList(variablesList);

    if (!func.isEmpty(customFunctions)) {
      setCustomFunction(customFunctions);
    }

    loadPreviewApp({
      selectedExistingProject,
      UIData,
      selectedUIParam,
      isShare,
      setImageScrollDireaction,
      compatibleInput,
    });

    // 需要在PreviewList被赋值后调用，才能更新到准确的数据
    updateCurrentProjectSavedPreviewList();

    // setThoughts([...thoughtsArr])
    nodeIndexRef.current = maxNodeIndex;
    edgeIndexRef.current = maxEdgeIndex;
    // nodeIndexRef.current = nodeIndex;
    // edgeIndexRef.current = edgeIndex;
  }

  function initNewProjectV2() {
    const width = homeRef.current.clientWidth;
    const height = homeRef.current.clientHeight;
    nodeIndexRef.current = 1;
    edgeIndexRef.current = 0;
    // STUD-1093 tutorial v2 - disableCotOnboarding
    const initNodeId = `editor-${nodeIndexRef.current++}`;
    //  去掉feature tag，从使用上看之前的feature tag控制没有意义
    const initNodes = [
      {
        id: initNodeId,
        type: "customNode",
        width: 250,
        height: 250,
        data: {
          ...nodeDataRef.current,
        },
        position: {
          x: width / 2,
          y: height / 2,
        },
      },
    ];
    resetPreviewData();
    setNodes(initNodes);
    setPreSelectNodeId(initNodeId);
    setEdges([]);
    // setThoughts([]); 已弃用,使用上面的 resetPreviewData
    setVariableList([]);
    return initNodes;
  }
  const judgeSingleFlowEdges = (firstSelectedEdgeId, edgeArr, nodeArr) => {
    const firstSelectedEdge = edges.find(x => x.id === firstSelectedEdgeId);
    if (func.isEmpty(firstSelectedEdge)) return false;
    const relevanceNodeArr = nodes.filter(x => x.id === firstSelectedEdge.source || x.id === firstSelectedEdge.target);
    const firstEdgeTargetNodeId = firstSelectedEdge.target;
    const targetNodeAsSourceNodeEdges = edges.filter(x => x.source === firstEdgeTargetNodeId);

    // 选中的边包含切分的edge提示并不继续执行
    const hasSlicedEdges = checkEdgeArr.some(edgeId => edges.some(x => x.id === edgeId && x.data.isCreatedBySlice));
    if (hasSlicedEdges) {
      func.customMsg({
        content: "Cannot include edges generated by sharding",
        type: "info",
      });
      return false;
    }
    //暂不支持有手动创建的边
    // const hasManualEdges = checkEdgeArr.some(edgeId => edges.some(
    //   x => x.id === edgeId && selectApiReg.test(x.data?.lineParam?.enterText) && imageGenSelectOptions.some(
    //     api => api.label === x.data?.lineParam?.enterText?.replace('/', '') && api.isCreatedManual
    //   )
    // ))
    // if (hasManualEdges) {
    //   message.info('Cannot support edges containing manually created api')
    //   return false
    // }
    if (!edgeArr.some(x => x.id === firstSelectedEdge.id)) {
      edgeArr.push(firstSelectedEdge);
    }
    relevanceNodeArr.forEach(node => {
      if (!nodeArr.some(x => x.id === node.id)) {
        nodeArr.push(node);
      }
    });
    // 被选中的边的数量
    const selectedEdges = targetNodeAsSourceNodeEdges.filter(x => checkEdgeArr.includes(x.id));
    if (func.isEmpty(selectedEdges)) {
      if (edgeArr.length > 0) return true;
      // message.info('Need to select the edges of one-way flow')
      return false;
    }
    // 如果有多个终点也不继续执行
    if (selectedEdges.length > 1) {
      func.customMsg({
        content: "The selected edge cannot have multiple endpoints",
        type: "info",
      });
      return false;
    }

    return judgeSingleFlowEdges(selectedEdges[0].id, edgeArr, nodeArr);
  };
  const clickToCreateFunction = () => {
    let edgeArr = [];
    let nodeArr = [];
    // 获取起始 edge 数据
    const sourceEdgeData = edges
      .filter(x => checkEdgeArr.includes(x.id))
      .filter((el, index, self) => {
        return !self.some(e => e.target === el.source);
      });
    if (sourceEdgeData.length >= 2) {
      func.customMsg({
        content: "Need to select the edges of one-way flow",
        type: "info",
      });
      return;
    }
    const isSingleFlowEdges = judgeSingleFlowEdges(sourceEdgeData[0]?.id, edgeArr, nodeArr);
    if (!isSingleFlowEdges) {
      edgeArr = [];
      nodeArr = [];
      return;
    }
    singleFlowEdgesRef.current.edgeArr = edgeArr;
    singleFlowEdgesRef.current.nodeArr = nodeArr;
    setCreateFunctionTitle("Create a new function");
    setFuncName("");
    setFuncDescription("");
    setCreateNewFuncModal(true);
  };

  const saveAsFunction = async () => {
    const data = singleFlowEdgesRef.current;
    const setEdgeArr = data.edgeArr.map(x => {
      const newObj = NEW_LINE_REG.test(x.id) ? getSimpleEdgeDataV3(x) : getSimpleEdgeData(x);
      if ("imageGenSelectOptions" in x.data) {
        newObj.imageGenSelectOptions = x.data.imageGenSelectOptions;
      }
      if ("isFromSaveData" in x.data) {
        newObj.isFromSaveData = x.data.isFromSaveData;
      }
      return Object.assign({}, { ...x }, { data: newObj });
    });

    const setNodeArr = data.nodeArr
      .map(x => (NEW_EDGE_REG.test(x.id) ? getSimpleNodeV3(x) : getSimpleNode(x)))
      .filter((el, index, self) => {
        return self.findIndex(e => e.id === el.id) === index;
      });

    const attributes = {
      isV3Function: isNewEdgeRunRef.current,
      description: data.description,
      edgeArr: setEdgeArr,
      name: data.name,
      nodeArr: setNodeArr,
      projectName: selectedTemplate.template_name,
    };

    return await createFunction(attributes);
  };

  const getCurrentStateOfFunctionForSaving = () => {
    const edgeArr = edges.map(x => {
      const edgeObj = NEW_LINE_REG.test(x.id) ? getSimpleEdgeDataV3(x) : getSimpleEdgeData(x);
      return Object.assign({}, { ...x }, { data: edgeObj });
    });

    const nodeArr = nodes.map(x => {
      const nodeObj = NEW_EDGE_REG.test(x.id) ? getSimpleNodeDataV3(x) : getSimpleNodeData(x);
      return Object.assign({}, { ...x }, { data: nodeObj });
    });

    return {
      description: funcDescription,
      edgeArr: edgeArr,
      name: funcNameRef.current,
      nodeArr: nodeArr,
      projectName: selectedTemplate.template_name,
      isV3Function: myModificationData.isV3Function || isNewEdgeRunRef.current,
    };
  };

  const saveUpdateFunction = async () => {
    const attributes = getCurrentStateOfFunctionForSaving();
    await gql.mutation.updateObject(myModificationData.id, {
      name: "studio_function",
      attributes: attributes,
    });
    setShowNavPage(true);
    setShowMyModification(false);
  };

  const publishFunction = async (name, description, functionData) => {
    let attributes = functionData || getCurrentStateOfFunctionForSaving();
    try {
      if (typeof attributes.edgeArr === "string") {
        attributes = Object.assign({}, attributes, { edgeArr: JSON.parse(attributes.edgeArr) });
      }
      if (typeof attributes.nodeArr === "string") {
        attributes = Object.assign({}, attributes, { nodeArr: JSON.parse(attributes.nodeArr) });
      }
    } catch (e) {
      console.error("JSON parse function data error", e);
    }
    await gql.mutation.createObject(
      {
        name: "studio_function",
        isGlobal: true,
        attributes: Object.assign({}, attributes, {
          name,
          projectName: name,
          description: description,
          author_user_id: userMe.id,
        }),
      },
      "Successfully published"
    );
  };

  const clickModalSaveBtn = async () => {
    if (func.isEmpty(funcNameRef.current)) return;
    singleFlowEdgesRef.current.name = funcNameRef.current;
    singleFlowEdgesRef.current.description = funcDescription;
    setSaveFunctionLoading(true);
    try {
      if (showMyModification) {
        await saveUpdateFunction();
      } else {
        await saveAsFunction();
      }
    } catch (error) {
      console.error(error);
      func.messageUtil(error);
    }
    setSaveFunctionLoading(false);
    handleUserSavedFunctions({
      tempOptions: defaultApis,
      isPagination: true,
    });
    setCreateNewFuncModal(false);
    func.customMsg({
      content: "Saved successfully",
      type: "success",
    });
  };
  const clickToCreateVariable = isManualConnection => {
    showModal({
      title: "What is this Node?",
      subTitle: "Give this node a name so the Edge knows how to use it in your function.",
      buttons: ["Cancel", "Confirm"],
      input: {
        type: "input",
        require: true,
        placeholder: "Enter variable name",
      },
      onOk({ reason, close }) {
        clickCreateVariableModalSave(reason, isManualConnection);
        close();
      },
      onCancel() {
        clickCancelVariableModal();
      },
    });
  };
  const clickCancelVariableModal = () => {
    if (!func.isEmpty(manuallyConnectedEdges.current)) {
      // 取消删除新增边
      setEdges(prevList => {
        return prevList.filter(x => x.id !== manuallyConnectedEdges.current.id);
      });
    }
  };

  const clickCreateVariableModalSave = (variableName, isManualConnection) => {
    if (func.isEmpty(nodeContextMenuRef.current.data.textAreaValue)) {
      func.customMsg({
        content: "The node is empty",
        type: "info",
      });
      clickCancelVariableModal();
      return;
    }
    const variableList = store.getState().fot.variableList;
    if (variableList.some(x => x.name === variableName)) {
      func.customMsg({
        content: "A variable with the same name already exists",
        type: "info",
      });
      clickCancelVariableModal();
      return;
    }

    setStartingPointInput(nodeContextMenuRef.current, variableName);

    func.customMsg({
      content: "Saved successfully",
      type: "success",
    });
    const eventProperties = {
      name: variableName, //TODO
    };
    logEvent("create_variable", eventProperties);

    // 手动链接后，重新调用 prompt_generation api去创建新的 edge prmpt
    if (isManualConnection) {
      setManualRecall(variableName);
    }
  };

  async function setStartingPointInput(sourceNodes, variableName) {
    const variableList = store.getState().fot.variableList;
    if (variableList.some(x => x.name === variableName)) return;
    const obj = {
      name: variableName,
      node: {
        ...sourceNodes,
        data: JSON.parse(JSON.stringify(sourceNodes.data)), // 去掉函数引用
      },
    };
    setVariableList([...variableList, obj]);
  }

  const setManualRecall = async variableName => {
    try {
      const edge = multipleEdgeHostRef.current;
      const sourceNodes = nodesRef.current.find(x => x.id === edge.source);
      // 把变量放进边的数据里
      if (!edge.data.lineParam.createFromCot) {
        // 非 cot 需要将起始点input, 保存至变量列表。
        await setStartingPointInput(sourceNodes, "Input");
        // 将 input 设置为变量
        setVarilableNames(edge, "Input");
      }
      // cot 创建 新变量保存
      setVarilableNames(edge, variableName);
      const { identifier } = await getAndSetQuery(edge.id, edge.data.lineParam.enterText);
      setIdentifier(edge.id, identifier);
      // 打字效果
      await setEdgeValueByWriter(edge);
      // run
      await clickRunAllBtn([sourceNodes]);
    } catch (error) {
      console.error("error", error);
    }
  };

  const initLoadUserFunctions = async () => {
    setUserFunctionsLoading(true);
    await handleUserSavedFunctions({
      // 将默认值从外部传入
      tempOptions: defaultApis,
      isPagination: true,
      pageSize: PROJECT_LOAD_SIZE,
    });

    setIsSetFeatureTag(true);
    setUserFunctionsLoading(false);
  };
  /**
   * `isPagination` 是否分页加载
   * @param {{isPagination?:boolean; page?:number, pageSize?:number, [key:string]: any}} args
   */
  async function handleUserSavedFunctions(args) {
    const page = get(args, "page", 1);
    const pageSize = get(args, "pageSize", PROJECT_LOAD_SIZE);
    const isPagination = get(args, "isPagination", false);
    // 该变量用于保存递归时 ImageGenSelectOptions 值
    let tempOptions = get(args, "tempOptions", []);
    try {
      const res = await fetchMyFunctions({ page, size: pageSize });

      // load over
      if (isEmpty(res)) {
        return;
      }

      const functionDisplayArr = [];

      // 分页加载不重置 singleFlowEdgeArrRef
      if (page === 1) {
        singleFlowEdgeArrRef.current = [];
      }
      res.forEach(each => {
        // 防止 JSON 解析报错
        let fun;
        try {
          fun = each.attributesV2 || JSON.parse(each.attributes);
        } catch {
          fun = {};
        }
        if (!fun.edgeArr || !fun.edgeArr instanceof Array) return;
        const edgeArr = fun?.edgeArr || [];
        let nodeArr = fun?.nodeArr || [];
        // 兼容只保存 id 的数据
        if (!func.isEmpty(fun?.nodeIdArr) && func.isEmpty(nodeArr)) {
          const width = homeRef.current?.clientWidth;
          const height = homeRef.current?.clientHeight;
          nodeArr = fun.nodeIdArr.map((x, index) => {
            const onj = {
              data: { ...nodeDataRef.current },
              dragging: false,
              height: 200,
              width: 200,
              id: x,
              selected: false,
              type: "customNode",
              position: {
                x: width / 2 + index * 400,
                y: height / 2,
              },
            };
            return onj;
          });
        }

        let groupType = fun?.type || "other";

        // 获取终点 edge 数据
        const targetEdgeData = edgeArr.filter((el, index, self) => {
          return !self.some(e => e.source === el.target);
        });
        // 获取终点 node 显示类型
        if (!func.isEmpty(fun.nodeArr)) {
          groupType = fun.nodeArr.find(x => x.id === targetEdgeData[0]?.target)?.data?.displayType || "";
        }

        let groupTitle = "";
        // title
        // hardcoded title type mapping to function name
        const preDefinedFunctionConfig = allPreDefinedFunctions.find(x => x.label === fun.name);
        if (preDefinedFunctionConfig?.groupTitle) {
          groupTitle = preDefinedFunctionConfig.groupTitle;
        } else if (fun.author_user_id) {
          // marketplace (other global)
          groupTitle = "marketPlace";
        } else {
          // my functions
          groupTitle = "myFunctions";
        }

        // order
        const order = [null, undefined].includes(preDefinedFunctionConfig?.order)
          ? Infinity
          : preDefinedFunctionConfig.order;

        // 兼容数据
        const obgData = {
          CustomCode: "customJsFunction",
          customApi: "customApiFunction",
          customApiFunc: "customApiFunction",
        };
        if (obgData[groupType]) {
          groupType = obgData[groupType];
        }

        singleFlowEdgeArrRef.current.push(fun);
        functionDisplayArr.push({
          id: each.id,
          author: fun.author,
          editable: !func.isEmpty(each.user),
          codeData: fun?.codeData || {},
          apiData: fun?.apiData || {},
          type: fun?.type || "",
          relationId: each.relationId,
          isCreatedManual: true,
          isV3Function: fun.isV3Function || false,
          value: fun.name || fun.function_name,
          label: fun.name || fun.function_name,
          description: fun.description,
          projectName: fun.projectName,
          edgeArr: JSON.stringify(edgeArr),
          nodeArr: JSON.stringify(nodeArr),
          groupType,
          groupTitle,
          order,
          hide: preDefinedFunctionConfig?.hide,
          updatedAt: each.updatedAt,
        });
      });

      // page load
      if (isPagination) {
        // 分页更新状态需要同步更新，借用外部变量
        tempOptions = [...tempOptions, ...functionDisplayArr];
        setImageGenSelectOptions(tempOptions);
        // page next
        await handleUserSavedFunctions({
          tempOptions,
          pageSize,
          isPagination,
          page: page + 1,
        });
        return;
      }
      const arr = [...defaultApis];
      setImageGenSelectOptions(arr.concat(functionDisplayArr));
    } catch (error) {
      console.error("fetchMyFunctions error::::", error);
      func.messageUtil(error);
    }
  }

  //获取起点相关的node和边
  const getInputChildNodesAndEdge = (sourceNodes, nodeArr, edgeArr) => {
    const allNode = getNodes();
    if (func.isEmpty(sourceNodes)) return { nodeArr, edgeArr };

    const edges = edgesRef.current.filter(x => sourceNodes.includes(x.source));

    if (func.isEmpty(edges)) {
      return { nodeArr, edgeArr };
    }
    edgeArr.push(...edges);
    const nodes = edges
      .map(x => x.target)
      ?.map(x => {
        if (!GROUPID_REG.test(x)) return x;
        const childNode = allNode.filter(n => n.parentNode === x)?.map(n => n.id);
        return [x, ...childNode];
      })
      ?.flat();

    if (func.isEmpty(nodes)) {
      return { nodeArr, edgeArr };
    }
    nodeArr.push(...nodes);
    return getInputChildNodesAndEdge(nodes, nodeArr, edgeArr);
  };
  const handleInputChild = () => {
    const inputId = (saasUIData.input || []).map(x => x.id);
    if (isEmpty(inputId)) {
      setSaasInputChildNode(nodesRef.current.map(x => x.id));
      setSaasInputChildEdge(edgesRef.current.map(x => x.id));
      return;
    }
    const { nodeArr, edgeArr } = getInputChildNodesAndEdge(inputId, [], []) || { nodeArr: [], edgeArr: [] };
    setSaasInputChildNode(nodeArr);
    setSaasInputChildEdge(edgeArr?.map(x => x.id));
  };
  const handleSaasV2InputChild = () => {
    const inputId = (saasV2UIData.input || []).map(x => x.id);
    if (func.isEmpty(inputId)) {
      setSaasV2InputChildNode(nodesRef.current.map(x => x.id));
      setSaasV2InputChildEdge(edgesRef.current.map(x => x.id));
      return;
    }

    const { nodeArr, edgeArr } = getInputChildNodesAndEdge(inputId, [], []) || { nodeArr: [], edgeArr: [] };
    setSaasV2InputChildNode(nodeArr);
    setSaasV2InputChildEdge(edgeArr?.map(x => x.id));
  };

  useEffect(() => {
    handleInputChild();
  }, [saasUIData.input]);
  useEffect(() => {
    handleSaasV2InputChild();
  }, [saasV2UIData.input]);

  function isStartInput() {
    if (isStaticApp) {
      return false;
    }
    const node = nodeContextMenuRef.current;
    if (node) {
      const startNodeIds = store.getState().fot.startNodeIds;
      const isStartNode = startNodeIds.includes(node.id);
      return isStartNode;
    }
    return false;
  }
  const getCurrentUILabel = () => {
    const currentUI = checkedUIList.find(x => x.value === selectedUI);
    if (currentUI?.isStaticApp) {
      return "static content";
    }
    return "dynamic content";
    // const node = nodeContextMenuRef.current
    // if (node) {
    //   const startNodeIds = store.getState().fot.startNodeIds
    //   const isStartNode = startNodeIds.includes(node.id)
    //   if (isStartNode) {
    //     return "input field"
    //   } else {
    //     return "dynamic content"
    //   }
    // }
    // return currentUI.label
  };

  async function handleInputField(currentNode, type = undefined, cotDescription = "") {
    const typeSelected = type || selectedUIRef.current;
    const edges = getEdges();
    const nodes = getNodes();
    /**
     * @type {PreviewApp.ValueOf<typeof UIConfigRef.current>}
     */
    const UIConfig = UIConfigRef.current[typeSelected];
    const saasUIData = UIConfig?.UIData?.current;
    const oldInputNode = UIConfig?.UIData?.current?.input || [];

    try {
      if (PreviewAppUtil.isChatAiApp(saasUIData)) {
        PreviewAppUtil.handleChatAIAppField({
          saasUIData: saasUIData,
          currentNode: currentNode,
          nodes: nodesRef.current,
          addContentIdRef: addContentIdRef,
          selectedTemplate,
          setSaasUIData: UIConfig.setUIDataFunc,
          setInputId,
        });
        return;
      } else {
        UIConfig.setUIDataFunc(prevData => {
          return PreviewAppUtil.genHandleInputField({
            saasUIData: prevData,
            currentNode,
            addContentIdRef,
            selectedTemplate,
          });
        });
      }
      //只在添加第一个input feild时，获取title
      if (!isEmpty(oldInputNode)) return;
      let inputNodeEdges = edges.filter(x => x.source === currentNode.id) || [];
      if (isNewEdgeRunRef.current && inputNodeEdges.length > 0) {
        inputNodeEdges = nodes.filter(x => x.id === inputNodeEdges[0].target);
      }
      const edge = inputNodeEdges.find(x => !func.isEmpty(x?.data?.lineParam?.enterText));
      let inputValue = edge?.data?.lineParam?.enterText;
      // /function 生成
      if (selectApiReg.test(inputValue)) {
        const selectOption = store.getState().fot.imageGenSelectOptions;
        const funcData = selectOption.find(x => inputValue === `/${x.label}`);
        // input使用description的部分，description没有则使用name of function
        inputValue = funcData?.description || funcData?.label;
      }
      //如果传了description，使用传入的值
      if (!isEmpty(cotDescription)) {
        inputValue = cotDescription;
      }
      if (func.isEmpty(inputValue)) {
        return;
      }
      setSaasLoading(true);

      // const query = `You are an AI that generates a creative 4-5 word call-to-action title and a short description for a landing page based on an AI description. You must start the title after "Title:" and description after "Description:"
      //   Input: ${inputValue}
      //   Output:`

      const previewTitle = await previewService.getTitle(inputValue);

      // STUD-1221 title, subTitle from `parseThinkingData` data

      UIConfig.setUIDataFunc(prevData => {
        return {
          ...prevData,
          title: prevData.fromCotTitle || previewTitle.title || selectedTemplate?.name,
          subTitle: prevData.fromCotSubtitle || previewTitle.subTitle,
        };
      });
      setSaasLoading(false);
    } catch (error) {
      setSaasLoading(false);
      console.error("error::::", error);
      func.messageUtil(error);

      UIConfig.setUIDataFunc(prevData => {
        return {
          ...prevData,
          title: prevData.fromCotTitle || selectedTemplate?.name || WEB_APP_DEFAULT_TITLE,
          subTitle: prevData.fromCotSubtitle || WEB_APP_DEFAULT_SUBTITLE,
        };
      });
    }
  }
  const getPaymentStudios = async index => {
    try {
      const res = await api.getWithError(`/be/payment/subscription/studios/${index}`);
      setStripePaymentActivated(res?.data?.status === "active");
    } catch (error) {
      console.log("error:::", error);
      func.messageUtil(error);
    }
  };
  const handleSaasV2InputField = async (currentNode, selectName, cotDescription = "") => {
    const selected = selectName || selectedUIRef.current;
    const edges = getEdges();
    const oldInputNode = UIConfigRef.current[selected]?.UIData?.current?.input || [];

    const newInputNode = [...oldInputNode, currentNode];
    try {
      UIConfigRef.current[selected].setUIDataFunc(prevData => {
        return {
          ...prevData,
          input: newInputNode,
        };
      });
      //只在添加第一个input feild时，获取title
      if (!isEmpty(oldInputNode)) return;
      let inputNodeEdges = edges.filter(x => x.source === currentNode.id) || [];
      if (isNewEdgeRunRef.current && inputNodeEdges.length > 0) {
        inputNodeEdges = nodes.filter(x => x.id === inputNodeEdges[0].target);
      }
      const edge = inputNodeEdges.find(x => !func.isEmpty(x?.data?.lineParam?.enterText));
      let inputValue = edge?.data?.lineParam?.enterText;
      // /function 生成
      if (selectApiReg.test(inputValue)) {
        const selectOption = store.getState().fot.imageGenSelectOptions;
        const funcData = selectOption.find(x => inputValue === `/${x.label}`);
        // input使用description的部分，description没有则使用name of function
        inputValue = funcData?.description || funcData?.label;
      }
      //如果传了description，使用传入的值
      if (!isEmpty(cotDescription)) {
        inputValue = cotDescription;
      }
      if (func.isEmpty(inputValue)) {
        return;
      }
      setSaasV2Loading(true);
      const arr = [
        postAIDescription(
          null,
          "brain_studios_xxx_brain_studios_saas_v2_app_name_1.model",
          { input: inputValue },
          false
        ),
        postAIDescription(
          null,
          "brain_studios_xxx_brain_studios_saas_v2_app_title_1.model",
          { input: inputValue },
          false
        ),
        postAIDescription(
          null,
          "brain_studios_xxx_brain_studios_saas_v2_app_tagline_1.model",
          { input: inputValue },
          false
        ),
        postAIDescription(
          null,
          "brain_studios_xxx_brain_studios_saas_v2_input_suggestion.model",
          { input: inputValue },
          false
        ),
      ];
      const resArr = await Promise.all(arr);
      const appName = resArr[0][0].title;
      const appTitle = resArr[1][0].title;
      const appUseCase1 = resArr[1][1].title;
      const appUseCase2 = resArr[1][2].title;
      const appUseCase3 = resArr[1][3].title;
      const appTagline = resArr[2][0].title;
      const inputSuggestion = resArr[3][0].title;
      UIConfigRef.current[selected].setUIDataFunc(prevData => {
        return {
          ...prevData,
          appName,
          appTitle,
          appUseCase1,
          appUseCase2,
          appUseCase3,
          appTagline,
          inputSuggestion,
        };
      });
      setSaasV2Loading(false);
    } catch (error) {
      setSaasV2Loading(false);
      console.error("error::::", error);
      func.messageUtil(error);
    }
  };

  const getSubscription = async price_id => {
    try {
      const res = await api.getWithError(`/be/payment/subscription/price/${price_id}`);
      setSubscriptionPrice(res.data);
    } catch (error) {
      console.error("get price detail failed.", error);
    }
  };
  function addOrRemoveInputFeild(currentNode) {
    const uiType = selectedUIRef.current;
    if (func.isEmpty(uiType)) {
      setShowPreviewPanel(true);
      return;
    }
    const inputIds = store.getState().fot.inputId;
    //Add as input field
    if (!isEmpty(currentNode?.id) && !inputIds.includes(currentNode?.id)) {
      clickAddInputField(currentNode, inputIds);
      return;
    }
    //Remove input field from app
    // const uiType = selectedUIRef.current
    if (func.isEmpty(uiType)) return;
    const saasActionObj = getSaasAction(uiType);
    const needDeleteIds = !isEmpty(currentNode?.id) ? [currentNode.id] : [];
    if (isEmpty(saasActionObj) || isEmpty(needDeleteIds)) return;
    // bsf-4912 replace thoughts
    let copyThoughts = cloneDeep(saasUIDataRef.current.output);
    handleSaasDeleteNodes(saasActionObj, copyThoughts, needDeleteIds);
  }
  function clickAddInputField(node) {
    const currentNode = node ?? nodeContextMenuRef.current;
    // 不是文本node则提示
    if (
      feature_tags.disableUploadTypesAsInputNode === true &&
      currentNode.data.displayType !== "inputTextarea" &&
      currentNode.data.displayType !== "text"
    ) {
      func.customMsg({
        content: "Only text types can be added",
        type: "warning",
      });
      return;
    }
    //添加input feild到preview
    getInputField(currentNode);
    //重新添加替换原有的需要弹窗确认
    // if (!util.isEmpty(UIConfigRef.current[selectedUIRef.current].UIData.current.input)) {
    //   Modal.confirm({
    //     content: 'There is currently an input field. Are you sure to replace it?',
    //     okText: 'Confirm',
    //     onOk: () => {
    //       getInputField(currentNode)
    //     }
    //   })
    // }else {
    //   getInputField(currentNode)
    // }
    setShowPreviewPanel(true);
  }
  const handleGuideInfo = async () => {
    try {
      setImagicaStudioTutorial({
        ...imagicaStudioTutorial,
        showGuide: true,
      });
      getIt(UiFunctionService).dirty();
      const { updated_project } = await handleSave();
      // const {updated_project} = await saveAsExistingTemplateV2(selectedTemplate, nodesRef.current, edgesRef.current)
      setTutorialTempProjectData(updated_project);
      const params = {
        template_name: "tutorial",
        name: "tutorial",
        description: "",
        v2: {
          edges: [],
          nodes: [],
          nodeIndex: 0,
          edgeIndex: 0,
        },
      };
      // const selectedTemplateObj = await createProject(params);
      // if(!util.isEmpty(selectedTemplateObj.template_name)) {
      //   selectedTemplateObj.name = selectedTemplateObj.template_name
      // }
      setSelectedTemplate(params || {});
      setStudioProjectName(params.name);
      // bsf-4912 use setSassUIData
      setSaasUIData(prev => ({ ...prev, output: [] }));
      setImageScrollDireaction("verticallyScroll");
      // 点击 Interactive Tutorial。没有权限不会显示
      clickStep(1, setCenter);
    } catch (error) {
      console.error("error::::", error);
    }
  };
  const getProject = async () => {
    if (!func.isEmpty(urlParam.attrIndexer)) {
      try {
        const objectData = await HomeUtil.getObjectData(coreBrainClient, urlParam.attrIndexer);
        const attributes = JsonUtil.parse(objectData[0].attributes);
        const selectedTemplateobj = {
          id: objectData[0].id,
          name: objectData[0].attributesV2.template_name,
          uuid: objectData[0].uuid,
          ...attributes,
        };

        // project刷新时转换v2数据到v3
        if (func.isEmpty(attributes.v3)) {
          selectedTemplateobj = HomeUsePluginUtil.handleV2TransformV3Data({
            feature_tags,
            attributesV2: selectedTemplateobj,
            nodeIndexRef,
            edgeIndexRef,
            newEdgeDataRef,
            newLineDataRef,
          });
        }
        setSelectedTemplate(selectedTemplateobj);
        loadFromExistingTemplateV2(selectedTemplateobj);
        setStudioProjectName(selectedTemplateobj.name);
        const urlParams = getQueryParams();
        if (urlParams.appId) {
          comeInSetting(urlParams.appId);
        }
      } catch (error) {
        navigate("/editor");
        window.location.reload();
      } finally {
        dispatch(fotActions.setFetching(false));
      }
    }
  };

  const outputEmpty = func.isEmpty(thoughts);

  const disabledPublish = isOnlyChatInterface(saasUIData?.output)
    ? false
    : isStaticMode && outputEmpty === false
    ? false
    : (selectedUI === "saas" && func.isEmpty(saasUIData.input)) ||
      (selectedUI === "saasV2" && func.isEmpty(saasV2UIData?.input));

  const copyShareLink = link => {
    navigator.clipboard.writeText(link);
    func.customMsg({
      content: "Copied successfully",
      type: "success",
    });
    logEvent("copy_published_link", { URL: link });
  };
  const getInputField = currentNode => {
    const inputId = store.getState().fot.inputId;
    const newInputId = [...inputId, currentNode.id];
    setInputId(newInputId);
    selectedUIRef.current === "saasV2" ? handleSaasV2InputField(currentNode) : handleInputField(currentNode);
  };
  const showPublishTips = (disabledPublish || outputEmpty) && shareLoading === false;
  async function clickShareBtn() {
    if (disabledPublish || outputEmpty || shareLoading) return;

    const selectedTemplate = store.getState().studio.selectedTemplate;
    // sutd-1722: published_metadata
    let published_metadata = getPublishMetadata() || {};
    // let published_metadata = selectedTemplate?.published_metadata || {}

    if (
      selectedUI === "saas" ||
      // || (util.isEmpty(selectedTemplate?.published_metadata)
      (func.isEmpty(published_metadata) &&
        func.isEmpty(published_metadata) &&
        feature_tags.showSubscriptionFeaturesToggle &&
        selectedUI === "saasV2")
    ) {
      published_metadata = {
        uuid: "",
        original: true,
        stripe_product_id: "",
        stripe_price_id: "",
        stripe_account_id: "",
      };
    }
    setShareLoading(true);
    try {
      let shareLink = "";
      const index = selectedTemplate.index || selectedTemplate.uuid;
      // save after generating sharable link if index exists
      if (!func.isEmpty(index)) {
        shareLink = await genLink(index);
      }
      // save before generating sharable link to get index if index does not exists
      const { updated_project } = await handleSave({
        templateObj: selectedTemplate,
        shareObj: { share: true, shareLink },
        params: { published_metadata },
        showPublishTips,
      });
      if (!updated_project) {
        throw new Error("Unable to create shared project");
      }
      setShareLoading(false);

      switch (selectedUI) {
        case "saas":
          logEvent("publish_as_webapp", { URL: updated_project.published_metadata.link });
          break;
        case "saasV2":
          logEvent("publish_as_saas", { URL: updated_project.published_metadata.link });
          break;
        case "thinkingApp":
          logEvent("publish_as_thinkingapp", { URL: updated_project.published_metadata.link });
          break;
        default:
          break;
      }
    } catch (error) {
      setShareLoading(false);
      console.error("clickShareBtn", error);
      func.messageUtil(error);
    }
  }

  // const onCloseCustomModel = () => {
  //   setReviseCreateFunction(false)
  // }
  // const getUserMe = async () => {
  //   try {
  //     setMeLoading(true)

  //     const res = await api.getWithError('/api/users/me.json');
  //     // 这里给me赋值确保权限判定逻辑不会因为下面的请求报错而导致判定错误
  //     setMe(res.data);
  //     const meObj = await gql.query.me();
  //     // res.data.objectId = meObj.objectId;
  //     const me = Object.assign({}, res.data, {objectId: meObj.objectId})
  //     if (util.isEmpty(me)) {
  //       throw 'Failed to get user info'
  //     }
  //     // getImagicaStudioTutorial(me)
  //     setMe(me)
  //     if (store.getState().editor.isClickSignup) {
  //       logEvent('signup');
  //     }
  //     if(store.getState().editor.isClickLogin) {
  //       logEvent('login') //TODO: add event properties
  //     }

  //   } catch (error) {
  //     console.error('error:::', error)
  //   } finally {
  //     setMeLoading(false)
  //   }
  // }

  const getImagicaStudioTutorial = async () => {
    try {
      setShowImagicaTutoriralLoading(true);
      const res = await api.getWithError(`/api/users/me/preferences.json`);
      isLoadPreferences.current = true;
      const arr = res?.data || [];
      if (!isArray(arr)) return;
      preferenceJson.value = arr;
      const obj = arr.find(x => x.key === "imagica_studio_tutorial");
      if (obj?.value) {
        const value = typeof obj.value === "string" ? JSON.parse(obj.value) : obj.value;
        setImagicaStudioTutorial({
          ...value,
          showGuide: false,
        });
      } else {
        setImagicaStudioTutorial({
          index: 0,
          showGuide: false,
          viewed: false,
          showManualGuide: false,
          showCotGuide: false,
        });
      }
      getIt(UiFunctionService).dirty();
    } catch (error) {
      console.error("get preferences failed.", error);
      func.messageUtil(error);
    } finally {
      isLoadPreferences.current = true;
      setShowImagicaTutoriralLoading(false);
    }
  };
  const setCloseModal = e => {
    setIsModelOpen(false);
  };
  const editPrompt = () => {
    setIsModelOpen(true);
    setLineParam({
      id: duplicateEdgeRef?.current?.id,
      ...duplicateEdgeRef?.current?.data?.lineParam,
    });
  };
  const watchDelGroupNode = delNodes => {
    setTimeout(() => {
      const currParentId = delNodes[0].parentNode;
      const nodes = delNodes.map(item => ({ id: item.id }));
      // 不是删除的group中的node则不执行
      if (func.isEmpty(currParentId)) {
        // handleDeleteNodes(nodes);
        return;
      }
      // 过滤出来当前group中剩余的nodes
      const restCurrParentIdNodes = nodesRef.current.filter(x => x.parentNode === currParentId);
      if (!func.isEmpty(restCurrParentIdNodes)) {
        handleDeleteNodes(nodes);
        return;
      }
      // 如果当前group中node被删完，则删除当前group
      // fraser删除逻辑
      handleDeleteNodes([{ id: currParentId }]);
    });
  };
  function resetUndoData() {
    undoRef.current = [];
  }
  const { changeDisableAddPreview } = useAddToPreview();
  // const filterGuideNode = () => {
  //   setNodes(prevList => {
  //     return prevList.filter(x => !GUIDE_NODE_PATTERN.test(x.id))
  //   })
  // }
  // const changeDisableAddPreview = () => {
  //   if(disableAddPreview) {
  //     filterGuideNode()
  //   }
  //   setDisableAddPreview(!disableAddPreview);
  //   if(disableAddPreview) {
  //     logEvent('click_add_content');
  //   }else{
  //     logEvent('click_add_content_done');
  //   }
  // }

  const menuDeleteOption = useMemo(() => {
    if (func.isEmpty(checkNodeArr)) return null;
    return (
      <p className={style["right-text"]} onClick={handleMenuDelete}>
        Delete
      </p>
    );
  }, [checkNodeArr]);

  const autoLoadInputField = type => {
    const obj = {
      saas: {
        saveUI: saasUIDataRef.current,
        func: handleInputField,
      },
      saasV2: {
        saveUI: saasV2UIDataRef.current,
        func: handleSaasV2InputField,
      },
    };

    const startNodes = store.getState().fot.startNodeIds;
    if (!isEmpty(obj[type]["saveUI"].input) || func.isEmpty(startNodes)) return;

    const resultArr = nodesRef.current.filter(x => startNodes.includes(x.id));
    const inputIds = store.getState().fot.inputId;
    const newInputId = [...inputIds, ...startNodes];
    setInputId(newInputId);
    resultArr.forEach(inputNode => {
      // 改变 input 预览状态
      obj[type]["func"](inputNode, type);
    });
  };
  // function autoLoad
  function clickZoomFitIcon(timeout = 50, duration = 500) {
    fitCanvas();
    // if (nodesRef.current.length === 1) {
    //   setTimeout(() => {
    //     fitView();
    //     zoomTo(1, {
    //       duration: 500,
    //     });
    //   }, timeout);
    //   return;
    // }

    // setTimeout(() => {
    //   autoFitView(duration);
    // }, timeout);
  }

  useNodesRenderComplated({
    onProjectMounted() {
      if (selectedTemplate.id) {
        fitCanvas();
      }
    },
  });

  function getHomeNodes() {
    return nodesRef.current;
  }
  function getHomeEdges() {
    return edgesRef.current;
  }
  function getHomeNodesIndex() {
    return nodeIndexRef.current;
  }
  function getHomeEdgesIndex() {
    return edgeIndexRef.current;
  }
  function getHomeNextNodeIndex() {
    return ++nodeIndexRef.current;
  }
  const getPreviewContent = () => {
    const data = {
      saasV2: style["preview-content-v2"],
      thinkingApp: style["preview-content"],
      saas: `${style["saas-content"]} ${style[applicationTheme]}`,
    };
    return data[selectedUI] || "";
  };

  const handleSubscriptionAndPublish = async ids => {
    try {
      const published_metadata = {
        uuid: "",
        original: true,
        stripe_product_id: ids?.stripe_product_id || "",
        stripe_price_id: ids?.stripe_price_id || "",
        stripe_account_id: ids?.stripe_account_id || "",
      };
      await handleSave({
        templateObj: selectedTemplate,
        params: { published_metadata },
        shareObj: { share: true },
        showPublishTips,
      });
      // await saveAsExistingTemplateV2(selectedTemplate, nodesRef.current, edgesRef.current, undefined, store.getState().fot.variableList, {published_metadata})
      setSettingPreviewLLoading(false);
      setShowSettingPreview(false);
    } catch (error) {
      console.error("error::::::::::", error);
      func.messageUtil(error);
      setSettingPreviewLLoading(false);
    }
  };

  // 关闭preview
  const closePreviewApp = () => {
    logEvent("close_publish_x");
    setShowPreviewPanel(false);
    setDisableAddPreview(true);
    cancelCreatePreview();
    saasUiDataToPreview();
    // FIXME: 清空当前激活的 app, 判断是否时 chat app 时会根据当前的 saasuidata 判断, saasuidata 没有清空则数据没有发生变化 canvas addto 按钮状态会出现错误
    clearCurrentPreviewApp();
  };
  const hideMultiEdgeRenderBox = () => {
    setSameTargetEdgeIds([]);
    setMultiSameTargetEdgeIds([]);
    nodesRef.current.forEach(x => {
      let sameTargetEdges = edgesRef.current.filter(y => x.id === y.target);
      if (sameTargetEdges.length >= 1) {
        sameTargetEdges = sameTargetEdges.sort((a, b) => {
          return a.id.split("-")[1] - b.id.split("-")[1];
        });
        const sameTargetEdgeIds = store.getState().editor.sameTargetEdgeIds;
        if (!sameTargetEdgeIds.includes(sameTargetEdges[0].id)) {
          setSameTargetEdgeIds([...sameTargetEdgeIds, sameTargetEdges[0].id]);
        }
        const multiSameTargetEdgeIds = store.getState().editor.multiSameTargetEdgeIds;
        if (sameTargetEdges.length > 1 && !multiSameTargetEdgeIds.includes(sameTargetEdges[0].id)) {
          setMultiSameTargetEdgeIds([...multiSameTargetEdgeIds, sameTargetEdges[0].id]);
        }
      }
    });
  };
  const onEscRightMenu = e => {
    if (e.code === "Escape") {
      menuCancel();
    }
  };
  const handleShare = async () => {
    // 根据url请求数据渲染share页面
    const data = store.getState().editor.subscriberParamsData;
    // data为空表示unpublish
    if (isBlank(data)) {
      showInvalidShareAppTip.value = true;
      return;
    }
    setShare(true);
    setSubscriptionPrice({});
    setShareSelectedLoading(true);
    const subscriberObj = !func.isEmpty(data) ? data?.attributes : undefined;
    const selectedUIQuery = subscriberObj?.selectedUI || searchParams.get("selectedUI") || "saas";

    setShowSaasOutputList(false);
    setShowPreviewPanel(true);
    setStripePaymentActivated(false);
    setShowShareSignin(false);
    getSharedProjectData(subscriberObj)
      .then(async data => {
        setSelectedTemplate(data);
        setShareSelectedLoading(false);
        // 判断分享中是否需要订阅收费
        if (!func.isEmpty(data?.published_metadata?.stripe_price_id)) {
          const urlParams = getQueryParams();
          setStripePaymentActivated(JSON.parse(searchParams.get("stripePaymentActivated")));
          // await getPaymentStudios(data.index)
          // 判断用户是否登录
          if (func.isEmpty(authStore.state.brainToken)) {
            authStore.removeBrainToken();
            localStorage.removeItem("BRAIN_USER_EMAIL");
            setIsLogin(false);
            if (!stripePaymentActivated) setShowShareSignin(true);
          }
          await getSubscription(data.published_metadata.stripe_price_id);
        }
        const uiData = !func.isEmpty(data?.v3) ? data?.v3?.UI : data?.v2?.UI;
        const currPage = uiData?.find(x => x.value === selectedUIQuery);

        if (func.isEmpty(currPage)) {
          showInvalidShareAppTip.value = true;
          return;
        }
        setSelectedUI(selectedUIQuery);
        loadFromExistingTemplateV2(data, selectedUIQuery, true);
      })
      .catch(error => {
        console.error("error:::", error);
        showInvalidShareAppTip.value = true;
      });
    // }
    //  else {
    //   showInvalidShareAppTip.value = true;
    // }
  };

  const clickMyAccount = () => {
    navigate("profile/account");
  };
  const handleInit = async () => {
    try {
      // 初始化定义mode
      setMode("editorMode");
      // // 如果有token或者在cavans下获取用户信息
      // if(!util.isEmpty(authStore.state.brainToken) || !props.share) {
      //   await getUserMe()
      // }
      if (homePluginState.isShare) {
        handleShare();
        return;
      }
      await handleOnboarding();
      handleImagicaData();

      // 获取项目信息
      getProject();

      singleFlowEdgeArrRef.current = [];

      setStudioThought("user Query");
      setView("feed");
      setStudioProjectName("New thinking app");

      // Get user's saved functions
      // if (!props.share) {
      // TAG: 912
      if (!UserIdentity.hasGuest()) {
        initLoadUserFunctions();
      } else {
        // 07/28/2023 load all built-in functions if user are not signed in @david
        setImageGenSelectOptions(defaultApis);
      }
    } catch (error) {
      if (error.name === 401) {
        props.clickLogOut();
      }
    }
  };
  useEffect(() => {
    if (!homePluginState.isShare) {
      // share 状态改变后重置
      setShowPreviewPanel(false);
    }
  }, [homePluginState.isShare]);

  useEffect(() => {
    func.disableScale();
    // init bird SDK
    try {
      initSDK();
    } catch (error) {
      console.error("init bird sdk failed", error.message);
    }

    handleInit();
    // need to optimize...
    document.oncontextmenu = e => {
      if (
        document.activeElement.nodeName === "TEXTAREA" ||
        document.activeElement.nodeName === "INPUT" ||
        isClickAddToContentRef.current
      ) {
        return;
      }

      const showFeatureCot = (store.getState().editor?.me.feature_tags ?? []).every(
        x => x !== "disable_studios_cot_features"
      );

      if (showFeatureCot && (showCotRef.current === true || store.getState().editor.cot.creating === true)) return;

      if (
        showNavPageRef?.current ||
        homePluginState.isShare ||
        store.getState().fot.imagicaStudioTutorial.showGuide ||
        !func.isEmpty(store.getState().editor.currentGuideTitlePage)
      ) {
        return;
      }

      // 增加 esc 监听
      document.addEventListener("keydown", onEscRightMenu);

      e.preventDefault(); //取消默认的浏览器自带右键
      setCustomFuncRightContextMenu({});
      setEdgeRight(false);
      setNodeRight(false);
      const evt = window.event || arguments[0];
      const menu = rightMenuRef.current; //获取右键菜单
      const container = homeRef.current; //获取区域
      /*获取当前鼠标右键按下后的位置，据此定义菜单显示的位置*/
      const rightEdge = container.clientWidth - evt.clientX;
      const bottomEdge = container.clientHeight - evt.clientY;
      /*如果从鼠标位置到容器右边的空间小于菜单的宽度，就定位菜单的左坐标（Left）为当前鼠标位置向左一个菜单宽度*/
      if (rightEdge < menu.offsetWidth) {
        menu.style.left = container.scrollLeft + evt.clientX - menu.offsetWidth + "px";
      } else {
        /*否则，就定位菜单的左坐标为当前鼠标位置*/
        menu.style.left = container.scrollLeft + evt.clientX + "px";
      }
      /*如果从鼠标位置到容器下边的空间小于菜单的高度，就定位菜单的上坐标（Top）为当前鼠标位置向上一个菜单高度*/
      if (bottomEdge < menu.offsetHeight) {
        menu.style.top = container.scrollTop + evt.clientY - menu.offsetHeight + "px";
      } else {
        /*否则，就定位菜单的上坐标为当前鼠标位置*/
        menu.style.top = container.scrollTop + evt.clientY + "px";
      }
      /*设置菜单可见*/
      menu.style.visibility = "visible";
      setAllowZoomOrPan(false);
    };
  }, []);

  useEffect(() => {
    // 如果node具有多个输入边，则不显示除第一条边之外的边上的输入框和run按钮
    hideMultiEdgeRenderBox();
    const { maxNodeIndex, maxEdgeIndex } = getNodeEdgeMaxIndex();
    nodeIndexRef.current = Math.max(nodeIndexRef.current, maxNodeIndex);
    edgeIndexRef.current = Math.max(edgeIndexRef.current, maxEdgeIndex);

    handleInputChild();
  }, [edges.length]);

  useEffect(() => {
    resetStartNodeIds();
    // const preStartNodeIds = [...startNodeIds]
    // const currentStartNodeIds = getStartNodes(nodesRef.current, edgesRef.current)
    // setStartNodeIds(currentStartNodeIds)
    // const notStartNodes = preStartNodeIds.filter(x => !currentStartNodeIds.includes(x))
    // if(!util.isEmpty(notStartNodes) && !util.isEmpty(selectedUIRef.current)) {
    //   removeInputFeild(notStartNodes)
    // }
  }, [edgesRef.current, nodesRef.current, showPreviewPanel]);

  useEffect(() => {
    // 如果是新建项目不执行
    if (homeStoreState.isClickAddNewProject) return;

    fitCanvas();
  }, [showPreviewPanel]);

  function resetStartNodeIds(isStaticMode) {
    if (isStaticMode) {
      setStartNodeIds([]);
      return;
    }
    const isChat = PreviewAppUtil.isChatAiApp(saasUIData);
    const preStartNodeIds = [...startNodeIds];
    const currentStartNodeIds = getStartNodes(nodesRef.current, edgesRef.current, isChat);
    setStartNodeIds(currentStartNodeIds);
    const notStartNodes = preStartNodeIds.filter(x => !currentStartNodeIds.includes(x));
    if (!func.isEmpty(notStartNodes) && !func.isEmpty(selectedUIRef.current)) {
      removeInputFeild(notStartNodes);
    }
  }

  useEffect(() => {
    const { maxNodeIndex, maxEdgeIndex } = getNodeEdgeMaxIndex();
    nodeIndexRef.current = Math.max(nodeIndexRef.current, maxNodeIndex);
    edgeIndexRef.current = Math.max(edgeIndexRef.current, maxEdgeIndex);
    if (needAddNodeByDrag()) {
      dragOverTimer.current = null;
    }
    judgePlaceHolderDisplay(nodes, edges);
    handleInputChild();
  }, [
    nodes.filter(x => !func.isEmpty(x.data.textAreaValue)).length,
    nodes.filter(x => func.isEmpty(x.data.textAreaValue)).length,
  ]);

  useEffect(() => {
    if (!showMyModification) return;
    let nodeArr, edgeArr;
    try {
      nodeArr = JSON.parse(myModificationData.nodeArr);
      edgeArr = JSON.parse(myModificationData.edgeArr);
    } catch (error) {
      console.error(error);
    }

    setSelectedTemplate({
      template_name: myModificationData?.value || "",
      name: myModificationData?.value || "",
      isV3Function: myModificationData.isV3Function,
    });
    if (!func.isEmpty(edgeArr)) {
      const edges = edgeArr.map(x => {
        const data = NEW_LINE_REG.test(x.id) ? newLineDataRef.current : edgeDataRef.current;
        x.data = Object.assign({}, { ...data }, x.data);
        return x;
      });
      setEdges(edges);
    }
    if (!func.isEmpty(nodeArr)) {
      const nodes = nodeArr.map(x => {
        const data = NEW_EDGE_REG.test(x.id) ? newEdgeDataRef.current : nodeDataRef.current;
        x.data = Object.assign({}, { ...data }, x.data);
        return x;
      });
      setNodes(nodes);
    }
  }, [showMyModification]);

  useEffect(() => {
    if (imagicaStudioTutorial.showGuide) return;
    setShowCopyMouseStyl(altOptionPressed && isMouseInNode.current);

    // 如果按住alt快捷复制过程中松开了alt，则不复制
    if (!altOptionPressed && isMouseInNode.current) {
      setTimeout(() => {
        const copyNewNodeId = `editor-${nodeIndexRef.current - 1}`;
        const copyNewNode = nodes.find(x => x.id === copyNewNodeId);
        if (isFireNodeDragStopEvent.current) {
          onNodesEdgesAdd([getSimpleNode(copyNewNode)], []);
          return;
        }
        const tempCopyingNodeId = copyingNodeIdRef.current;
        // 重置保存的复制id
        copyingNodeIdRef.current = "";
        // 删除新增的复制node
        setNodes(prevList => {
          return prevList.filter(x => x.id !== copyNewNodeId);
        });
        // 重置边的连接node
        setEdges(prevList => {
          return prevList.map(x => {
            if (x.source === copyNewNodeId) {
              return {
                ...x,
                source: tempCopyingNodeId,
              };
            }
            if (x.target === copyNewNodeId) {
              return {
                ...x,
                target: tempCopyingNodeId,
              };
            }
            return x;
          });
        });
      }, 500);
    }
  }, [altOptionPressed]);

  useEffect(() => {
    if (imagicaStudioTutorial.showGuide || isNewEdgeRunRef.current) return;
    // 设置选中的边颜色
    setEdges(prevList => {
      return prevList.map(l => {
        if (checkEdgeArr.includes(l.id)) {
          return {
            ...l,
            markerEnd: selectedMarkerEnd,
          };
        }
        return {
          ...l,
          markerEnd,
        };
      });
    });
  }, [checkEdgeArr]);

  useEffect(() => {
    if (func.isEmpty(selectedUI)) return;
    // 操作preview修改保存的数据
    UIConfigRef.current[selectedUI].setUIDataFunc(prevData => {
      return {
        ...prevData,
        output: [...thoughts],
      };
    });
  }, [thoughts.length]);

  useEffect(() => {
    // let tempThoughts = [];
    let inputId = [];
    if (selectedUI === "saas") {
      // tempThoughts = [...saasUIData.output];
      inputId = (saasUIData.input || []).map(x => x.id);
    } else if (selectedUI === "thinkingApp") {
      // tempThoughts = [...thinkingAppUIData.output];
      inputId = (thinkingAppUIData.input || []).map(x => x.id);
    } else if (selectedUI === "saasV2") {
      // tempThoughts = [...saasV2UIData.output];
      inputId = (saasV2UIData.input || []).map(x => x.id);
    }

    // setThoughts(tempThoughts); bsf-4912 已弃用, 不需要重新更新 sassuidata.output, 只需要更新 inputid
    setInputId(inputId);
  }, [selectedUI]);

  // Listening `Backspace` and `Del` keyboards to delete nodes.
  useEffect(() => {
    if (!deleteKeysPress || imagicaStudioTutorial.showGuide || cotStore.creating === true) return;

    deleteSelectedElements(() => onPaneClick());
  }, [deleteKeysPress]);

  useEffect(() => {
    postImagicaStudioApi(imagicaStudioTutorial);
  }, [imagicaStudioTutorial]);

  // TODO: move this logic out of this file @david
  useEffect(() => {
    if (!isEmpty(userMe) && isSetFeatureTag === true) {
      const functionList = cloneDeep(imageGenSelectOptions);
      functionList.forEach(x => {
        if (x.value === "map") {
          set(x, "hide", feature_tags.showMap === false);
        }
        if (x.value === "showFoodInterface") {
          set(x, "hide", feature_tags.enableFood === false);
        }
        if (x.value === "askBrain") {
          set(x, "hide", feature_tags.showFunctionAskBrain === false);
        }
        if (x.value === "midjourneyV2") {
          set(x, "hide", feature_tags.enableMidjourneyV2 === false);
        }
        if (x.alias === true) {
          set(x, "hide", true);
        }
      });
      setImageGenSelectOptions(functionList);
    }
  }, [userMe, isSetFeatureTag]);

  useEffect(() => {
    setSaasUIData(prevData => ({
      ...prevData,
      staticMode: isStaticMode,
    }));
  }, [isStaticMode]);

  // useEffect(() => {
  //   // 返回 home
  //   if(!reviseCustomCode) {
  //     setShowNavPage(true)
  //   } else {
  //     // 进入修改
  //     setShowNavPage(false)
  //     setReviseCreateFunction(true)
  //   }
  // }, [reviseCustomCode])

  const handleClickAmplitude = event => {
    const data_amplitude = event.target.getAttribute("data-amplitude");
    let target = "";
    let option = "";
    if (data_amplitude) {
      target = data_amplitude?.split(":")[0] || "";
      option = data_amplitude?.split(":")[1] || "";
      logEvent("click", { target: target, option: option });
    }
  };
  useEffect(() => {
    //Logging Amplitude click events
    window.addEventListener("click", handleClickAmplitude);
    return () => {
      window.removeEventListener("click", handleClickAmplitude);
    };
  }, []);

  const publishBtnStyl = classNames(
    style["publish-btn"],
    showPublishTips && style["disabled-publish-btn"],
    selectedUI === "saas" ? style["saas-button-proportion"] : null
  );
  const publishPopoverContet = showPublishTips ? (disabledPublish ? needAInputField : needAOutputField) : "";

  const enableAddNodetoPreview =
    isGroupNode === false &&
    forbidAddToPreviewType.includes(get(nodeContextMenuRef, "current.data.displayType")) === false;

  const enableCOT =
    selectedTemplate?.name &&
    !showMyModification &&
    !showNavPage &&
    feature_tags.showFeatureCot &&
    !imagicaStudioTutorial.showGuide &&
    homePluginState.isShare !== true;

  if (showInvalidShareAppTip.value) {
    return <InvalidShareAppTip />;
  }

  const disableUploadTypesAsInputNode = feature_tags.disableUploadTypesAsInputNode
    ? nodeContextMenuRef.current?.data?.displayType === "uploadFile"
    : false;

  // Need it to debugger
  // console.log('HomeUsePlugin');
  const publishedMetadata = getPublishMetadata();

  const isMobile = window.navigator.userAgent.includes("Mobile");
  if (isMobile && !isShareWebApp() && !homePluginState.isShare && !hasClosedMobilePromptView) {
    return (
      <WebOnlyPromptView
        onClose={() => {
          setHasClosedMobilePromptView(true);
        }}
      />
    );
  }
  const homeContext = new HomeContextParams(showAllInputs);

  return (
    <HomeUsePluginContext.Provider value={homeContext}>
      <HomeChildProvider
        initialState={{
          onNodeContentChange,
          onEdgeContentChange,
          getStartEndNodesContentArr,
          getSimpleNodeData,
          getSaveDataV3,
          setNodes,
          setEdges,
          getHomeNodes,
          getHomeEdges,
          handleCustomToGroup,
          handleDeleteSingleNodeByPreviewBtn,
          getAndSetQueryV3,
          clickRunAllBtn,
          handleSave,
          handleHiddenNode,
          emptyPreviewApp,
          autoSelectTemplate,
          addToPreviewList,
          resetStartNodeIds,
          getUIListParam,
          handleGroupToCustom,
          handleInputField,
          addIndividual,
          clickGroupToPreview,
          initNewProjectV2,
          clickAddNodeBtn,
          autoAddInputNodeByBluePrint,
          addThink,
          newEdgeDataRef,
          newLineDataRef,
          nodeDataRef,
        }}
      >
        <div
          className={imagicaStudioTutorial.showGuide ? style["showGuide"] : style["home-plugin"]}
          ref={homeRef}
          onClick={menuCancel}
        >
          {/* 右键菜单 */}
          <div ref={rightMenuRef} className={style["right-menu"]} onClick={() => setAllowZoomOrPan(true)}>
            <p
              className={style["right-text"]}
              onClick={clickAddNodeBtn}
              data-testid="rightmenu-new"
              data-amplitude="rightmenu_new"
            >
              New
            </p>
            {feature_tags.disableUploadFileV2 === false && (
              <p
                className={style["right-text"]}
                onClick={handleClickUpload}
                data-testid="rightmenu-new-upload"
                data-amplitude="rightmenu_new_upload"
              >
                Upload a File
              </p>
            )}
            {feature_tags.showStudiosAgent && (
              <p className={style["right-text"]} onClick={clickAddNodeAgent} data-amplitude="rightmenu_new_agent">
                New Agent
              </p>
            )}
            {feature_tags.disableGenUI === false && (
              <p className={style["right-text"]} onClick={handleClickCreateGenUINode} data-amplitude="rightmenu_gen_UI">
                Gen UI
              </p>
            )}
            {menuDeleteOption}
            <p className={style["right-text"]} onClick={copyNodeAndEdge} data-amplitude="rightmenu_copy">
              Copy
            </p>
            <p className={style["right-text"]} onClick={pasteNodeAndEdge} data-amplitude="rightmenu_paste">
              Paste
            </p>

            {!func.isEmpty(customFuncRightContextMenu) ? (
              <p
                className={style["right-text"]}
                onClick={editCustomJsApiFunction}
                data-amplitude="rightmenu_edit_function"
              >
                Edit Function
              </p>
            ) : null}
            {edgeRight && developerMode && !selectApiReg.test(duplicateEdgeRef?.current?.data?.lineParam?.enterText) ? (
              <p className={style["right-text"]} onClick={editPrompt} data-amplitude="rightmenu_edit_prompt">
                Edit Prompt
              </p>
            ) : null}
            {edgeRight ? (
              <p className={style["right-text"]} onClick={duplicateEdge} data-amplitude="rightmenu_duplicate">
                Duplicate
              </p>
            ) : null}
            {nodeRight ? (
              <>
                {developerMode && !showMyModification ? (
                  <p
                    className={style["right-text"]}
                    onClick={() => clickToCreateVariable()}
                    data-amplitude="rightmenu_create_variable"
                  >
                    Create variable
                  </p>
                ) : null}

                {!func.isEmpty(selectedUI) ? (
                  <>
                    {enableAddNodetoPreview ? (
                      <p
                        className={style["right-text"]}
                        aria-disabled={homePluginState.disablePublishAsContent}
                        onClick={e => {
                          if (e.target.getAttribute("aria-disabled") === "true") return;
                          if (
                            handleSendToAppV2({
                              cb: () => {
                                addIndividual(nodeContextMenuRef.current);
                              },
                            })
                          )
                            return;
                          addIndividual(nodeContextMenuRef.current);
                        }}
                        data-amplitude="rightmenu_add_to_preview"
                      >
                        {`Publish as ${getCurrentUILabel()}`}
                      </p>
                    ) : null}
                    {!func.isEmpty(nodeContextMenuRef.current?.data?.parentNodeId) ? (
                      <p
                        className={style["right-text"]}
                        aria-disabled={thoughts
                          .map(x => x?.groupId)
                          ?.includes(nodeContextMenuRef.current?.data?.parentNodeId)}
                        onClick={e =>
                          e.target.getAttribute("aria-disabled") === "true" ||
                          clickToPreviewNode(nodeContextMenuRef.current)
                        }
                        data-amplitude="rightmenu_add_group_to_preview"
                      >
                        {`Add group to ${getCurrentUILabel()}`}
                      </p>
                    ) : null}
                    {showAddInputField && disableUploadTypesAsInputNode === false && !isStaticApp ? (
                      <p
                        className={style["right-text"]}
                        data-amplitude={
                          "rightmenu_add/remove_as_input_field:" +
                          (!inputId.includes(nodeContextMenuRef.current?.id) ? "remove" : "add")
                        }
                        onClick={() => addOrRemoveInputFeild(nodeContextMenuRef.current)}
                      >
                        {inputId.includes(nodeContextMenuRef.current?.id) && enableRemovePreview()
                          ? "Remove from preview"
                          : "Publish as input field"}
                      </p>
                    ) : null}
                  </>
                ) : null}
              </>
            ) : null}
            {!func.isEmpty(checkEdgeArr) && !showMyModification ? (
              <p
                className={style["right-text"]}
                onClick={clickToCreateFunction}
                data-amplitude="rightmenu_create_function"
              >
                Create function
              </p>
            ) : null}
            {/* <p className={style['right-text']} onClick={menuCancel} data-amplitude="rightmenu_cancel">
            Cancel
          </p> */}
          </div>

          <AnimatePresence>
            {isModelOpen && developerMode ? (
              <motion.div
                className={style["model-box"]}
                variants={bottomVariants}
                initial="init"
                animate="show"
                exit="hide"
              >
                <CodeModal setCloseModal={setCloseModal} setEdgeLineParam={setEdgeLineParam} />
              </motion.div>
            ) : null}
          </AnimatePresence>

          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onNodeClick={onNodeClick}
            onNodeDragStart={onNodeDragStart}
            onNodeDragStop={onNodeDragStop}
            onNodesChange={onNodesChange}
            onNodeMouseEnter={onNodeMouseEnter}
            onNodeMouseLeave={onNodeMouseLeave}
            onNodesDelete={onNodesDelete}
            onEdgeClick={onEdgeClick}
            onEdgesChange={onEdgesChange}
            // onEdgeMouseEnter={onEdgeMouseEnter}
            // onEdgeMouseLeave={onEdgeMouseLeave}
            onConnect={onConnect}
            onConnectStart={onConnectStart}
            onConnectEnd={onConnectEnd}
            onPaneClick={onPaneClick}
            onNodeContextMenu={onNodeContextMenu}
            onEdgeContextMenu={onEdgeContextMenu}
            connectionLineComponent={isNewEdgeRunRef.current ? CustomNewConnectionLine : ConnectionLine}
            onSelectionStart={onSelectionStart}
            onSelectionEnd={onSelectionEnd}
            onSelectionDragStart={onSelectionDragStart}
            onSelectionDragStop={onSelectionDragStop}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onDragLeave={onDragLeave}
            connectOnClick={false}
            zoomOnDoubleClick={false}
            deleteKeyCode={null}
            zoomActivationKeyCode={null}
            panActivationKeyCode={null}
            panOnDrag={reactFlowState.panOnDrag}
            panOnScroll={reactFlowState.panOnScroll}
            selectionOnDrag={reactFlowState.selectionOnDrag}
            nodesDraggable={reactFlowState.nodesDraggable}
            zoomOnScroll={reactFlowState.zoomOnScroll}
            zoomOnPinch={reactFlowState.zoomOnPinch}
            elementsSelectable={reactFlowState.elementsSelectable}
            fitView={false}
            selectionMode={SelectionMode.Partial}
            minZoom={0.1}
            className={[
              figmaMode ? style["figma-mode"] : "",
              showCopyMouseStyl ? style["copy-mouse"] : "",
              style["canvas-overflow-hidden"],
            ]}
            style={{ height: "calc(100% -  46px)" }}
          >
            <div className={style["background-box"]}>
              <Background
                id="1"
                gap={70}
                color="rgba(2, 12, 38, 0.05)"
                lineWidth={0.5}
                variant={BackgroundVariant.Lines}
              />
              {/* create new project后显示可供选择的不同模板 */}
              {feature_tags.enableNewOnboardingFlow ? <TemplateModal /> : null}
            </div>
            {/* 顶部导航栏 */}
            {!homePluginState.isShare ? (
              <AutoResizablePanel className={style["top-pane"]}>
                <HomeNavBar
                  page={props.page}
                  showCot={showCot}
                  setShowCot={setShowCot}
                  setCotAutomaticNode={setCotAutomaticNode}
                  isLastStepGuidePage={isLastStepGuidePage}
                  stickyNotes={true}
                  showNavPage={showNavPage}
                  runAllLoading={runAllLoading}
                  checkedUIList={checkedUIList}
                  stepDetachment={stepDetachment}
                  undoRef={undoRef}
                  setSelectedUI={setSelectedUI}
                  setCheckedUIList={setCheckedUIList}
                  isShowPreviewPanel={showPreviewPanel}
                  setShowPreviewPanel={setShowPreviewPanel}
                  handleSave={handleSave}
                  saveAsNewTemplateV2={saveAsNewTemplateV2}
                  loadFromExistingTemplateV2={loadFromExistingTemplateV2}
                  initNewProjectV2={initNewProjectV2}
                  clickLogOut={props.clickLogOut}
                  clickMyAccount={clickMyAccount}
                  setShowNavPage={setShowNavPage}
                  clickRunAllBtn={clickRunAllBtn}
                  resetUndoData={resetUndoData}
                  setShowMyModification={setShowMyModification}
                  emptyNodes={emptyNodes}
                  showMyModification={showMyModification}
                  myModificationData={myModificationData}
                  resetPreviewData={resetPreviewData}
                  getSimpleNodeData={getSimpleNodeData}
                  isPublish={isPublish}
                  setIsPublish={setIsPublish}
                  clickZoomFitIcon={clickZoomFitIcon}
                  publishFunction={publishFunction}
                  clickStep={clickStep}
                  showDisableStudiosOnboarding={feature_tags.showDisableStudiosOnboarding}
                  addNewProject={addNewProject}
                  getHomeNodes={getHomeNodes}
                  setNodes={setNodes}
                  autoSelectTemplate={autoSelectTemplate}
                />
              </AutoResizablePanel>
            ) : null}
            {!func.isEmpty(stepDetachment) ? (
              <GuideNodePage
                {...stepDetachment}
                clickBackStep={clickBackStep}
                clickStep={clickStep}
                clickZoomFitIcon={clickZoomFitIcon}
                loadFromExistingTemplateV2={loadFromExistingTemplateV2}
                // setStepDetachment={setStepDetachment}
              />
            ) : null}

            {/* 模板选择页面 */}
            {showNavPage && !homePluginState.isShare ? (
              <AutoResizablePanel id="homeContainer" className={cls(style["nav-page-pane"], style.background)}>
                <Home
                  node={nodeDataRef.current}
                  userFunctionsLoading={userFunctionsLoading}
                  showDisableStudiosOnboarding={feature_tags.showDisableStudiosOnboarding}
                  setShowMyModification={setShowMyModification}
                  setMyModificationData={setMyModificationData}
                  setShowNavPage={setShowNavPage}
                  loadFromExistingTemplateV2={loadFromExistingTemplateV2}
                  handleUserSavedFunctions={handleUserSavedFunctions}
                  pushFunc={data => singleFlowEdgeArrRef.current.push(data)}
                  emptyNodes={emptyNodes}
                  clickZoomFitIcon={clickZoomFitIcon}
                  initNewProjectV2={initNewProjectV2}
                  getSimpleNodeData={getSimpleNodeData}
                  clickStep={clickStep}
                  addNewProject={addNewProject}
                  setShowCot={setShowCot}
                  newEdgeDataRef={newEdgeDataRef}
                  newLineDataRef={newLineDataRef}
                />
              </AutoResizablePanel>
            ) : null}
            {/* 右侧idea列表 */}
            {showIdeaList && checkNodeArr.length === 1 ? (
              shrinkIdeaList ? (
                <IdeaList
                  ideaLoading={ideaLoading}
                  ideaList={ideaList}
                  ideaDescriptionRef={ideaDescriptionRef}
                  extractDescriptionRef={extractDescriptionRef}
                  selectedNodeIdRef={selectedNodeIdRef}
                  setIdeaList={setIdeaList}
                  setIdeaLoading={setIdeaLoading}
                  setShrinkIdeaList={setShrinkIdeaList}
                  postAIDescription={postAIDescription}
                />
              ) : (
                <span
                  className={`${style["shrink-addicon"]} iconfont icon-tianjia`}
                  onClick={() => setShrinkIdeaList(true)}
                ></span>
              )
            ) : null}
            {/* 右侧节点预览区域 */}
            {!saasUIData.previewV2 &&
            homePluginState.isShare &&
            showPreviewPanel &&
            (!imagicaStudioTutorial.showGuide || stepRef.current === 10) ? (
              <AutoResizablePanel position="top-right" className={style["node-preview-pane"]}>
                {!homePluginState.isShare ? (
                  <>
                    <div className={style["preview-header"]}>
                      {/* UI name */}
                      <p className={style["preview-select-title"]}>{selectedTemplate.name}</p>
                      {/* 关闭按钮 */}
                      <div className={style["share-icon-box"]}>
                        <img
                          src={`${settings.S3CDN}${settings.viewAssetsPath}preview-close.png`}
                          className={style["close-icon"]}
                          onClick={() => {
                            closePreviewApp();
                          }}
                        />
                      </div>
                    </div>
                    {/* 链接 */}
                    {publishedMetadata?.link ? (
                      <div onClick={() => copyShareLink(publishedMetadata.link)}>
                        <Input
                          value={publishedMetadata.link}
                          className={style["preview-link"]}
                          suffix={
                            <img
                              src={`${settings.S3CDN}${settings.viewAssetsPath}preview-share.png`}
                              style={{ width: "16px" }}
                              onClick={e => clickToShare(e, publishedMetadata.link)}
                            />
                          }
                          readOnly
                        />
                      </div>
                    ) : null}

                    {/* publish，setting按钮 */}
                    <div className={style["preview-btns"]}>
                      {selectedUI === "saas" && <ThemeSelect show={setShowThemeModal} />}
                      <Popover
                        content={publishPopoverContet}
                        placement={selectedUI === "saas" ? "topRight" : "top"}
                        trigger="hover"
                        overlayStyle={showPublishTips === true ? { fontFamily: "Ak-Regular" } : { display: "none" }}
                      >
                        <Button
                          className={publishBtnStyl}
                          loading={shareLoading}
                          disabled={false}
                          type="primary"
                          shape="round"
                          onClick={() => clickShareBtn()}
                          data-testid="PublishShareButton"
                        >
                          {shareLoading === false ? "Publish" : ""}
                          {showPublishTips ? <ExclamationCircleOutlined /> : ""}
                        </Button>
                      </Popover>
                      {feature_tags.showSubscriptionFeaturesToggle && selectedUI === "saasV2" ? (
                        <Button
                          className="setting-btn"
                          onClick={() => {
                            logEvent("click_publish_settings");
                            setShowSettingPreview(true);
                          }}
                        >
                          Settings
                          <img
                            src={`${settings.S3CDN}${settings.viewAssetsPath}preview-setting.png`}
                            className={style["setting-icon"]}
                          />
                        </Button>
                      ) : null}
                    </div>

                    <div className={style["publish-box"]}>
                      <p className={style["publish-tips"]}>{shareLoading ? "Publishing...  " : publishStr()}</p>
                      {feature_tags.enableStaticMode ? (
                        <Switch
                          checked={isStaticMode}
                          checkedChildren="Static Mode"
                          unCheckedChildren="Static Mode"
                          onClick={checked => {
                            setIsStaticMode(checked);
                          }}
                        />
                      ) : null}
                    </div>
                  </>
                ) : null}

                {showThemeModal && selectedUI === "saas" && (
                  <ThemeModal
                    saasTheme={saasTheme}
                    setSaasTheme={setSaasTheme}
                    setModal={setShowThemeModal}
                    themes={themes}
                  />
                )}

                {/* 预览内容部分 */}
                <div
                  className={`${homePluginState.isShare ? "share-app" : "project-app"} ${
                    selectedUI === "saas" ? style["saas-content"] : null
                  }`}
                  style={{ flex: 1, overflow: "hidden" }}
                >
                  {homePluginState.isShare && shareSelectedLoading ? (
                    <div>
                      <LoadingOutline />
                    </div>
                  ) : null}
                  <div className={getPreviewContent()}>
                    {selectedUI === "saas" ? (
                      <AISaas {...aiSaasProps} />
                    ) : selectedUI === "saasV2" ? (
                      <AISaasV2
                        ref={aiSaasV2Ref}
                        saasV2Loading={saasV2Loading}
                        saasV2UIData={saasV2UIData}
                        saasUIInputData={saasV2UIData.input}
                        saasUIListData={aiSaasProps.displayThought}
                        runAllLoading={aiSaasProps.runAllLoading}
                        subscriptionPrice={subscriptionPrice}
                        stripePaymentActivated={stripePaymentActivated}
                        isShare={homePluginState.isShare}
                        showSaasOutputList={aiSaasProps.showSaasOutputList}
                        setSaasV2UIData={setSaasV2UIData}
                        clickCreateContent={aiSaasProps.clickCreateContent}
                      ></AISaasV2>
                    ) : selectedUI === "thinkingApp" ? (
                      <UI UIMode="editorPreivewMode" className="thinking-app" />
                    ) : null}
                  </div>
                </div>
                {!imagicaStudioTutorial.showGuide && (
                  <div className={style["preview-btn-group"]}>
                    <span
                      className={style[disableAddPreview ? "preview-btn-add-content" : "preview-btn-done"]}
                      onClick={changeDisableAddPreview}
                    >
                      {disableAddPreview && (
                        <img
                          src={`${settings.S3CDN}${settings.viewAssetsPath}add-icon-green.png`}
                          alt=""
                          style={{ paddingRight: "10px" }}
                        />
                      )}
                      {disableAddPreview ? "Add Content" : "Done"}
                    </span>
                  </div>
                )}

                <SettingPreview
                  showSettingPreview={showSettingPreview}
                  settingPreviewLoading={settingPreviewLoading}
                  handleSave={handleSave}
                  setSettingPreviewLLoading={setSettingPreviewLLoading}
                  setShowSettingPreview={val => setShowSettingPreview(val)}
                  handleSubscriptionAndPublish={handleSubscriptionAndPublish}
                ></SettingPreview>
              </AutoResizablePanel>
            ) : null}
            {/* 重构的preview */}
            {!homePluginState.isShare &&
              showPreviewPanel &&
              (!imagicaStudioTutorial.showGuide || stepRef.current === 10) && (
                <PreviewPane
                  closePreviewApp={() => {
                    closePreviewApp();
                    cancelCreatePreview();
                  }}
                  autoAddOutPutNode={awaitAddNodeFunc}
                  autoSelectTemplate={autoSelectTemplate}
                />
              )}
            {saasUIData?.v2App?.type !== "chat_ai" &&
              saasUIData.previewV2 &&
              homePluginState.isShare &&
              showPreviewPanel &&
              (!imagicaStudioTutorial.showGuide || stepRef.current === 10) && (
                <AISaasApp clickRunAllBtn={clickRunAllBtn} handleHiddenNode={handleHiddenNode} />
              )}
            {/* CHAT AI APP */}
            {saasUIData?.v2App?.type === "chat_ai" && homePluginState.isShare && showPreviewPanel && <ChatAIPage />}
            {/* 底部工具栏 */}
            {!showNavPage ? (
              <HomeToolBar
                figmaMode={figmaMode}
                // checkedUIList={checkedUIList}
                showPreviewPanel={showPreviewPanel}
                setCheckedUIList={setCheckedUIList}
                setSelectedUI={setSelectedUI}
                setFigmaMode={setFigmaMode}
                showMyModification={showMyModification}
                handleSave={handleSave}
                addNewNode={addNewNode}
                addUploadNode={handleClickUpload}
                setShowCot={setShowCot}
                showCot={showCot}
                CotComponents={
                  enableCOT === true ? (
                    <CotInputView
                      clickZoomFitIcon={clickZoomFitIcon}
                      clickAddNodeBtn={clickAddNodeBtn}
                      showCot={showCot}
                      bloc={cotInputBloc}
                      setShowCot={setShowCot}
                      addThink={addThink}
                      clickClosePreview={clickClosePreview}
                    />
                  ) : (
                    <></>
                  )
                }
                undoRef={undoRef}
                historyIndex={historyIndex}
                handleUndo={handleUndo}
                handleRedo={handleRedo}
                showDisableStudiosOnboarding={feature_tags.showDisableStudiosOnboarding}
                showStartVideo={feature_tags.showStartVideo}
                // handleGuideInfo={handleGuideInfo}
                clickZoomFitIcon={clickZoomFitIcon}
                setCurrentGuideTitlePage={setCurrentGuideTitlePage}
              />
            ) : null}

            {isPublish ? (
              <Publish
                guidePublishData={guidePublishData}
                setSelectedUI={setSelectedUI}
                setIsPublish={setIsPublish}
                setShowPreviewPanel={setShowPreviewPanel}
                checkedUIList={checkedUIList}
                setCheckedUIList={setCheckedUIList}
                autoLoadInputField={autoLoadInputField}
                clickStep={clickStep}
                setCurrentGuideTitlePage={setCurrentGuideTitlePage}
                autoAddOutPutNode={awaitAddNodeFunc}
                cancelCreatePreview={cancelCreatePreview}
              />
            ) : null}

            {/* Create a new function 弹窗 */}
            <Modal
              title={createFunctionTitle}
              centered
              open={createNewFuncModal}
              onCancel={() => setCreateNewFuncModal(false)}
              maskStyle={{ background: "transparent" }}
              footer={[
                <Button
                  key="submit"
                  type="primary"
                  disabled={func.isEmpty(funcName)}
                  onClick={clickModalSaveBtn}
                  loading={saveFunctionLoading}
                >
                  Save
                </Button>,
              ]}
            >
              <Form
                name="basic"
                labelCol={{
                  span: 4,
                }}
                wrapperCol={{
                  span: 20,
                }}
                // onFinish={onFinish}
                autoComplete="off"
                validateTrigger="onBlur"
              >
                <Form.Item
                  label="Name"
                  // name="funcName"
                >
                  <Input
                    maxLength={PROJECT_NAME_LENGTH.max}
                    placeholder="Enter function name"
                    value={funcName}
                    onChange={e => {
                      setFuncName(e.target.value.slice(0, PROJECT_NAME_LENGTH.max));
                    }}
                  />
                </Form.Item>

                <Form.Item
                  label="Description"
                  // name="funcDescription"
                >
                  <Input
                    placeholder="Enter function description"
                    value={funcDescription}
                    onChange={e => setFuncDescription(e.target.value)}
                  />
                </Form.Item>
              </Form>
            </Modal>

            <CustomJSFunction
              showNavPage={showNavPage}
              singleFlowEdgeArrRef={singleFlowEdgeArrRef}
              requesSavedFunctionstList={handleUserSavedFunctions}
              setEdgeLineParam={setEdgeLineParam}
              setQueryLoading={setQueryLoading}
            ></CustomJSFunction>

            <CustomAPIFunction
              showNavPage={showNavPage}
              singleFlowEdgeArrRef={singleFlowEdgeArrRef}
              requestSavedFunctionstList={handleUserSavedFunctions}
              sendRequest={sendRequest}
              evaluateFinalBodyStr={evaluateFinalBodyStr}
              setEdgeLineParam={setEdgeLineParam}
              setQueryLoading={setQueryLoading}
            />
            {termsPopuBloc.state.agreementPromptModel.value && (
              <TermsPopup
                imagicaTermsPopup={webConfigState?.imagica_terms_popup}
                clickTermsButton={type => termsPopuBloc.clickTermsButton(type)}
              />
            )}
          </ReactFlow>

          {/* <CustomCircleMarker /> */}

          <GuideContainer showNavPage={showNavPage} addNewProject={addNewProject} />

          {showUnsupported && <UnSupportedGraph handleClose={closeUnsupported} />}
          <GuideVideoView showGuideVideo={showGuideVideo}></GuideVideoView>
          <Outlet></Outlet>
        </div>
      </HomeChildProvider>
    </HomeUsePluginContext.Provider>
  );
}

export function HomeUsePluginRoot(props) {
  return (
    <HomeUsePluginProvider initialState={props}>
      <PreviewAppProvider>
        <HomeUsePlugin {...props} />
      </PreviewAppProvider>
    </HomeUsePluginProvider>
  );
}
